import React, { useState, useEffect, useMemo, useRef, lazy, Suspense } from "react";
import './index.css';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from './supabaseClient';
import { useNotificationCounts } from './hooks/useNotificationCounts';
import { useAuthController } from './hooks/useAuthController';
import { useTasksController } from './hooks/useTasksController';
import { useMaterialsController } from './hooks/useMaterialsController';
import { useDocumentsController } from './hooks/useDocumentsController';
import { useCommentsController } from './hooks/useCommentsController';
import { usePhotosController } from './hooks/usePhotosController';
import { useScheduleController } from './hooks/useScheduleController';
import { useTeamController } from './hooks/useTeamController';
import { PERMISSIONS, hasPermission as hasPermissionForPosition, canViewSection as canViewSectionForPosition } from './lib/auth/roles';
import { usePhotoModal } from './contexts/PhotoModalContext';
import { useSearchParams } from 'react-router-dom';
import DashboardAlertsWidget from './components/common/DashboardAlertsWidget';

// SplashScreen يظهر فوراً عند فتح التطبيق قبل أي حاجة تانية، فبيفضل يتحمل مباشرة (eager)
import SplashScreen from './SplashScreen';
// ✅ صفحة تسجيل الدخول اتبدّلت من تصميم "كاميرا المراقبة" لتصميم split-screen
// (فورم يسار / بانل RITIS يمين) بناءً على طلب باكري — SecurityCameraToggle
// اتشالت، والبانل اليمين بقى فيه نفس الكرة الأرضية المتحركة والمتوهجة
// المستخدمة في مانجر برزنتيشن (PresentationGlobe.jsx، نفس المكون زي ما هو
// من غير أي تعديل — شبكة النقاط اللي كانت هنا قبل كده اتشالت بناءً على
// طلب باكري، ده كان المقصود فعليًا من الأول).
import PresentationGlobe from './PresentationGlobe';

import {
    Users, Calendar, Activity, CheckCircle, AlertTriangle, TrendingUp,
    Sun, Moon, Clock, DollarSign, AlertCircle,
    ChevronRight, MessageSquare, FileText, Shield, Bell, LogOut,
    MapPin, Mail, Briefcase, RefreshCw, Receipt, Wallet,
    Package, Image as ImageIcon, X,
    Check, ChevronLeft, Lock, ChevronDown,
    Settings, ArrowRight, ExternalLink,
    LayoutList, XCircle, Globe, Mic, Ticket, Monitor,
    FileDown, ClipboardList, UserCog, Key, Maximize, BookOpen, Link, Inbox, Gauge,
    Boxes,
} from "lucide-react";
import SectionLoader from './components/common/SectionLoader';
import UserMenu from './components/common/UserMenu';
import TurnstileWidget from './components/common/TurnstileWidget';
import ProjectSelector from './components/project/ProjectSelector';
import ProjectSwitchingOverlay from './components/project/ProjectSwitchingOverlay';
import UserManagement from './components/admin/UserManagement';
import BudgetView from './components/views/BudgetView';
import LocationView from './components/views/LocationView';
import DocumentsView from './components/views/DocumentsView';
import OverviewView from './components/views/OverviewView';
import InvoicesView from './components/views/InvoicesView';
import TasksView from './components/views/TasksView';
import ClientAccountView from './components/views/ClientAccountView';
import TeamView from './components/views/TeamView';
import ScheduleView from './components/views/ScheduleView';
import PhotosView from './components/views/PhotosView';
import CommentsView from './components/views/CommentsView';
import TimelineView from './components/views/TimelineView';
import ExpensesView from './components/views/ExpensesView';
import DailyActivitiesView from './components/views/DailyActivitiesView';
import MaterialsView from './components/views/MaterialsView';
import EVMView from './components/views/EVMView';
import {
    DEFAULT_PROJECT,
    COMPANY_INFO,
} from './constants/appConstants';
import { fetchProjectData } from './services/projectDataService';
// أيقونات اتشالت لأنها مستوردة بس مش مستخدمة في أي مكان بالملف:
// Star, CreditCard, Zap, EyeOff, Ruler, Hash, TruckIcon, HardHat, AlertOctagon, Wrench




// ══════════════════════════════════════════════
// كل الصفحات/الأقسام دي بتتحمّل Lazy (code-splitting):
// كل صفحة بتنزل كودها بس وقت ما المستخدم يفتحها فعلاً،
// مش كلها مع بعض عند أول تحميل للداشبورد. ده أكبر تحسين للسرعة.
// ══════════════════════════════════════════════
const ProjectManager = lazy(() => import('./ProjectManager'));
const SystemsLibrary = lazy(() => import('./SystemsLibrary'));
const SubmittalAssembler = lazy(() => import('./SubmittalAssembler'));
const HRSection = lazy(() => import('./HRSection'));
const AccountsSection = lazy(() => import('./AccountsSection'));
const CoordinationSection = lazy(() => import('./CoordinationSection'));
const Meetings = lazy(() => import('./Meetings'));
const PrequalProfile = lazy(() => import('./PrequalProfile'));
const ShowcaseTokenManager = lazy(() => import('./ShowcaseTokenManager'));
const ShowcaseLeadsDashboard = lazy(() => import('./ShowcaseLeadsDashboard'));
const ShowcaseAnalytics = lazy(() => import('./ShowcaseAnalytics'));
const ClientPortalModal = lazy(() => import('./ClientPortalModal'));
const ApprovedBrands = lazy(() => import('./ApprovedBrands'));

// معالج مؤقّت (timer handle) لتسجيل الخروج التلقائي بعد فترة عدم نشاط —
// module-level لازم يفضل موجود بين كل الـ renders من غير ما React يصفّره.
let sessionTimer;

// ── FIFO invoice ageing — نفس اللوجيك بالظبط الموجود في AccountsOverview.jsx
// (اللي بدوره بيطابق "RITIS Executive Finance Brief" الأسبوعي): الدفعات
// بتتطبّق على أقدم فاتورة مفتوحة الأول، وبعدين اللي فاضل من كل فاتورة بيتحط
// في الباكت بتاع عمرها هي. مكرر هنا (مش imported من AccountsOverview) عشان
// AccountsOverview مالهاش named export وعشان نتجنب أي كوبلينج زيادة بين
// الملفين — لو الخوارزمية اتغيّرت لازم تتغيّر في المكانين مع بعض.
function ageProjectTransactions(txs, today) {
    const key = (t) => (t.transaction_date ? new Date(t.transaction_date).getTime() : Number.MAX_SAFE_INTEGER);
    const sorted = [...txs].sort((a, b) => key(a) - key(b) || (a.id || 0) - (b.id || 0));

    const open = [];
    let unapplied = 0;

    sorted.forEach(t => {
        if ((t.debit || 0) > 0) open.push({ date: t.transaction_date, amount: t.debit });
        if ((t.credit || 0) > 0) {
            let pay = t.credit;
            while (pay > 0 && open.length > 0) {
                const first = open[0];
                const take = Math.min(pay, first.amount);
                first.amount -= take;
                pay -= take;
                if (first.amount <= 0.005) open.shift();
            }
            if (pay > 0) unapplied += pay;
        }
    });

    const buckets = { b30: 0, b60: 0, b90: 0, b90p: 0, undated: 0 };
    open.forEach(o => {
        if (o.amount <= 0.005) return;
        if (!o.date) { buckets.undated += o.amount; return; }
        const days = Math.floor((today.getTime() - new Date(o.date).getTime()) / 86400000);
        if (days <= 30) buckets.b30 += o.amount;
        else if (days <= 60) buckets.b60 += o.amount;
        else if (days <= 90) buckets.b90 += o.amount;
        else buckets.b90p += o.amount;
    });

    return { buckets, unapplied };
}

// ✅ اتنقل هنا برّا الكمبوننت (كان جواها، بيتبنى من جديد Object جديد كل render).
// الـ 6 قيم دول ثوابت 100% — ما بيتغيّروا مع darkMode أصلاً (التبديل فاتح/غامق
// بيحصل عبر data-theme على <div> الأساسي اللي بيغيّر قيم CSS variables، مش
// عبر الأوبجكت ده) — من أيام تحويل الـ design system لـ CSS variables
// (designSystem.css). المشكلة إن `theme` ده بيتبعت كـ prop لـ 9+ فيوز تقيلة
// (DailyActivitiesView, BudgetView, ClientAccountView...إلخ)، وبما إنه كان
// Object جديد كل render لـ App (مش useMemo ولا hoisted)، كان لوحده كافي يكسر
// أي memoization لكل الفيوز دي — أي state يتغيّر في App (حتى حاجة ملهاش أي
// علاقة بالفيو نفسه) كان بيولّد theme جديد، فيدي re-render كامل لكل فيو فاتح.
// ده كان سبب رئيسي في "الرمشة" اللي ظهرت بعد تحويل الـ design system.
const theme = {
    bg: "bg-[var(--jr-bg)]",
    card: "bg-[var(--jr-surface)]",
    text: "text-[var(--jr-text)]",
    subtext: "text-[var(--jr-text-2)]",
    border: "border-[var(--jr-border)]",
    hover: "hover:bg-[var(--jr-surface-2)]",
};

export default function App() {
    //Projects

    const [showSplash, setShowSplash] = useState(() => {
        return !sessionStorage.getItem('splashShown')
    })
    const [showProjectManager, setShowProjectManager] = useState(false);


    const [PROJECTS, setProjects] = useState({
        // Default project
        'sec-eoa-jubail': {
            id: 'sec-eoa-jubail',
            name: 'SEC EOA Jubail',
            shortName: 'SEC-EOA',
            client: 'Saudi Electricity Company',
            contract: 'CONTRACT-SEC-2024',
            totalValue: 45000000,
            startDate: new Date('2024-01-01'),
            endDate: new Date('2025-12-31'),
            status: 'Active'
        }
    });



    const loadProjectsFromDatabase = async () => {
        try {
            const { data, error } = await supabase
                .from('projects')
                .select('*');

            if (error) throw error;

            const projectsObj = {};
            data.forEach(project => {
                projectsObj[project.id] = {
                    id: project.id,
                    name: project.name,
                    work_start: project.work_start,
                    work_end: project.work_end,
                    shortName: project.short_name,
                    client: project.client,
                    contract: project.contract,
                    client_reference: project.client_reference,
                    totalValue: project.total_value,
                    startDate: new Date(project.start_date),
                    endDate: new Date(project.end_date),
                    status: project.status,
                    location: project.location,
                    latitude: project.latitude,   // ← أضف
                    longitude: project.longitude,  // ← أضف
                    description: project.description,        // ← جديد
                    sector: project.sector,             // ← جديد
                    coverImageUrl: project.cover_image_url,  // ← جديد
                    total_value: project.total_value,
                    start_date: project.start_date,
                    end_date: project.end_date,
                    percent_complete: project.percent_complete,
                    completion_pending_confirmation: project.completion_pending_confirmation,
                    forecasted_end_date: project.forecasted_end_date,
                    contract_type: project.contract_type,
                    rep_name: project.rep_name,
                    rep_position: project.rep_position,
                    rep_phone: project.rep_phone,
                    manpower_management: project.manpower_management,
                    manpower_supervisory: project.manpower_supervisory,
                    manpower_skilled: project.manpower_skilled,
                    manpower_unskilled: project.manpower_unskilled,
                    equipment_list: project.equipment_list,
                    subcontractors: project.subcontractors
                };
            });

            setProjects(projectsObj);

        } catch (err) {
            // ✅ كان catch فاضي تمامًا — فشل تحميل المشاريع كان يمر بصمت كاملة.
            console.error('❌ Failed to load projects:', err);
            showToast('Failed to load projects list. Please refresh the page.');
        }
    };
    useEffect(() => {
        loadProjectsFromDatabase();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- تحميل أولي عند mount فقط، والدالة معرّفة كل render لكنها مش بتعتمد على أي state متغيّر
    }, []);
    //clinet ticket
    // ✅ showITSMMenu اتنقلت لـ UserMenu.jsx (local state جوه الكمبوننت الجديد)
    const { counts } = useNotificationCounts()

    //daily activites
    //calender Activities
    const [editingActivity, setEditingActivity] = useState(null);
    const [dailyActivities, setDailyActivities] = useState([]);
    const [showAddActivityModal, setShowAddActivityModal] = useState(false);
    const [activityForm, setActivityForm] = useState({
        activity_name: '',
        system: '',
        description: '',
        workers_count: 0,
        imageFiles: [],
        date: new Date().toISOString().split('T')[0],
        task_id: null,
        progress: 0,
        location_zone: '',
        location_floor: '',
        location_area: '',
        is_subcontractor: false,
        subcontractor_name: '',
        subcontractor_workers: 0,
        hse_status: 'clear',
        hse_type: null,
        hse_notes: '',
    });
    const [uploadingActivity, setUploadingActivity] = useState(false);
    const [activityFilter, setActivityFilter] = useState('all');
    // ── Location Tag ──
    // (في activityForm تضيف)

    // ── HSE ──
    const [hseIncidents, setHseIncidents] = useState([]);

    // ── RFI ──
    const [rfis, setRfis] = useState([]);

    // ── Communications ──
    const [contacts, setContacts] = useState([]);

    //User Management
    // resendCount بقت جوه useAuthController.
    const [showUserManagement, setShowUserManagement] = useState(false);
    //add Materilas — state وhandlers صفحة Materials (بما فيها استيراد Excel)
    // بقوا جوه useMaterialsController (src/hooks/useMaterialsController.js).
    //Location

    const [mapClickPos, setMapClickPos] = useState(null);
    const [selectedTaskForPin, setSelectedTaskForPin] = useState('');
    //Photo 
    //notifcation
    const [notifications, setNotifications] = useState([]);
    const [showNotifications, setShowNotifications] = useState(false);
    //confim model

    const [confirmModal, setConfirmModal] = useState({ show: false, message: '', onConfirm: null });
    //Catgury & expences 

    const [editingCategory, setEditingCategory] = useState(null);
    const [editingExpense, setEditingExpense] = useState(null);
    const [editingCommitment, setEditingCommitment] = useState(null);


    const [activitySearch, setActivitySearch] = useState('');
    const [activitySystemFilter, setActivitySystemFilter] = useState('all');
    const [activitySupervisorFilter, setActivitySupervisorFilter] = useState('all');
    // ترقيم تاب Activities: بدل ما نرندر كل الأيام (وكل نشاط جواها) مرة واحدة،
    // بنعرض 5 أيام بس في كل صفحة. اخترت الترقيم بالأيام مش بالنشاط المفرد
    // عشان نحافظ على شكل التجميع بالتاريخ (date headers) زي ما هو.
    const [activitiesDatePage, setActivitiesDatePage] = useState(1);
    useEffect(() => { setActivitiesDatePage(1); }, [activityFilter, activitySearch, activitySystemFilter, activitySupervisorFilter]);
    const [uploadProgressCount, setUploadProgressCount] = useState(0);
    const [activityViewMode, setActivityViewMode] = useState('overview');
    const [calendarMonth, setCalendarMonth] = useState(new Date());
    const [selectedCalendarDay, setSelectedCalendarDay] = useState(null);
    // team update
    // editingMember/showEditMemberModal بقوا جوه useTeamController.
    //Settings
    const [showPortalModal, setShowPortalModal] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    //Project Schdule
    // باقي state/handlers صفحة Schedule (الفورمات/المودالات/الـ dependencies/
    // الـ gantt) بقوا جوه useScheduleController.
    // phases/setPhases, milestones/setMilestones اتسابوا هنا لأنهم بيتحمّلوا من
    // fetchData المركزية (phases مُستخدمة كمان في مكان تاني غير ScheduleView).
    const [phases, setPhases] = useState([]);
    const [milestones, setMilestones] = useState([]);
    // dependencies/setDependencies, baselineRevisions/setBaselineRevisions
    // اتسابوا هنا لأنهم بيتحمّلوا من fetchData *و* بيتم تعديلهم محليًا مباشرة
    // (addDependency/deleteDependency/createBaselineRevision جوه الـ hook).
    const [dependencies, setDependencies] = useState([]);
    const [baselineRevisions, setBaselineRevisions] = useState([]);
    // selectedRevision اتساب هنا لأن مودال تفاصيله مرسوم في App.jsx نفسها مباشرة.
    const [selectedRevision, setSelectedRevision] = useState(null);
    //task_challanges — ملاحظة: taskChallenges/setTaskChallenges اتسابوا هنا (مش في
    // useTasksController) لأنهم بيتحمّلوا من fetchData() المركزية (مش خاصين بصفحة
    // Tasks بس)؛ باقي state التاسكات (الفلاتر، الفورم، المودالات...) بقى في
    // src/hooks/useTasksController.js.
    const [taskChallenges, setTaskChallenges] = useState({});
    //Attendace
    // allAchievements/memberAchievements/newAchievement/teamSearchQuery/
    // teamPage/showLeaveRequests/processingLeaveId/showQRModal/qrUrl/
    // showAttendanceModal/attendanceDate/attendanceRecords/showAttendanceSummary
    // بقوا جوه useTeamController.
    // leaveRequests/setLeaveRequests, attendanceData/setAttendanceData اتسابوا
    // هنا لأنهم بيتحمّلوا من fetchData المركزية (attendanceData مُستخدمة كمان
    // في OverviewView).
    const [leaveRequests, setLeaveRequests] = useState([]);
    const [attendanceData, setAttendanceData] = useState([]);
    //clinet Comments

    const [clientComments, setClientComments] = useState([]);
    // photoSearch/photoSort/photoView/selectedSystemFilter/photosPage (+ ترقيم
    // الصفحات) بقوا جوه usePhotosController.
    // Auth — كل state/handlers الخاصة بتسجيل الدخول (otpCode، step، isLoggedIn،
    // currentUser، loginForm... إلخ) بما فيها الـ mount-effect اللي كان بيحاول
    // يستعيد السيشن من localStorage، بقوا جوه useAuthController
    // (src/hooks/useAuthController.js) — بيتم استدعاؤه تحت بعد تعريف fetchData.
    //add Tasks

    const [timelineTask, setTimelineTask] = useState(null);
    // state صفحة Tasks (الفلاتر/الترتيب/المودالات/الفورم) بقى في hook مستقل:
    // useTasksController (src/hooks/useTasksController.js) — بيتم استدعاؤه تحت
    // بعد تعريف fetchData.
    // UI
    // ✅ showUserMenu اتنقلت لـ UserMenu.jsx (local state جوه الكمبوننت الجديد)

    const [searchParams, setSearchParams] = useSearchParams();

    const view = searchParams.get('view') || 'Overview';

    const setView = (newView) => {
        setSearchParams({ view: newView });
    };
    const [darkMode, setDarkMode] = useState(false);
    // ✅ setLoading كانت state ميتة فعليًا (destructured بدون الـ getter، و
    // loading نفسها ما كانت بتُقرأ في أي مكان) — شيلتها هي وكل استدعاءاتها
    // (كانت جوه checkUser اللي بقت جوه useAuthController، وجوه fetchData تحت).
    const [autoRefresh] = useState(false);

    // ✅ كانت Materials بتستخدم نفس searchTerm بتاع Tasks — يعني البحث في صفحة
    // Tasks كان بيأثر على فلترة Materials (والعكس) لأنهم بيشاركوا نفس الـ state.
    // خليتها state منفصل عشان كل صفحة يبقى ليها بحثها الخاص. (searchTerm بتاع
    // Tasks نفسه بقى جوه useTasksController.)
    // searchTerm/selectedSystem بتوع Materials بقوا جوه useMaterialsController.
    // ✅ modalPhoto/setModalPhoto انتقلوا لـ PhotoModalContext (src/contexts/PhotoModalContext.jsx)
    // — كانوا هنا جوه App بيسببوا re-render كامل للتطبيق كله كل ما تفتح/تقفل صورة
    // (ده كان سبب الـ "رمشة" اللي باكري لاحظها). دلوقتي العرض والـ state بتوعهم
    // في Provider منفصل فوق <App/> في index.tsx، فمفيش أي تأثير على App نفسه.
    const setModalPhoto = usePhotoModal();
    const [uploadingCategory, setUploadingCategory] = useState(false);
    // commentFilter/editingComment/replyInputs بقوا جوه useCommentsController.
    // commentReplies/setCommentReplies اتسابوا هنا لأنهم بيتحمّلوا من fetchData
    // المركزية (fetchData بتعمل setCommentReplies(data.commentReplies) مباشرة).
    const [commentReplies, setCommentReplies] = useState({});

    //export attendce
    // showExportModal/exportRange بقوا جوه useTeamController.
    // في الـ State - أضف
    const [uploadingExpense, setUploadingExpense] = useState(false);
    const [uploadingCommitment, setUploadingCommitment] = useState(false);
    const [showAddCommitmentModal, setShowAddCommitmentModal] = useState(false);
    const [selectedMonth] = useState("all");
    const [selectedYear] = useState(new Date().getFullYear());
    // ========== PROJECT STATE ==========
    const [currentProjectId, setCurrentProjectId] = useState(
        localStorage.getItem('selectedProject') || DEFAULT_PROJECT
    );
    const [switchingProject, setSwitchingProject] = useState(false);

    // ========== DYNAMIC PROJECT & SHEETS ==========
    const PROJECT =
        PROJECTS[currentProjectId] || PROJECTS[DEFAULT_PROJECT];

    // FULL_ACCESS_POSITIONS/resolveStartProject/checkUser (استعادة السيشن عند
    // فتح الصفحة) بقوا جوه useAuthController.

    // Data
    // showWelcome بقت جوه useAuthController.
    const [tasksData, setTasksData] = useState([]);
    const [teamData, setTeamData] = useState([]);
    const [budgetData, setBudgetData] = useState([]);
    const [materialsData, setMaterialsData] = useState([]);
    const [performanceData, setPerformanceData] = useState([]);
    const [photosData, setPhotosData] = useState([]);
    const [commentsData, setCommentsData] = useState([]);

    const [invoicesData, setInvoicesData] = useState([]);
    const [clientAccountData, setClientAccountData] = useState([]);
    const [changeOrdersData] = useState([]);

    const [budgetCategories, setBudgetCategories] = useState([]);
    const [budgetExpenses, setBudgetExpenses] = useState([]);
    const [budgetCommitments, setBudgetCommitments] = useState([]);
    const [expandedCategory, setExpandedCategory] = useState(null);
    const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
    const [showAddExpenseModal, setShowAddExpenseModal] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);
    //clinet Account

    const [editingClient, setEditingClient] = useState(null);
    const [clientForm, setClientForm] = useState({
        transaction_date: '',
        description: '',
        debit: '',
        credit: '',
        reference: ''
    });
    // Invoice Approval States
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [showInvoiceDetailsModal, setShowInvoiceDetailsModal] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    const [approvingInvoice, setApprovingInvoice] = useState(false);

    // Materils Section — toast/setToast عام للتطبيق كله (مش خاص بـ Materials
    // بس) فاتساب هنا؛ باقي state صفحة Materials بقى جوه useMaterialsController.
    const [toast, setToast] = useState({ show: false, message: '', type: '' });

    // ganttKey/ganttScheduleRef وإعادة حسابهم بقوا جوه useScheduleController.

    // Team Management States
    // showPerformanceModal/editingPerformance/savingPerformance/
    // showAddTeamMemberModal/showAssignExistingMemberModal/uploadingTeamMember/
    // assigningMember/memberSearchTerm/selectedMemberToAssign/assignmentSystems/
    // newMemberForm بقوا جوه useTeamController.
    // allTeamMembers/setAllTeamMembers اتسابوا هنا لأنهم بيتحمّلوا من effect
    // تسجيل الدخول/تبديل مشروع (مش جوه fetchData نفسها).
    const [allTeamMembers, setAllTeamMembers] = useState([]); // كل الأعضاء المتاحين
    const compressImage = async (file) => {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    const maxSize = 1920; // ✅ أكبر — جودة أعلى
                    let width = img.width;
                    let height = img.height;

                    if (width > height && width > maxSize) {
                        height = (height * maxSize) / width;
                        width = maxSize;
                    } else if (height > maxSize) {
                        width = (width * maxSize) / height;
                        height = maxSize;
                    }

                    canvas.width = width;
                    canvas.height = height;
                    canvas.getContext('2d').drawImage(img, 0, 0, width, height);

                    // ✅ مرة واحدة بس + جودة أعلى
                    canvas.toBlob((blob) => {
                        resolve(blob || file);
                    }, 'image/jpeg', 0.85);
                };
                img.src = e.target.result;
            };
            reader.readAsDataURL(file);
        });
    };
    //notifcation 
    const fetchNotifications = async () => {
        let query = supabase
            .from('notifications')
            .select('*')
            .eq('is_read', false)
            .order('created_at', { ascending: false })
            .limit(20);

        // لو مش manager، بس جيب notifications المشروع بتاعه
        // ✅ Coordination Team اتشالت من هنا — بقت بورتال مقفول على الماتريال
        // بس (مالهاش project_id ثابت)، فمنطق notifications العادي ده (المربوط
        // بمشروع) ملوش معنى ليها خالص دلوقتي.
        const fullAccess = ['CEO', 'Program Manager', 'Operations Manager'];
        if (!fullAccess.includes(currentUser?.position)) {
            query = query.eq('project_id', currentProjectId);
        }

        const { data } = await query;
        setNotifications(data || []);
    };

    // HR Portal notifications — بورتال HR مالوش project_id ثابت، فالإشعارات
    // بتاعته لازم تتبني من بيانات HR فعلية (طلبات إجازة معلّقة، تقارير يومية
    // ناقصة، نسبة حضور منخفضة) بدل جدول notifications اللي كله task/project.
    // مش بتتخزن في الداتابيز — بتتحسب لايف كل مرة، فمفيش is_read حقيقي ليها.
    const fetchHRNotifications = async () => {
        try {
            const today = new Date().toISOString().split('T')[0];
            const notifs = [];

            // 1) Pending leave requests
            const { data: pendingLeaves } = await supabase
                .from('leave_requests')
                .select('id, leave_type, leave_date, end_date, created_at, team_members(name)')
                .eq('status', 'Pending')
                .order('created_at', { ascending: false })
                .limit(20);
            (pendingLeaves || []).forEach(lr => {
                notifs.push({
                    id: `leave-${lr.id}`,
                    type: 'hr-leave-pending',
                    message: `Pending ${lr.leave_type || 'leave'} request — ${lr.team_members?.name || 'Unknown employee'}`,
                    priority: 'high',
                    created_at: lr.created_at || today,
                });
            });

            // 2) Active projects, used for missing-report + attendance checks below
            const { data: activeProjects } = await supabase
                .from('projects')
                .select('id, name, short_name')
                .eq('status', 'Active');

            // 3) Missing daily reports today
            const { data: reportsToday } = await supabase
                .from('daily_activities')
                .select('project_id')
                .eq('date', today);
            const reportedIds = new Set((reportsToday || []).map(r => String(r.project_id)));
            (activeProjects || []).forEach(p => {
                if (!reportedIds.has(String(p.id))) {
                    notifs.push({
                        id: `report-${p.id}`,
                        type: 'hr-report-missing',
                        message: `Missing daily report today — ${p.short_name || p.name}`,
                        priority: 'medium',
                        created_at: new Date().toISOString(),
                    });
                }
            });

            // 4) Low attendance today (below 60%)
            const { data: attToday } = await supabase
                .from('attendance')
                .select('project_id, status')
                .eq('date', today);
            const byProject = {};
            (attToday || []).forEach(a => {
                const key = String(a.project_id);
                byProject[key] = byProject[key] || { present: 0, total: 0 };
                byProject[key].total += 1;
                if (a.status === 'Present' || a.status === 'Late') byProject[key].present += 1;
            });
            Object.entries(byProject).forEach(([pid, c]) => {
                const rate = c.total ? Math.round((c.present / c.total) * 100) : null;
                if (rate !== null && rate < 60) {
                    const proj = (activeProjects || []).find(p => String(p.id) === pid);
                    notifs.push({
                        id: `attendance-${pid}`,
                        type: 'hr-attendance-low',
                        message: `Low attendance today (${rate}%) — ${proj?.short_name || proj?.name || pid}`,
                        priority: rate < 40 ? 'critical' : 'high',
                        created_at: new Date().toISOString(),
                    });
                }
            });

            notifs.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
            setNotifications(notifs);
        } catch (err) {
            console.error('Failed to load HR notifications:', err);
        }
    };

    // Accounts Portal notifications — نفس فكرة HR بالظبط: مفيش project_id ثابت،
    // فالإشعارات بتتبني لايف من بيانات الحسابات الفعلية (فلو أبس متصعّدة لسه
    // مفتوحة، فلو أبس واقفة من غير رد لفترة، ردود عملاء لسه محتاجة إغلاق،
    // ومشاريع عندها مديونية متأخرة أوي من غير أي متابعة) بدل جدول notifications.
    const fetchAccountsNotifications = async () => {
        try {
            const today = new Date();
            const notifs = [];

            const { data: followUps } = await supabase
                .from('client_follow_ups')
                .select('id, project_id, subject, status, escalated, follow_up_date, created_at')
                .order('follow_up_date', { ascending: false });

            const { data: projs } = await supabase
                .from('projects')
                .select('id, name, short_name, client');
            const projById = {};
            (projs || []).forEach(p => { projById[p.id] = p; });
            const projLabel = (pid) => {
                const p = projById[pid];
                return p?.short_name || p?.name || pid;
            };

            (followUps || []).forEach(f => {
                const days = f.follow_up_date ? Math.floor((today.getTime() - new Date(f.follow_up_date).getTime()) / 86400000) : 0;

                // 1) Escalated follow-ups still open — needs closure
                if (f.escalated && f.status !== 'resolved') {
                    notifs.push({
                        id: `accfu-esc-${f.id}`,
                        type: 'accounts-escalated-open',
                        message: `Escalated follow-up still open — ${projLabel(f.project_id)}: ${f.subject}`,
                        priority: 'critical',
                        created_at: f.created_at || f.follow_up_date,
                    });
                }
                // 2) Client responded but not yet marked resolved
                else if (f.status === 'responded') {
                    notifs.push({
                        id: `accfu-resp-${f.id}`,
                        type: 'accounts-response-pending',
                        message: `Client responded, awaiting closure — ${projLabel(f.project_id)}: ${f.subject}`,
                        priority: 'medium',
                        created_at: f.created_at || f.follow_up_date,
                    });
                }
                // 3) Contacted but stale — no movement in 7+ days, not escalated
                else if (f.status === 'contacted' && !f.escalated && days >= 7) {
                    notifs.push({
                        id: `accfu-stale-${f.id}`,
                        type: 'accounts-followup-stale',
                        message: `No update in ${days} days — ${projLabel(f.project_id)}: ${f.subject}`,
                        priority: days >= 14 ? 'high' : 'medium',
                        created_at: f.created_at || f.follow_up_date,
                    });
                }
            });

            // 4) Projects with severely overdue receivables (90+ days) and no
            //    follow-up logged in the last 14 days — flag for outreach.
            const { data: txns } = await supabase
                .from('client_account')
                .select('project_id, transaction_date, debit, credit');
            const byProject = {};
            (txns || []).forEach(t => {
                (byProject[t.project_id] = byProject[t.project_id] || []).push(t);
            });
            const recentFollowUpProjectIds = new Set(
                (followUps || [])
                    .filter(f => f.follow_up_date && Math.floor((today.getTime() - new Date(f.follow_up_date).getTime()) / 86400000) < 14)
                    .map(f => f.project_id)
            );
            const overdue = [];
            Object.entries(byProject).forEach(([pid, txs]) => {
                const { buckets } = ageProjectTransactions(txs, today);
                if (buckets.b90p > 1000 && !recentFollowUpProjectIds.has(pid)) {
                    overdue.push({ pid, amount: buckets.b90p });
                }
            });
            overdue.sort((a, b) => b.amount - a.amount);
            overdue.slice(0, 8).forEach(o => {
                notifs.push({
                    id: `accfu-overdue-${o.pid}`,
                    type: 'accounts-overdue-no-followup',
                    message: `SAR ${Math.round(o.amount).toLocaleString()} overdue 90+ days, no recent follow-up — ${projLabel(o.pid)}`,
                    priority: o.amount > 50000 ? 'high' : 'medium',
                    created_at: today.toISOString(),
                });
            });

            const order = { critical: 0, high: 1, medium: 2, low: 3 };
            notifs.sort((a, b) => (order[a.priority] ?? 9) - (order[b.priority] ?? 9) || new Date(b.created_at) - new Date(a.created_at));
            setNotifications(notifs);
        } catch (err) {
            console.error('Failed to load Accounts notifications:', err);
        }
    };
    //task_challangs — loadChallenges بقت جوه useTasksController.

    // ملاحظة: كل كود Leaflet (الأيقونات، MapClickHandler) كان معرّف هنا بس
    // مش مستخدم في أي مكان — الخرائط في التطبيق بقت بتعتمد على Google Maps
    // (LocationGoogleMap) مش react-leaflet. اتشال الكود الميت + استيراد
    // leaflet/react-leaflet بالكامل من أول الملف لتقليل حجم الـ bundle.


    // في أول App component




    // ─────────────────────────────────────────────
    // Daily Activities Functions
    // Changes from previous version:
    // 1. Removed "progress cannot go backward" restriction
    // 2. Images are now mandatory (at least 1 photo required)
    // 3. deleteDailyActivity now updates task status (not just progress)
    // 4. Fixed Math.max on empty array (was returning -Infinity)
    // 5. Fixed project completion check (now fetches updated tasks after update)
    // ─────────────────────────────────────────────

    const uploadWithRetry = async (file, retries = 3) => {
        const compressed = await compressImage(file);
        for (let attempt = 1; attempt <= retries; attempt++) {
            const path = `activities/${Date.now()}-${Math.random().toString(36).substring(7)}.jpg`;
            const { error } = await supabase.storage
                .from('photos')
                .upload(path, compressed, { contentType: 'image/jpeg' });
            if (!error) {
                const { data: { publicUrl } } = supabase.storage.from('photos').getPublicUrl(path);
                setUploadProgressCount(prev => prev + 1);
                return publicUrl;
            }
            console.warn(`Upload attempt ${attempt} failed:`, error.message);
            if (attempt < retries) await new Promise(r => setTimeout(r, attempt * 1000));
        }
        throw new Error('Image upload failed after 3 attempts');
    };

    // ─────────────────────────────────────────────
    // ✅ Completion-confirmation gate:
    // لما نسبة الإنجاز (MAX بين الأنشطة اليومية المرتبطة بالتاسك) توصل 100%،
    // التاسك ميتسكرش أوتوماتيك — بس بيتحط عليه completion_pending_confirmation=true
    // وبيفضل status='In Progress' لحد ما المسؤول (PM) يأكد بزرار مباشر على
    // التاسك (confirmTaskCompletion). لو التاسك كان متأكَّد Completed قبل كدا
    // وبعدين الدليل (daily activity) اتمسح/اتعدّل وخلّى الـ MAX يقل عن 100%،
    // التاسك والمشروع (لو كان كمان متأكَّد Completed) بيرجعوا يفتحوا أوتوماتيك
    // + إشعار للمسؤول.
    // ─────────────────────────────────────────────
    const syncTaskProgressFromActivities = async (taskId) => {
        if (!taskId) return null;

        const { data: taskRow, error: taskRowError } = await supabase
            .from('tasks')
            .select('id, sub_task, status, completion_pending_confirmation, project_id')
            .eq('id', taskId)
            .single();

        if (taskRowError) console.error('syncTaskProgressFromActivities: failed to load task', taskRowError);
        if (!taskRow) return null;

        const { data: activities } = await supabase
            .from('daily_activities')
            .select('progress')
            .eq('task_id', taskId);

        const maxProgress = activities?.length
            ? Math.max(...activities.map(a => a.progress || 0))
            : 0;

        const wasConfirmedCompleted = taskRow.status === 'Completed' && !taskRow.completion_pending_confirmation;

        let newStatus;
        let newPending;

        if (maxProgress >= 100) {
            if (wasConfirmedCompleted) {
                // كان متأكَّد Completed من قبل — سيبه زي ما هو
                newStatus = 'Completed';
                newPending = false;
            } else {
                // وصل 100% بس لسه محتاج تأكيد المسؤول
                newStatus = 'In Progress';
                newPending = true;
            }
        } else {
            newStatus = maxProgress > 0 ? 'In Progress' : 'Pending';
            newPending = false;
        }

        await supabase.from('tasks')
            .update({ progress: maxProgress, status: newStatus, completion_pending_confirmation: newPending })
            .eq('id', taskId);

        // ✅ لو كان متأكَّد Completed وبعد التعديل/المسح رجع تحت 100% — نفك
        // قفل المشروع تلقائيًا لو هو كمان كان متأكَّد Completed (لأن EV اللي
        // كانت مبنية على التأكيد دا بقت مش صحيحة).
        if (wasConfirmedCompleted && maxProgress < 100 && taskRow.project_id) {
            const { data: projectRow } = await supabase
                .from('projects')
                .select('id, name, status')
                .eq('id', taskRow.project_id)
                .single();

            if (projectRow?.status === 'Completed') {
                await supabase.from('projects')
                    .update({ status: 'Active', completion_pending_confirmation: false })
                    .eq('id', taskRow.project_id);

                await supabase.from('notifications').insert([{
                    project_id: taskRow.project_id,
                    type: 'project_reopened',
                    message: `⚠️ Project "${projectRow.name}" was automatically reopened because task "${taskRow.sub_task}" dropped back below 100% after a linked daily activity was edited or deleted.`,
                    priority: 'high',
                }]);

                showToast(`⚠️ Project "${projectRow.name}" was automatically reopened — a completed task dropped back below 100%`);
            }
        }

        return { maxProgress, newStatus, newPending };
    };

    const addDailyActivity = async () => {
        // Validate activity name
        if (!activityForm.activity_name) {
            showToast('Please enter activity name');
            return;
        }

        // ✅ Images are mandatory
        if (!activityForm.imageFiles || activityForm.imageFiles.length === 0) {
            showToast('⚠️ Please attach at least one photo');
            return;
        }

        setUploadingActivity(true);
        try {
            setUploadProgressCount(0);
            const imageUrls = await Promise.all(
                activityForm.imageFiles.map(file => uploadWithRetry(file))
            );

            await supabase.from('daily_activities').insert([{
                project_id: currentProjectId,
                activity_name: activityForm.activity_name,
                system: activityForm.system || null,
                description: activityForm.description || null,
                workers_count: activityForm.workers_count || 0,
                images: imageUrls,
                date: activityForm.date,
                time: new Date().toTimeString().split(' ')[0],
                created_by: currentUser?.name,
                member_id: null,
                task_id: activityForm.task_id || null,
                progress: activityForm.progress || 0,
                location_zone: activityForm.location_zone || null,
                location_floor: activityForm.location_floor || null,
                location_area: activityForm.location_area || null,
                is_subcontractor: activityForm.is_subcontractor,
                subcontractor_name: activityForm.subcontractor_name || null,
                subcontractor_workers: activityForm.subcontractor_workers || 0,
                hse_status: activityForm.hse_status,
                hse_type: activityForm.hse_type || null,
                hse_notes: activityForm.hse_notes || null,
            }]);

            // Update task progress/status (behind the completion-confirmation gate)
            let reachedPendingConfirmation = false;
            if (activityForm.task_id) {
                const result = await syncTaskProgressFromActivities(activityForm.task_id);
                reachedPendingConfirmation = !!result?.newPending;
            }

            showToast(reachedPendingConfirmation
                ? '✅ Activity added! Task reached 100% — needs PM confirmation'
                : '✅ Activity added!');
            setShowAddActivityModal(false);
            setActivityForm({
                activity_name: '', system: '', description: '',
                workers_count: 0, imageFiles: [],
                date: new Date().toISOString().split('T')[0],
            });
            fetchData(currentProjectId);
        } catch (err) {
            console.error('Activity add error:', err);
            showToast('❌ Failed to add activity. Please try again.');
        }
        setUploadingActivity(false);
    };

    const deleteDailyActivity = async (id) => {
        showConfirm('Are you sure you want to delete this activity?', async () => {
            const activity = dailyActivities.find(a => a.id === id);

            await supabase.from('daily_activities').delete().eq('id', id);

            // ✅ Update progress/status (gate handles auto-reopen on retraction)
            if (activity?.task_id) {
                await syncTaskProgressFromActivities(activity.task_id);
            }

            showToast('✅ Activity deleted!');
            fetchData(currentProjectId);
        });
    };

    const updateDailyActivity = async () => {
        // ✅ Images are mandatory — must keep existing or add new ones
        const totalImages = (editingActivity.images?.length || 0) +
            (editingActivity.newImageFiles?.length || 0);
        if (totalImages === 0) {
            showToast('⚠️ Please attach at least one photo');
            return;
        }

        setUploadingActivity(true);
        try {
            const imageUrls = [...(editingActivity.images || [])];
            setUploadProgressCount(0);
            const newUrls = await Promise.all(
                (editingActivity.newImageFiles || []).map(file => uploadWithRetry(file))
            );
            imageUrls.push(...newUrls);

            await supabase.from('daily_activities').update({
                activity_name: editingActivity.activity_name,
                system: editingActivity.system || null,
                description: editingActivity.description || null,
                workers_count: editingActivity.workers_count || 0,
                date: editingActivity.date,
                images: imageUrls,
                progress: editingActivity.progress || 0,
                location_zone: editingActivity.location_zone || null,
                location_floor: editingActivity.location_floor || null,
                location_area: editingActivity.location_area || null,
                is_subcontractor: editingActivity.is_subcontractor || false,
                subcontractor_name: editingActivity.subcontractor_name || null,
                subcontractor_workers: editingActivity.subcontractor_workers || 0,
                hse_status: editingActivity.hse_status || 'clear',
                hse_type: editingActivity.hse_type || null,
                hse_notes: editingActivity.hse_notes || null,
            }).eq('id', editingActivity.id);

            // Update task progress/status (gate handles pending-confirmation + auto-reopen)
            let reachedPendingConfirmation = false;
            if (editingActivity.task_id) {
                const result = await syncTaskProgressFromActivities(editingActivity.task_id);
                reachedPendingConfirmation = !!result?.newPending;
            }

            showToast(reachedPendingConfirmation
                ? '✅ Activity updated! Task reached 100% — needs PM confirmation'
                : '✅ Activity updated!');
            setEditingActivity(null);
            fetchData(currentProjectId);
        } catch (err) {
            console.error('Activity update error:', err);
            showToast('❌ Failed to update activity. Please try again.');
        }
        setUploadingActivity(false);
    };

    // ✅ المسؤول (PM) بيأكد إن التاسك فعلاً خلص 100% — دي الخطوة اللي بتحوّل
    // التاسك من "pending confirmation" إلى Completed رسميًا. لو كل تاسكات
    // المشروع بقت Completed بعد كدا، المشروع بياخد completion_pending_confirmation
    // (مش Completed مباشرة) — لحد ما المسؤول يأكد المشروع نفسه بزرار منفصل
    // (confirmProjectCompletion).
    const confirmTaskCompletion = async (taskId) => {
        try {
            await supabase.from('tasks')
                .update({ status: 'Completed', completion_pending_confirmation: false })
                .eq('id', taskId);

            const { data: taskRow } = await supabase
                .from('tasks')
                .select('project_id')
                .eq('id', taskId)
                .single();

            if (taskRow?.project_id) {
                const { data: allProjectTasks } = await supabase
                    .from('tasks')
                    .select('status')
                    .eq('project_id', taskRow.project_id);

                const allDone = allProjectTasks?.length > 0 &&
                    allProjectTasks.every(t => t.status === 'Completed');

                if (allDone) {
                    await supabase.from('projects')
                        .update({ completion_pending_confirmation: true })
                        .eq('id', taskRow.project_id);

                    showToast('🎉 All project tasks are complete — needs your confirmation to mark the project Completed');
                } else {
                    showToast('✅ Task completion confirmed');
                }
            }

            fetchData(currentProjectId);
        } catch (err) {
            console.error('Task completion confirmation error:', err);
            showToast('❌ Failed to confirm task completion. Please try again.');
        }
    };

    // ✅ المسؤول بيأكد إقفال المشروع نفسه بعد ما كل تاسكاته بقت Completed.
    const confirmProjectCompletion = async () => {
        try {
            await supabase.from('projects')
                .update({ status: 'Completed', completion_pending_confirmation: false })
                .eq('id', currentProjectId);

            showToast('🎉 Project completed!');
            fetchData(currentProjectId);
        } catch (err) {
            console.error('Project completion confirmation error:', err);
            showToast('❌ Failed to confirm project completion. Please try again.');
        }
    };


    // attendanceStats/attendanceSummary/totalHours بقوا جوه useTeamController.

    //clinet Portal


    // ========== TEAM MANAGEMENT FUNCTIONS ==========
    // createBaselineRevision/addDependency/deleteDependency بقوا جوه
    // useScheduleController.

    // Fetch all team members (shared across projects)
    const fetchAllTeamMembers = async () => {
        try {
            const { data, error } = await supabase
                .from('team_members')
                .select('*')
                .order('name');
            if (error) throw error;
            setAllTeamMembers(data || []);
        } catch (err) {
            console.error('Error fetching team members:', err);
        }
    };

    // fetchAchievements بقت جوه useTeamController (مع تصحيح باج: كانت بس
    // بتتستدعى بعد تعديل عضو، مش عند أول تحميل — ضفت mount effect ليها هناك).

    // addNewTeamMember/assignExistingMember/removeTeamMember بقوا جوه
    // useTeamController.

    // openEditMemberModal/updateTeamMember/addMemberAchievement/
    // removeMemberAchievement/saveAttendance/openAttendanceModal/
    // generateAttendanceQR بقوا جوه useTeamController.

    // ========== Functions ==========

    //milestone
    // phasesFromTasks بقت جوه useScheduleController.

    // ══════════════════════════════════════════════
    // ✅ EVM — Earned Value Management (احترافي، على مستوى المشروع كله)
    //
    // المبدأ: مفيش "Weight" بيتكتب يدوي — الوزن نسبة محسوبة من task_value.
    // BAC   = مجموع قيم كل التاسكات (Budget at Completion)
    // PV    = مجموع (قيمة التاسك × نسبة الإنجاز *المخطط لها* حسب baseline ثابت)
    // EV    = مجموع (قيمة التاسك × نسبة الإنجاز *الفعلية*)
    // AC    = مجموع المصروفات الفعلية المسجّلة (budget_expenses.amount)
    // SPI   = EV / PV   |   CPI = EV / AC
    // EAC   = BAC / CPI (لو الأداء الحالي استمر لحد آخر المشروع)
    //
    // ملاحظة مهمة: أي تاسك من غير task_value أو من غير baseline_start/baseline_end
    // بيتستبعد من الحساب (مش بيتفترض ليه قيمة أو تاريخ)، وبيتم عدّه في
    // data quality عشان تعرف الأرقام دقيقة قد إيه فعليًا.
    // ══════════════════════════════════════════════
    const evmData = useMemo(() => {
        const today = new Date();
        const tasks = tasksData || [];

        const plannedPct = (t) => {
            if (!t.baseline_start || !t.baseline_end) return null;
            const bs = new Date(t.baseline_start);
            const be = new Date(t.baseline_end);
            if (isNaN(bs.getTime()) || isNaN(be.getTime()) || be <= bs) return null;
            if (today <= bs) return 0;
            if (today >= be) return 100;
            return ((today - bs) / (be - bs)) * 100;
        };

        let bac = 0, pv = 0, ev = 0;
        let missingValueCount = 0, missingBaselineCount = 0, valuedTasksCount = 0;

        tasks.forEach(t => {
            const val = parseFloat(t.task_value) || 0;
            if (val <= 0) { missingValueCount++; return; } // تاسك من غير قيمة = مستبعد كليًا من EVM
            valuedTasksCount++;
            bac += val;
            ev += val * ((t.progress || 0) / 100);
            const pct = plannedPct(t);
            if (pct === null) missingBaselineCount++;
            else pv += val * (pct / 100);
        });

        const ac = (budgetExpenses || []).reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);

        const spi = pv > 0 ? ev / pv : null;
        const cpi = ac > 0 ? ev / ac : null;
        const sv = ev - pv;
        const cv = ev - ac;
        // EAC (CPI-only) — الصيغة الكلاسيكية: BAC/CPI، بتفترض إن الأداء
        // التكلفي الحالي هيستمر لحد آخر المشروع، بس بتتجاهل الأداء الزمني
        // (SPI) خالص.
        const eac = cpi ? bac / cpi : null;
        // ✅ EAC (SPI-adjusted) — صيغة أكتر تحفّظ لمشروع متأخر فعليًا: بتفترض
        // إن الباقي من الشغل هيتأثر بالأداء الزمني والتكلفي مع بعض
        // (BAC / (CPI × SPI))، مش التكلفي بس. لو المشروع متأخر (SPI<1)،
        // الـEAC المتحفظ ده هيطلع أعلى من EAC العادي — فرق الاتنين بيوضح
        // قد إيه التأخير الزمني لوحده بيزود الكلفة المتوقعة.
        const eacSpiAdjusted = (cpi && spi) ? bac / (cpi * spi) : null;
        const etc = eac !== null ? eac - ac : null;
        const vac = eac !== null ? bac - eac : null;

        return {
            bac, pv, ev, ac, spi, cpi, sv, cv, eac, eacSpiAdjusted, etc, vac,
            totalTasks: tasks.length,
            valuedTasksCount,
            missingValueCount,
            missingBaselineCount,
            contractValue: PROJECT?.totalValue || 0,
        };
    }, [tasksData, budgetExpenses, PROJECT]);

    // addPhase/updatePhase/deletePhase/addMilestone/updateMilestone/
    // deleteMilestone بقوا جوه useScheduleController.

    // exportAttendanceRange بقت جوه useTeamController.

    const deleteCategory = async (categoryId) => {
        showConfirm('Are you sure you want to delete this category and all its expenses?', async () => {
            await supabase.from('budget_expenses').delete().eq('category_id', categoryId);
            await supabase.from('budget_categories').delete().eq('category_id', categoryId);
            showToast('Category deleted');
            fetchData(currentProjectId);
        })
    };

    const updateCategory = async () => {
        await supabase.from('budget_categories').update({
            category_name: editingCategory.category_name,
            allocated: parseFloat(editingCategory.allocated),
            notes: editingCategory.notes,
            date: editingCategory.date || null,
        }).eq('category_id', editingCategory.category_id);
        showToast('✅ Category updated!');
        setEditingCategory(null);
        fetchData(currentProjectId);
    };

    const deleteExpense = async (expenseId) => {
        showConfirm('Are you sure you want to delete this expense?', async () => {
            await supabase.from('budget_expenses').delete().eq('id', expenseId);
            showToast('Expense deleted');
            fetchData(currentProjectId);
        })
    };

    const updateExpense = async () => {
        await supabase.from('budget_expenses').update({
            amount: parseFloat(editingExpense.amount),
            recipient: editingExpense.recipient,
            invoice_no: editingExpense.invoice_no,
            expense_date: editingExpense.expense_date,
            description: editingExpense.description,
        }).eq('id', editingExpense.id);
        showToast('✅ Expense updated!');
        setEditingExpense(null);
        fetchData(currentProjectId);
    };
    // handleImportExcel / handleSubmitImport / handleDownloadTemplate / handleSaveMaterial /
    // handleDeleteMaterial / handleEditMaterial كلهم بقوا جوه useMaterialsController
    // (src/hooks/useMaterialsController.js).
    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
    };
    const showConfirm = (message, onConfirm) => {
        setConfirmModal({ show: true, message, onConfirm });
    };


    // getTaskDuration بقت معرّفة على مستوى الملف (فوق) عشان يبقى ليها reference ثابت

    // handleSaveTask / handleToggleTaskSelect / handleOpenTaskDetails / handleEditTask /
    // handleUpdateTaskValue / handleDeleteTask / handleBulkDelete / getSortedTasks كلهم بقوا
    // جوه useTasksController (src/hooks/useTasksController.js) مع باقي state صفحة Tasks.




    // ========== INVOICE APPROVAL HANDLER ==========
    const handleInvoiceApproval = (level, decision) => {
        const doApproval = async () => {
            setApprovingInvoice(true);
            try {
                const updateData = {
                    updated_at: new Date().toISOString()
                };

                if (level === 'operations') {
                    updateData.operations_manager_approval = decision;
                    updateData.operations_manager_id = currentUser.id || currentUser.email;
                    updateData.operations_manager_name = currentUser.name;
                    updateData.operations_approved_at = new Date().toISOString();

                    if (decision === 'Rejected') {
                        updateData.approval_status = 'Rejected';
                        updateData.operations_rejection_reason = rejectionReason || 'No reason provided';
                    }
                } else if (level === 'CEO') {
                    updateData.ceo_approval = decision;
                    updateData.ceo_id = currentUser.id || currentUser.email;
                    updateData.ceo_name = currentUser.name;
                    updateData.ceo_approved_at = new Date().toISOString();

                    if (decision === 'Approved') {
                        updateData.approval_status = 'Approved';
                    } else {
                        updateData.approval_status = 'Rejected';
                        updateData.CEO_rejection_reason = rejectionReason || 'No reason provided';
                    }
                }

                const { error } = await supabase
                    .from('invoices')
                    .update(updateData)
                    .eq('id', selectedInvoice.id);

                if (error) throw error;

                showToast(`Invoice ${decision.toLowerCase()} successfully!`);
                setShowInvoiceDetailsModal(false);
                setRejectionReason('');
                fetchData();

            } catch (err) {
                console.error('❌ Error updating invoice:', err);
                showToast('❌ Failed to update invoice. Please try again.');
            } finally {
                setApprovingInvoice(false);
            }
        };

        if (decision === 'Rejected' && !rejectionReason.trim()) {
            showConfirm('Are you sure you want to reject this invoice without providing a reason?', doApproval);
        } else {
            doApproval();
        }
    };

    // Systems Health
    // دالة موحّدة لتبديل المشروع (تصفير بيانات المشروع القديم + جلب بيانات الجديد).
    // كانت منطق التبديل مكرر في نسختين: نسخة ناقصة هنا (كانت اسمها handleProjectChange
    // وبتعمل setCurrentProjectId بس، من غير تصفير أو fetch) ونسخة كاملة متكررة جوه
    // ProjectSelector. دلوقتي بقت نسخة واحدة كاملة وموحّدة.
    const switchProject = (newProjectId) => {
        if (newProjectId === currentProjectId) return;
        // ✅ كان بيعمل fetchData(newProjectId) + fetchAllTeamMembers() هنا يدويًا،
        // وبعدين setCurrentProjectId كان بيشغّل useEffect([isLoggedIn, currentProjectId])
        // اللي بيعمل نفس التصفير ونفس الـ fetch تاني — يعني كل تبديل مشروع كان
        // بيجيب كل البيانات مرتين. دلوقتي switchProject بس بيغيّر الـ id،
        // والـ useEffect هو المسؤول الوحيد عن التصفير والجلب.
        setSwitchingProject(true);
        showToast(`Switching to ${PROJECTS[newProjectId]?.shortName}...`, 'info');
        localStorage.setItem('selectedProject', newProjectId);
        setCurrentProjectId(newProjectId);
    };
    const [categoryForm, setCategoryForm] = useState({
        category_name: '',
        allocated: '',
        notes: '',
        date: new Date().toISOString().split('T')[0],
    });

    const [expenseForm, setExpenseForm] = useState({
        category_id: '',
        amount: '',
        recipient: '',
        invoice_no: '',
        expense_date: new Date().toISOString().split('T')[0],
        description: '',
        attachment: null,
        linked_commitment_id: '', // ✅ لو الصرف ده بيقفل/يجزّئ commitment مفتوح
    });

    // ✅ Commitments — نفس فكرة categoryForm/expenseForm بالظبط
    const [commitmentForm, setCommitmentForm] = useState({
        category_id: '',
        vendor: '',
        po_number: '',
        amount: '',
        commitment_date: new Date().toISOString().split('T')[0],
        description: '',
    });

    // Gantt Chart State
    const [ganttView, setGanttView] = useState("week");// month, quarter, year
    const [ganttstart_date, setGanttstart_date] = useState(() => {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth(), 1);
    });


    // Documentation State — documentsData اتسابت هنا (بتتحمّل من fetchData
    // المركزية)؛ باقي state صفحة Documents بقى جوه useDocumentsController.
    const [documentsData, setDocumentsData] = useState([]);

    // حساب Total Spent لكل بند
    const getCategorySpent = (categoryId) => {
        return budgetExpenses
            .filter(exp => exp.category_id === categoryId)
            .reduce((sum, exp) => sum + exp.amount, 0);
    };

    // Get expenses لبند معين
    const getCategoryExpenses = (categoryId) => {
        return budgetExpenses.filter(exp => exp.category_id === categoryId);
    };

    // ✅ Commitments — مجموع الجزء "المفتوح" لسه من كل commitment في حالة
    // open لبند معين. مش amount كامل — ده (amount − converted_amount) عشان
    // لو جزء من الـPO اتحول لمصروف فعلي (expense مرتبط بيه)، الجزء ده يتشال
    // من "Committed" ومايتحسبش مرتين (مرة Committed ومرة Spent). مش بيتحسب
    // جوه AC/EVM خالص — ده رقم إضافي للـ Cost Control بس.
    const getCategoryCommitted = (categoryId) => {
        return budgetCommitments
            .filter(c => c.category_id === categoryId && c.status === 'open')
            .reduce((sum, c) => sum + Math.max(0, c.amount - (c.converted_amount || 0)), 0);
    };

    const getCategoryCommitments = (categoryId) => {
        return budgetCommitments.filter(c => c.category_id === categoryId);
    };

    const addBudgetCategory = async () => {
        if (!categoryForm.category_name || !categoryForm.allocated) {
            showToast("Please fill all required fields");
            return;
        }
        setUploadingCategory(true);

        try {
            const categoryId = `BDG-${Date.now()}`;

            const { error } = await supabase
                .from('budget_categories')
                .insert([
                    {
                        project_id: currentProjectId,
                        category_id: categoryId,
                        category_name: categoryForm.category_name,
                        allocated: parseFloat(categoryForm.allocated),
                        notes: categoryForm.notes,
                        date: categoryForm.date || null,
                        created_by: currentUser.name,
                        created_date: new Date().toISOString()
                    }
                ])
                .select();

            if (error) throw error;

            showToast("Budget category added successfully!");
            setShowAddCategoryModal(false);
            setCategoryForm({ category_name: '', allocated: '', notes: '' });

            // Refresh data
            fetchData();

        } catch (err) {
            console.error(err);
            showToast("❌ Failed to add category. Please try again.");
        } finally {
            setUploadingCategory(false);
        }
    };

    const addBudgetExpense = async () => {
        if (!expenseForm.category_id || !expenseForm.amount || !expenseForm.recipient) {
            showToast("Please fill all required fields");
            return;
        }
        setUploadingExpense(true);

        try {
            const expenseId = `EXP-${Date.now()}`;

            // Upload attachment if exists
            let attachmentUrl = null;
            if (expenseForm.attachment) {
                const fileExt = expenseForm.attachment.name.split('.').pop();
                const fileName = `${expenseId}.${fileExt}`;
                const filePath = `${currentProjectId}/expenses/${fileName}`;



                const { error: uploadError } = await supabase.storage
                    .from('attachments')
                    .upload(filePath, expenseForm.attachment);

                if (uploadError) {
                    console.error('Upload error:', uploadError);
                    throw uploadError;
                }

                // Get public URL
                const { data: { publicUrl } } = supabase.storage
                    .from('attachments')
                    .getPublicUrl(filePath);

                attachmentUrl = publicUrl;

            }

            // Insert expense
            const { error } = await supabase
                .from('budget_expenses')
                .insert([
                    {
                        project_id: currentProjectId,
                        expense_id: expenseId,
                        category_id: expenseForm.category_id,
                        amount: parseFloat(expenseForm.amount),
                        recipient: expenseForm.recipient,
                        invoice_no: expenseForm.invoice_no,
                        expense_date: expenseForm.expense_date,
                        description: expenseForm.description,
                        attachment_url: attachmentUrl,
                        created_by: currentUser.name,
                        created_at: new Date().toISOString(),
                        linked_commitment_id: expenseForm.linked_commitment_id || null,
                    }
                ])
                .select();

            if (error) throw error;

            // ✅ لو الصرف ده مرتبط بـcommitment مفتوح، نحدّث الجزء المتحوّل
            // منه (converted_amount) ونقفله تلقائيًا لو اتغطى بالكامل — بدل
            // ما نسيب المستخدم يفتكر يضغط "Close" يدويًا، واللي لو نساه كان
            // هيخلي الفلوس تتحسب مرتين (Committed + Spent) في نفس الوقت.
            if (expenseForm.linked_commitment_id) {
                const commitment = budgetCommitments.find(c => c.commitment_id === expenseForm.linked_commitment_id);
                if (commitment) {
                    const newConverted = Math.min(commitment.amount, (commitment.converted_amount || 0) + parseFloat(expenseForm.amount));
                    await supabase.from('budget_commitments').update({
                        converted_amount: newConverted,
                        status: newConverted >= commitment.amount ? 'closed' : 'open',
                    }).eq('id', commitment.id);
                }
            }

            showToast("Expense added successfully!");
            setShowAddExpenseModal(false);
            setExpenseForm({
                category_id: '',
                amount: '',
                recipient: '',
                invoice_no: '',
                expense_date: new Date().toISOString().split('T')[0],
                description: '',
                attachment: null,
                linked_commitment_id: '',
            });

            // Refresh data
            fetchData();

        } catch (err) {
            console.error(err);
            showToast("❌ Failed to add expense. Please try again.");
        } finally {
            setUploadingExpense(false);
        }
    };

    // ✅ Commitments — PO/التزام اتفق عليه بس لسه ما اتصرفش فعليًا (بيتحول
    // لـ expense حقيقي لاحقًا وقت الصرف الفعلي، وهنا بنقفله status='closed'
    // بدل ما نمسحه، عشان يفضل في السجل التاريخي).
    const addBudgetCommitment = async () => {
        if (!commitmentForm.category_id || !commitmentForm.amount || !commitmentForm.vendor) {
            showToast("Please fill all required fields");
            return;
        }
        setUploadingCommitment(true);

        try {
            const commitmentId = `CM-${Date.now()}`;

            const { error } = await supabase
                .from('budget_commitments')
                .insert([
                    {
                        project_id: currentProjectId,
                        commitment_id: commitmentId,
                        category_id: commitmentForm.category_id,
                        po_number: commitmentForm.po_number,
                        vendor: commitmentForm.vendor,
                        amount: parseFloat(commitmentForm.amount),
                        commitment_date: commitmentForm.commitment_date,
                        description: commitmentForm.description,
                        status: 'open',
                        created_by: currentUser.name,
                        created_at: new Date().toISOString()
                    }
                ])
                .select();

            if (error) throw error;

            showToast("Commitment added successfully!");
            setShowAddCommitmentModal(false);
            setCommitmentForm({
                category_id: '',
                vendor: '',
                po_number: '',
                amount: '',
                commitment_date: new Date().toISOString().split('T')[0],
                description: '',
            });

            fetchData();

        } catch (err) {
            console.error(err);
            showToast("❌ Failed to add commitment. Please try again.");
        } finally {
            setUploadingCommitment(false);
        }
    };

    const updateCommitment = async () => {
        if (!editingCommitment) return;
        await supabase.from('budget_commitments').update({
            category_id: editingCommitment.category_id,
            vendor: editingCommitment.vendor,
            po_number: editingCommitment.po_number,
            amount: parseFloat(editingCommitment.amount),
            commitment_date: editingCommitment.commitment_date,
            description: editingCommitment.description,
            status: editingCommitment.status,
        }).eq('id', editingCommitment.id);
        showToast('✅ Commitment updated!');
        setEditingCommitment(null);
        fetchData(currentProjectId);
    };

    // ✅ بنقفل الـ commitment (status='closed') بدل ما نمسحه نهائيًا — بيفضل
    // في السجل عشان يبين إنه اتحول لمصروف فعلي أو اتلغى، مش بس اختفى.
    const closeCommitment = async (commitmentId) => {
        showConfirm('Mark this commitment as closed (converted to an actual expense, or cancelled)?', async () => {
            await supabase.from('budget_commitments').update({ status: 'closed' }).eq('id', commitmentId);
            showToast('Commitment closed');
            fetchData(currentProjectId);
        });
    };

    const deleteCommitment = async (commitmentId) => {
        showConfirm('Are you sure you want to delete this commitment?', async () => {
            await supabase.from('budget_commitments').delete().eq('id', commitmentId);
            showToast('Commitment deleted');
            fetchData(currentProjectId);
        });
    };


    // ========== GANTT CHART FUNCTIONS ==========

    // Generate timeline dates based on view
    const getTimelineColumns = () => {
        const columns = [];
        const start = new Date(ganttstart_date);
        if (ganttView === "week") {
            for (let i = 0; i < 30; i++) {
                const date = new Date(start);
                date.setDate(start.getDate() + i);
                columns.push({
                    label: date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }),
                    date: new Date(date),
                    isWeekend: date.getDay() === 5 || date.getDay() === 6,
                    dayNum: date.getDate(),
                    dayName: date.toLocaleDateString('en-GB', { weekday: 'short' }),
                });
            }
        }
        else if (ganttView === "month") {

            // Show 6 months
            for (let i = 0; i < 6; i++) {
                const date = new Date(start);
                date.setMonth(start.getMonth() + i);
                columns.push({
                    label: date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
                    date: new Date(date)
                });
            }
        } else if (ganttView === "quarter") {
            // Show 4 quarters
            for (let i = 0; i < 4; i++) {
                const date = new Date(start);
                date.setMonth(start.getMonth() + (i * 3));
                columns.push({
                    label: `Q${Math.floor(date.getMonth() / 3) + 1} ${date.getFullYear()}`,
                    date: new Date(date)
                });
            }
        } else {
            // Show 2 years
            for (let i = 0; i < 2; i++) {
                const date = new Date(start);
                date.setFullYear(start.getFullYear() + i);
                columns.push({
                    label: date.getFullYear().toString(),
                    date: new Date(date)
                });
            }
        }

        return columns;
    };

    // Calculate task position and width in timeline
    const getTaskBarStyle = (task, columns) => {
        if (!task.start_date || !task.deadline_date) return null;

        const taskStart = new Date(task.start_date);
        taskStart.setHours(0, 0, 0, 0);
        const taskEnd = new Date(task.deadline_date);
        taskEnd.setHours(0, 0, 0, 0);

        const timelineStart = new Date(columns[0].date);
        timelineStart.setHours(0, 0, 0, 0);
        const timelineEnd = new Date(columns[columns.length - 1].date);
        timelineEnd.setHours(0, 0, 0, 0);

        if (taskEnd < timelineStart || taskStart > timelineEnd) return null;

        const totalDays = columns.length; // ✅ عدد الـ columns الفعلي
        const taskStartDays = Math.max(0, (taskStart - timelineStart) / 86400000);
        const taskDuration = Math.min(
            (taskEnd - taskStart) / 86400000 + 1,
            totalDays - taskStartDays
        );

        const left = (taskStartDays / totalDays) * 100;
        const width = (taskDuration / totalDays) * 100;

        return { left: `${left}%`, width: `${Math.max(width, 1)}%` };
    };

    // Navigate timeline
    const navigateTimeline = (direction) => {
        const newDate = new Date(ganttstart_date);
        if (ganttView === "week") {
            newDate.setDate(newDate.getDate() + (direction === "next" ? 30 : -30));
        }
        else if (ganttView === "month") {
            newDate.setMonth(newDate.getMonth() + (direction === "next" ? 6 : -6));
        } else if (ganttView === "quarter") {
            newDate.setMonth(newDate.getMonth() + (direction === "next" ? 12 : -12));
        } else {
            newDate.setFullYear(newDate.getFullYear() + (direction === "next" ? 2 : -2));
        }

        setGanttstart_date(newDate);
    };

    // Group tasks by system
    const groupedTasks = tasksData.reduce((groups, task) => {
        const system = task.system || "Other";
        if (!groups[system]) groups[system] = [];
        groups[system].push(task);
        return groups;
    }, {});

    // deleteDocument / uploadDocument / filteredDocuments / getFileIcon /
    // formatFileSize / documentStats كلهم بقوا جوه useDocumentsController
    // (src/hooks/useDocumentsController.jsx).
    // ========== PDF REPORT GENERATOR ==========
    // npm install jspdf jspdf-autotable html2canvas

    const generatePDF = async () => {
        const { jsPDF } = window.jspdf || await import('jspdf');
        const autoTable = (await import('jspdf-autotable')).default;

        const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
        const pageW = 210;
        const pageH = 297;
        const margin = 15;
        let y = 0;

        const colors = {
            primary: [37, 99, 235],      // blue-600
            secondary: [124, 58, 237],   // violet-600
            success: [16, 185, 129],     // emerald-500
            warning: [245, 158, 11],     // amber-500
            danger: [239, 68, 68],       // red-500
            dark: [15, 23, 42],          // slate-900
            gray: [100, 116, 139],       // slate-500
            lightGray: [241, 245, 249],  // slate-100
            white: [255, 255, 255],
        };

        const addPage = () => {
            doc.addPage();
            y = margin;
        };

        // ══════════════════════════════════════════
        // PAGE 1 — COVER
        // ══════════════════════════════════════════

        // Background gradient simulation
        doc.setFillColor(...colors.dark);
        doc.rect(0, 0, pageW, pageH, 'F');

        // Decorative circles
        doc.setFillColor(37, 99, 235, 0.3);
        doc.circle(pageW - 20, 40, 60, 'F');
        doc.setFillColor(124, 58, 237, 0.2);
        doc.circle(20, pageH - 40, 50, 'F');

        // Top accent bar
        doc.setFillColor(...colors.primary);
        doc.rect(0, 0, pageW, 3, 'F');

        // Logo / Icon area
        doc.setFillColor(37, 99, 235, 0.15);
        doc.roundedRect(margin, 35, 30, 30, 5, 5, 'F');
        doc.setFillColor(...colors.primary);
        doc.roundedRect(margin + 8, 45, 14, 14, 3, 3, 'F');

        // Report type label
        doc.setTextColor(...colors.gray);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text('PROJECT MANAGEMENT REPORT', margin, 85);

        // Project name
        doc.setTextColor(...colors.white);
        doc.setFontSize(28);
        doc.setFont('helvetica', 'bold');
        const projectName = PROJECT?.name || 'Project Report';
        const splitName = doc.splitTextToSize(projectName, pageW - margin * 2);
        doc.text(splitName, margin, 100);

        // Client
        doc.setTextColor(...colors.gray);
        doc.setFontSize(13);
        doc.setFont('helvetica', 'normal');
        doc.text(PROJECT?.client || '', margin, 118);

        // Divider
        doc.setDrawColor(...colors.primary);
        doc.setLineWidth(0.5);
        doc.line(margin, 125, pageW - margin, 125);

        // Stats row on cover
        const coverStats = [
            { label: 'CONTRACT', value: PROJECT?.contract || 'N/A' },
            { label: 'STATUS', value: PROJECT?.status || 'Active' },
            { label: 'TOTAL TASKS', value: String(tasksData.length) },
            { label: 'COMPLETION', value: `${Math.round((tasksData.filter(t => t.status === 'Completed').length / (tasksData.length || 1)) * 100)}%` },
        ];

        const statW = (pageW - margin * 2) / coverStats.length;
        coverStats.forEach((s, i) => {
            const x = margin + i * statW;
            doc.setTextColor(...colors.gray);
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            doc.text(s.label, x, 140);
            doc.setTextColor(...colors.white);
            doc.setFontSize(14);
            doc.setFont('helvetica', 'bold');
            doc.text(s.value, x, 150);
        });

        // Progress bar on cover
        const progress = Math.round((tasksData.filter(t => t.status === 'Completed').length / (tasksData.length || 1)) * 100);
        doc.setFillColor(255, 255, 255, 0.1);
        doc.roundedRect(margin, 162, pageW - margin * 2, 6, 3, 3, 'F');
        doc.setFillColor(...colors.primary);
        doc.roundedRect(margin, 162, (pageW - margin * 2) * (progress / 100), 6, 3, 3, 'F');
        doc.setTextColor(...colors.gray);
        doc.setFontSize(8);
        doc.text(`Overall Progress: ${progress}%`, margin, 175);

        // Project dates
        doc.setFillColor(255, 255, 255, 0.05);
        doc.roundedRect(margin, 182, pageW - margin * 2, 28, 4, 4, 'F');

        const dateItems = [
            { label: 'Start Date', value: PROJECT?.startDate ? new Date(PROJECT.startDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A' },
            { label: 'End Date', value: PROJECT?.endDate ? new Date(PROJECT.endDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A' },
            { label: 'Location', value: PROJECT?.location || 'N/A' },
            { label: 'Total Value', value: PROJECT?.totalValue ? `SAR ${Number(PROJECT.totalValue).toLocaleString()}` : 'N/A' },
        ];

        dateItems.forEach((item, i) => {
            const x = margin + 8 + i * ((pageW - margin * 2) / 4);
            doc.setTextColor(...colors.gray);
            doc.setFontSize(7);
            doc.setFont('helvetica', 'normal');
            doc.text(item.label.toUpperCase(), x, 191);
            doc.setTextColor(...colors.white);
            doc.setFontSize(9);
            doc.setFont('helvetica', 'bold');
            doc.text(item.value, x, 199);
        });

        // Team & generated info
        doc.setTextColor(...colors.gray);
        doc.setFontSize(9);
        doc.text(`Team Members: ${teamData.length}  |  Open Challenges: ${Object.values(taskChallenges).flat().filter(c => c.status === 'Open').length}  |  Photos: ${photosData.length}`, margin, 225);

        // Generated date
        doc.setFontSize(8);
        doc.text(`Generated: ${new Date().toLocaleString('en-GB', { timeZone: 'Asia/Riyadh' })}`, margin, 235);
        doc.text(`Prepared by: ${currentUser?.name || 'System'}  •  ${currentUser?.position || ''}`, margin, 242);

        // Bottom bar
        doc.setFillColor(...colors.primary);
        doc.rect(0, pageH - 3, pageW, 3, 'F');

        // ══════════════════════════════════════════
        // PAGE 2 — EXECUTIVE SUMMARY
        // ══════════════════════════════════════════
        addPage();

        // Page header
        const drawPageHeader = (title, subtitle = '') => {
            doc.setFillColor(...colors.primary);
            doc.rect(0, 0, pageW, 18, 'F');
            doc.setTextColor(...colors.white);
            doc.setFontSize(13);
            doc.setFont('helvetica', 'bold');
            doc.text(title, margin, 12);
            if (subtitle) {
                doc.setFontSize(8);
                doc.setFont('helvetica', 'normal');
                doc.text(subtitle, pageW - margin - doc.getTextWidth(subtitle), 12);
            }
            doc.setTextColor(...colors.dark);
            y = 28;
        };

        const drawPageFooter = (pageNum) => {
            doc.setFillColor(...colors.lightGray);
            doc.rect(0, pageH - 10, pageW, 10, 'F');
            doc.setTextColor(...colors.gray);
            doc.setFontSize(7);
            doc.text(PROJECT?.name || '', margin, pageH - 4);
            doc.text(`Page ${pageNum}`, pageW / 2, pageH - 4, { align: 'center' });
            doc.text(new Date().toLocaleDateString('en-GB'), pageW - margin, pageH - 4, { align: 'right' });
        };

        drawPageHeader('EXECUTIVE SUMMARY', PROJECT?.shortName || '');
        drawPageFooter(2);

        // Summary cards
        const completed = tasksData.filter(t => t.status === 'Completed').length;
        const inProgress = tasksData.filter(t => t.status === 'In Progress').length;
        const pending = tasksData.filter(t => t.status === 'Pending').length;
        const overdue = tasksData.filter(t => {
            const deadline = t.deadline_date ? new Date(t.deadline_date) : null;
            return deadline && deadline < new Date() && t.status !== 'Completed';
        }).length;

        const summaryCards = [
            { label: 'Total Tasks', value: tasksData.length, color: colors.primary },
            { label: 'Completed', value: completed, color: colors.success },
            { label: 'In Progress', value: inProgress, color: colors.primary },
            { label: 'Pending', value: pending, color: colors.warning },
            { label: 'Overdue', value: overdue, color: colors.danger },
            { label: 'Team Members', value: teamData.length, color: colors.secondary },
        ];

        const cardW = (pageW - margin * 2 - 10) / 3;
        const cardH = 22;
        summaryCards.forEach((card, i) => {
            const col = i % 3;
            const row = Math.floor(i / 3);
            const x = margin + col * (cardW + 5);
            const cardY = y + row * (cardH + 5);

            doc.setFillColor(...colors.lightGray);
            doc.roundedRect(x, cardY, cardW, cardH, 3, 3, 'F');
            doc.setFillColor(...card.color);
            doc.roundedRect(x, cardY, 3, cardH, 1, 1, 'F');

            doc.setTextColor(...card.color);
            doc.setFontSize(18);
            doc.setFont('helvetica', 'bold');
            doc.text(String(card.value), x + 8, cardY + 14);

            doc.setTextColor(...colors.gray);
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            doc.text(card.label, x + 8, cardY + 20);
        });

        y += 2 * (cardH + 5) + 10;

        // Completion rate visual
        doc.setFillColor(...colors.lightGray);
        doc.roundedRect(margin, y, pageW - margin * 2, 20, 3, 3, 'F');
        doc.setTextColor(...colors.dark);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text('Overall Completion Rate', margin + 5, y + 8);
        doc.setTextColor(...colors.success);
        doc.setFontSize(14);
        doc.text(`${Math.round((completed / (tasksData.length || 1)) * 100)}%`, pageW - margin - 20, y + 12);

        const barX = margin + 5;
        const barW = pageW - margin * 2 - 30;
        doc.setFillColor(209, 213, 219);
        doc.roundedRect(barX, y + 12, barW, 4, 2, 2, 'F');
        doc.setFillColor(...colors.success);
        doc.roundedRect(barX, y + 12, barW * (completed / (tasksData.length || 1)), 4, 2, 2, 'F');

        y += 30;

        // ══════════════════════════════════════════
        // PAGE 3 — TASKS
        // ══════════════════════════════════════════
        addPage();
        drawPageHeader('TASKS OVERVIEW', `${tasksData.length} Total Tasks`);
        drawPageFooter(3);

        // Tasks by status breakdown
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...colors.dark);
        doc.text('Tasks Distribution', margin, y);
        y += 6;

        const statusGroups = [
            { status: 'Completed', color: colors.success },
            { status: 'In Progress', color: colors.primary },
            { status: 'Pending', color: colors.warning },
        ];

        statusGroups.forEach((g, i) => {
            const count = tasksData.filter(t => t.status === g.status).length;
            const pct = Math.round((count / (tasksData.length || 1)) * 100);
            const bX = margin + i * ((pageW - margin * 2) / 3 + 2);
            const bW = (pageW - margin * 2) / 3 - 2;

            doc.setFillColor(...colors.lightGray);
            doc.roundedRect(bX, y, bW, 14, 2, 2, 'F');
            doc.setFillColor(...g.color);
            doc.roundedRect(bX, y, bW * (pct / 100), 14, 2, 2, 'F');
            doc.setTextColor(...colors.white);
            doc.setFontSize(8);
            doc.setFont('helvetica', 'bold');
            doc.text(`${g.status}: ${count} (${pct}%)`, bX + 3, y + 9);
        });

        y += 22;

        // Tasks table
        autoTable(doc, {
            startY: y,
            head: [['#', 'Task', 'System', 'Assigned', 'Priority', 'Progress', 'Status', 'Deadline']],
            body: tasksData.map((t, i) => [
                i + 1,
                t.sub_task || 'N/A',
                t.system || 'N/A',
                t.assigned_to || 'N/A',
                t.priority || 'Medium',
                `${t.progress || 0}%`,
                t.status || 'Pending',
                t.deadline_date ? new Date(t.deadline_date).toLocaleDateString('en-GB') : 'N/A',
            ]),
            styles: { fontSize: 7.5, cellPadding: 3, font: 'helvetica' },
            headStyles: { fillColor: colors.primary, textColor: colors.white, fontStyle: 'bold', fontSize: 8 },
            alternateRowStyles: { fillColor: [248, 250, 252] },
            columnStyles: {
                0: { cellWidth: 8 },
                1: { cellWidth: 45 },
                2: { cellWidth: 25 },
                3: { cellWidth: 25 },
                4: { cellWidth: 18 },
                5: { cellWidth: 16 },
                6: { cellWidth: 20, textColor: [255, 255, 255] },
                7: { cellWidth: 20 },
            },
            didParseCell: (data) => {
                if (data.column.index === 6 && data.section === 'body') {
                    const status = data.cell.raw;
                    const color = status === 'Completed' ? colors.success : status === 'In Progress' ? colors.primary : colors.warning;
                    data.cell.styles.fillColor = color;
                    data.cell.styles.textColor = [255, 255, 255];
                    data.cell.styles.fontStyle = 'bold';
                    data.cell.styles.halign = 'center';
                }
            },
            margin: { left: margin, right: margin },
        });

        // ══════════════════════════════════════════
        // PAGE 4 — TEAM & PERFORMANCE
        // ══════════════════════════════════════════
        doc.addPage();
        drawPageHeader('TEAM & PERFORMANCE', `${teamData.length} Members`);
        drawPageFooter(4);
        y = 28;

        autoTable(doc, {
            startY: y,
            head: [['#', 'Name', 'Position', 'Department', 'Nationality', 'Productivity', 'Quality', 'Teamwork', 'Reliability', 'Avg Score']],
            body: teamData.map((m, i) => {
                const perf = performanceData.find(p => p.member_id === m.id);
                const avg = perf ? Math.round((perf.productivity + perf.quality + perf.teamwork + perf.reliability) / 4) : 0;
                return [
                    i + 1,
                    m.name || '-',
                    m.position || '-',
                    m.department || '-',
                    m.nationality || '-',
                    perf ? `${perf.productivity}%` : '-',
                    perf ? `${perf.quality}%` : '-',
                    perf ? `${perf.teamwork}%` : '-',
                    perf ? `${perf.reliability}%` : '-',
                    `${avg}%`,
                ];
            }),
            styles: { fontSize: 7.5, cellPadding: 3 },
            headStyles: { fillColor: colors.secondary, textColor: colors.white, fontStyle: 'bold', fontSize: 8 },
            alternateRowStyles: { fillColor: [248, 250, 252] },
            didParseCell: (data) => {
                if (data.column.index === 9 && data.section === 'body') {
                    const val = parseInt(data.cell.raw);
                    const color = val >= 80 ? colors.success : val >= 60 ? colors.warning : colors.danger;
                    data.cell.styles.textColor = color;
                    data.cell.styles.fontStyle = 'bold';
                    data.cell.styles.halign = 'center';
                }
            },
            margin: { left: margin, right: margin },
        });

        // ══════════════════════════════════════════
        // PAGE 5 — CHALLENGES
        // ══════════════════════════════════════════
        const allChallenges = Object.values(taskChallenges).flat();
        if (allChallenges.length > 0) {
            doc.addPage();
            drawPageHeader('CHALLENGES REPORT', `${allChallenges.filter(c => c.status === 'Open').length} Open`);
            drawPageFooter(5);
            y = 28;

            // Summary
            const openC = allChallenges.filter(c => c.status === 'Open').length;
            const resolvedC = allChallenges.filter(c => c.status === 'Resolved').length;
            const criticalC = allChallenges.filter(c => c.severity === 'Critical').length;

            const cCards = [
                { label: 'Total', value: allChallenges.length, color: colors.primary },
                { label: 'Open', value: openC, color: colors.danger },
                { label: 'Resolved', value: resolvedC, color: colors.success },
                { label: 'Critical', value: criticalC, color: [220, 38, 38] },
            ];

            const cCardW = (pageW - margin * 2 - 15) / 4;
            cCards.forEach((c, i) => {
                const x = margin + i * (cCardW + 5);
                doc.setFillColor(...colors.lightGray);
                doc.roundedRect(x, y, cCardW, 18, 3, 3, 'F');
                doc.setFillColor(...c.color);
                doc.roundedRect(x, y, 3, 18, 1, 1, 'F');
                doc.setTextColor(...c.color);
                doc.setFontSize(16);
                doc.setFont('helvetica', 'bold');
                doc.text(String(c.value), x + 8, y + 12);
                doc.setTextColor(...colors.gray);
                doc.setFontSize(7);
                doc.setFont('helvetica', 'normal');
                doc.text(c.label, x + 8, y + 17);
            });

            y += 26;

            autoTable(doc, {
                startY: y,
                head: [['Task', 'Challenge', 'Severity', 'Status', 'Reported By', 'Date']],
                body: allChallenges.map(c => {
                    const task = tasksData.find(t => t.id === c.task_id);
                    return [
                        task?.sub_task || 'N/A',
                        c.description || 'N/A',
                        c.severity || 'Medium',
                        c.status || 'Open',
                        c.created_by || 'N/A',
                        c.created_at ? new Date(c.created_at).toLocaleDateString('en-GB') : 'N/A',
                    ];
                }),
                styles: { fontSize: 7.5, cellPadding: 3 },
                headStyles: { fillColor: colors.danger, textColor: colors.white, fontStyle: 'bold' },
                alternateRowStyles: { fillColor: [248, 250, 252] },
                columnStyles: {
                    0: { cellWidth: 35 },
                    1: { cellWidth: 65 },
                    2: { cellWidth: 20 },
                    3: { cellWidth: 20 },
                    4: { cellWidth: 25 },
                    5: { cellWidth: 20 },
                },
                margin: { left: margin, right: margin },
            });
        }

        // ══════════════════════════════════════════
        // SAVE
        // ══════════════════════════════════════════
        const fileName = `${PROJECT?.shortName || 'Project'}_Report_${new Date().toLocaleDateString('en-GB').replace(/\//g, '-')}.pdf`;
        doc.save(fileName);
        showToast('✅ Report generated successfully!', 'success');
    };

    // ========== HR SUMMARY PDF (HR Portal) ==========
    // بورتال HR مالوش مشروع واحد يتبني عليه تقرير — الملخص هنا لازم يبقى
    // على مستوى كل المشاريع (حضور اليوم، تقارير يومية ناقصة، طلبات إجازة
    // معلّقة) بدل تقرير المشروع الواحد اللي بيعمله generatePDF.
    const generateHRSummaryPDF = async () => {
        const { jsPDF } = window.jspdf || await import('jspdf');
        const autoTable = (await import('jspdf-autotable')).default;

        const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
        const pageW = 210;
        const margin = 15;
        let y = margin;

        const colors = {
            primary: [37, 99, 235],
            secondary: [124, 58, 237],
            success: [16, 185, 129],
            warning: [245, 158, 11],
            danger: [239, 68, 68],
            dark: [15, 23, 42],
            gray: [100, 116, 139],
            lightGray: [241, 245, 249],
            white: [255, 255, 255],
        };

        try {
            const today = new Date().toISOString().split('T')[0];

            const { data: activeProjects } = await supabase
                .from('projects')
                .select('id, name, short_name')
                .eq('status', 'Active');

            const [{ data: attToday }, { data: reportsToday }, { data: pendingLeaves }, { data: assignments }] = await Promise.all([
                supabase.from('attendance').select('project_id, status').eq('date', today),
                supabase.from('daily_activities').select('project_id').eq('date', today),
                supabase.from('leave_requests').select('id, leave_type, leave_date, end_date, status, team_members(name)').eq('status', 'Pending').order('created_at', { ascending: false }),
                supabase.from('project_team_assignments').select('project_id, team_member_id'),
            ]);

            const byProject = {};
            (attToday || []).forEach(a => {
                const key = String(a.project_id);
                byProject[key] = byProject[key] || { present: 0, absent: 0, total: 0 };
                byProject[key].total += 1;
                if (a.status === 'Present' || a.status === 'Late') byProject[key].present += 1;
                if (a.status === 'Absent') byProject[key].absent += 1;
            });

            const teamCounts = {};
            (assignments || []).forEach(a => { teamCounts[a.project_id] = (teamCounts[a.project_id] || 0) + 1; });

            const reportedIds = new Set((reportsToday || []).map(r => String(r.project_id)));
            const missingReports = (activeProjects || []).filter(p => !reportedIds.has(String(p.id)));

            const totalPresent = Object.values(byProject).reduce((s, c) => s + c.present, 0);
            const totalRecorded = Object.values(byProject).reduce((s, c) => s + c.total, 0);
            const avgRate = totalRecorded > 0 ? Math.round((totalPresent / totalRecorded) * 100) : 0;
            const totalTeam = Object.values(teamCounts).reduce((s, c) => s + c, 0);

            // ══════════════════════════════════════════
            // COVER
            // ══════════════════════════════════════════
            doc.setFillColor(...colors.dark);
            doc.rect(0, 0, pageW, 297, 'F');
            doc.setFillColor(...colors.secondary);
            doc.rect(0, 0, pageW, 3, 'F');

            doc.setFillColor(109, 40, 217, 0.15);
            doc.roundedRect(margin, 35, 30, 30, 5, 5, 'F');
            doc.setFillColor(...colors.secondary);
            doc.roundedRect(margin + 8, 45, 14, 14, 3, 3, 'F');

            doc.setTextColor(...colors.gray);
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            doc.text('HUMAN RESOURCES REPORT', margin, 85);

            doc.setTextColor(...colors.white);
            doc.setFontSize(26);
            doc.setFont('helvetica', 'bold');
            doc.text('HR Summary Report', margin, 100);

            doc.setTextColor(...colors.gray);
            doc.setFontSize(12);
            doc.setFont('helvetica', 'normal');
            doc.text(new Date().toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }), margin, 116);

            doc.setDrawColor(...colors.secondary);
            doc.setLineWidth(0.5);
            doc.line(margin, 124, pageW - margin, 124);

            const coverStats = [
                { label: 'ACTIVE PROJECTS', value: String((activeProjects || []).length) },
                { label: 'TOTAL TEAM', value: String(totalTeam) },
                { label: 'ATTENDANCE TODAY', value: `${avgRate}%` },
                { label: 'PENDING LEAVE', value: String((pendingLeaves || []).length) },
            ];
            const statW = (pageW - margin * 2) / coverStats.length;
            coverStats.forEach((s, i) => {
                const x = margin + i * statW;
                doc.setTextColor(...colors.gray);
                doc.setFontSize(8);
                doc.setFont('helvetica', 'normal');
                doc.text(s.label, x, 140);
                doc.setTextColor(...colors.white);
                doc.setFontSize(14);
                doc.setFont('helvetica', 'bold');
                doc.text(s.value, x, 150);
            });

            // ══════════════════════════════════════════
            // PAGE 2 — Attendance by project
            // ══════════════════════════════════════════
            doc.addPage();
            y = margin;
            doc.setTextColor(...colors.dark);
            doc.setFontSize(16);
            doc.setFont('helvetica', 'bold');
            doc.text('Attendance by Project — Today', margin, y);
            y += 8;

            autoTable(doc, {
                startY: y,
                head: [['Project', 'Team', 'Present', 'Absent', 'Rate']],
                body: (activeProjects || []).map(p => {
                    const c = byProject[String(p.id)] || { present: 0, absent: 0, total: 0 };
                    const rate = c.total > 0 ? Math.round((c.present / c.total) * 100) : null;
                    return [
                        p.short_name || p.name,
                        String(teamCounts[p.id] || 0),
                        String(c.present),
                        String(c.absent),
                        rate !== null ? `${rate}%` : 'No data',
                    ];
                }),
                styles: { fontSize: 8, cellPadding: 3 },
                headStyles: { fillColor: colors.primary, textColor: colors.white, fontStyle: 'bold' },
                alternateRowStyles: { fillColor: colors.lightGray },
                margin: { left: margin, right: margin },
            });

            y = doc.lastAutoTable.finalY + 12;

            if (missingReports.length > 0) {
                doc.setTextColor(...colors.dark);
                doc.setFontSize(13);
                doc.setFont('helvetica', 'bold');
                doc.text('Missing Daily Reports Today', margin, y);
                y += 6;
                autoTable(doc, {
                    startY: y,
                    head: [['Project']],
                    body: missingReports.map(p => [p.short_name || p.name]),
                    styles: { fontSize: 8, cellPadding: 3 },
                    headStyles: { fillColor: colors.warning, textColor: colors.white, fontStyle: 'bold' },
                    alternateRowStyles: { fillColor: colors.lightGray },
                    margin: { left: margin, right: margin },
                });
                y = doc.lastAutoTable.finalY + 12;
            }

            // ══════════════════════════════════════════
            // Pending leave requests
            // ══════════════════════════════════════════
            if ((pendingLeaves || []).length > 0) {
                if (y > 250) { doc.addPage(); y = margin; }
                doc.setTextColor(...colors.dark);
                doc.setFontSize(13);
                doc.setFont('helvetica', 'bold');
                doc.text('Pending Leave Requests', margin, y);
                y += 6;
                autoTable(doc, {
                    startY: y,
                    head: [['Employee', 'Type', 'From', 'To']],
                    body: (pendingLeaves || []).map(lr => [
                        lr.team_members?.name || 'Unknown',
                        lr.leave_type || 'N/A',
                        lr.leave_date ? new Date(lr.leave_date).toLocaleDateString('en-GB') : 'N/A',
                        lr.end_date ? new Date(lr.end_date).toLocaleDateString('en-GB') : 'N/A',
                    ]),
                    styles: { fontSize: 8, cellPadding: 3 },
                    headStyles: { fillColor: colors.secondary, textColor: colors.white, fontStyle: 'bold' },
                    alternateRowStyles: { fillColor: colors.lightGray },
                    margin: { left: margin, right: margin },
                });
            }

            const fileName = `HR_Summary_${new Date().toLocaleDateString('en-GB').replace(/\//g, '-')}.pdf`;
            doc.save(fileName);
            showToast('✅ HR summary report generated!', 'success');
        } catch (err) {
            console.error('HR summary PDF error:', err);
            showToast('❌ Failed to generate HR report. Please try again.');
        }
    };

    // Accounts Portal PDF — نفس بنية generateHRSummaryPDF بالظبط (cover +
    // جداول) بس البيانات هنا مالية: مديونية العملاء، أعمار الفواتير، وحالة
    // الفلو أبس المفتوحة/المتصعّدة.
    const generateAccountsSummaryPDF = async () => {
        const { jsPDF } = window.jspdf || await import('jspdf');
        const autoTable = (await import('jspdf-autotable')).default;

        const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
        const pageW = 210;
        const margin = 15;
        let y = margin;

        const colors = {
            primary: [37, 99, 235],
            secondary: [8, 145, 178],
            success: [16, 185, 129],
            warning: [245, 158, 11],
            danger: [239, 68, 68],
            dark: [15, 23, 42],
            gray: [100, 116, 139],
            lightGray: [241, 245, 249],
            white: [255, 255, 255],
        };

        try {
            const today = new Date();

            const [{ data: projs }, { data: txns }, { data: followUps }] = await Promise.all([
                supabase.from('projects').select('id, name, short_name, client, status'),
                supabase.from('client_account').select('project_id, transaction_date, debit, credit'),
                supabase.from('client_follow_ups').select('id, project_id, subject, status, escalated, contact_name, follow_up_date'),
            ]);

            const projById = {};
            (projs || []).forEach(p => { projById[p.id] = p; });
            const projLabel = (pid) => projById[pid]?.short_name || projById[pid]?.name || pid;

            const byProject = {};
            (txns || []).forEach(t => { (byProject[t.project_id] = byProject[t.project_id] || []).push(t); });

            const rows = Object.entries(byProject).map(([pid, txs]) => {
                const outstanding = txs.reduce((s, t) => s + (t.debit || 0) - (t.credit || 0), 0);
                const { buckets } = ageProjectTransactions(txs, today);
                return { pid, project: projLabel(pid), client: projById[pid]?.client || '—', outstanding, buckets };
            }).filter(r => r.outstanding > 1);

            const totalOutstanding = rows.reduce((s, r) => s + r.outstanding, 0);
            const totalBuckets = rows.reduce((acc, r) => {
                acc.b30 += r.buckets.b30; acc.b60 += r.buckets.b60; acc.b90 += r.buckets.b90; acc.b90p += r.buckets.b90p;
                return acc;
            }, { b30: 0, b60: 0, b90: 0, b90p: 0 });

            const openFollowUps = (followUps || []).filter(f => f.status !== 'resolved');
            const escalatedOpen = openFollowUps.filter(f => f.escalated);

            const sar = (v) => `SAR ${Math.round(v || 0).toLocaleString()}`;

            // ══════════════════════════════════════════
            // COVER
            // ══════════════════════════════════════════
            doc.setFillColor(...colors.dark);
            doc.rect(0, 0, pageW, 297, 'F');
            doc.setFillColor(...colors.secondary);
            doc.rect(0, 0, pageW, 3, 'F');

            doc.setFillColor(8, 145, 178, 0.15);
            doc.roundedRect(margin, 35, 30, 30, 5, 5, 'F');
            doc.setFillColor(...colors.secondary);
            doc.roundedRect(margin + 8, 45, 14, 14, 3, 3, 'F');

            doc.setTextColor(...colors.gray);
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            doc.text('ACCOUNTS SUMMARY REPORT', margin, 85);

            doc.setTextColor(...colors.white);
            doc.setFontSize(26);
            doc.setFont('helvetica', 'bold');
            doc.text('Accounts Summary Report', margin, 100);

            doc.setTextColor(...colors.gray);
            doc.setFontSize(12);
            doc.setFont('helvetica', 'normal');
            doc.text(new Date().toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }), margin, 116);

            doc.setDrawColor(...colors.secondary);
            doc.setLineWidth(0.5);
            doc.line(margin, 124, pageW - margin, 124);

            const coverStats = [
                { label: 'TOTAL OUTSTANDING', value: sar(totalOutstanding) },
                { label: 'PROJECTS WITH BALANCE', value: String(rows.length) },
                { label: 'OPEN FOLLOW-UPS', value: String(openFollowUps.length) },
                { label: 'ESCALATED · OPEN', value: String(escalatedOpen.length) },
            ];
            const statW = (pageW - margin * 2) / coverStats.length;
            coverStats.forEach((s, i) => {
                const x = margin + i * statW;
                doc.setTextColor(...colors.gray);
                doc.setFontSize(8);
                doc.setFont('helvetica', 'normal');
                doc.text(s.label, x, 140);
                doc.setTextColor(...colors.white);
                doc.setFontSize(13);
                doc.setFont('helvetica', 'bold');
                doc.text(s.value, x, 150);
            });

            // ══════════════════════════════════════════
            // PAGE 2 — Receivables ageing + top exposure
            // ══════════════════════════════════════════
            doc.addPage();
            y = margin;
            doc.setTextColor(...colors.dark);
            doc.setFontSize(16);
            doc.setFont('helvetica', 'bold');
            doc.text('Receivables Ageing — Portfolio', margin, y);
            y += 8;

            autoTable(doc, {
                startY: y,
                head: [['0–30 Days', '31–60 Days', '61–90 Days', '90+ Days', 'Total Outstanding']],
                body: [[sar(totalBuckets.b30), sar(totalBuckets.b60), sar(totalBuckets.b90), sar(totalBuckets.b90p), sar(totalOutstanding)]],
                styles: { fontSize: 9, cellPadding: 4, halign: 'center' },
                headStyles: { fillColor: colors.primary, textColor: colors.white, fontStyle: 'bold' },
                didParseCell: (data) => {
                    if (data.section === 'body' && data.column.index === 3) {
                        data.cell.styles.textColor = colors.danger;
                        data.cell.styles.fontStyle = 'bold';
                    }
                },
                margin: { left: margin, right: margin },
            });
            y = doc.lastAutoTable.finalY + 12;

            doc.setTextColor(...colors.dark);
            doc.setFontSize(13);
            doc.setFont('helvetica', 'bold');
            doc.text('Top Clients by Exposure', margin, y);
            y += 6;

            const topRows = [...rows].sort((a, b) => b.outstanding - a.outstanding).slice(0, 15);
            autoTable(doc, {
                startY: y,
                head: [['Project', 'Client', '90+ Days', 'Outstanding']],
                body: topRows.map(r => [r.project, r.client, sar(r.buckets.b90p), sar(r.outstanding)]),
                styles: { fontSize: 8, cellPadding: 3 },
                headStyles: { fillColor: colors.secondary, textColor: colors.white, fontStyle: 'bold' },
                alternateRowStyles: { fillColor: colors.lightGray },
                didParseCell: (data) => {
                    if (data.section === 'body' && data.column.index === 2) {
                        const raw = topRows[data.row.index]?.buckets.b90p || 0;
                        if (raw > 0) { data.cell.styles.textColor = colors.danger; data.cell.styles.fontStyle = 'bold'; }
                    }
                },
                margin: { left: margin, right: margin },
            });

            // ══════════════════════════════════════════
            // PAGE 3 — Action Required (open / escalated follow-ups)
            // ══════════════════════════════════════════
            if (openFollowUps.length > 0) {
                doc.addPage();
                y = margin;
                doc.setTextColor(...colors.dark);
                doc.setFontSize(16);
                doc.setFont('helvetica', 'bold');
                doc.text('Action Required — Open Follow-ups', margin, y);
                y += 8;

                const sortedOpen = [...openFollowUps].sort((a, b) => (b.escalated === a.escalated ? 0 : b.escalated ? 1 : -1));
                autoTable(doc, {
                    startY: y,
                    head: [['Project', 'Contact', 'Subject', 'Status', 'Logged']],
                    body: sortedOpen.map(f => [
                        projLabel(f.project_id),
                        f.contact_name || '—',
                        f.subject || '—',
                        f.escalated ? 'Escalated' : (f.status === 'responded' ? 'Responded' : 'Contacted'),
                        f.follow_up_date ? new Date(f.follow_up_date).toLocaleDateString('en-GB') : '—',
                    ]),
                    styles: { fontSize: 8, cellPadding: 3 },
                    headStyles: { fillColor: colors.warning, textColor: colors.white, fontStyle: 'bold' },
                    alternateRowStyles: { fillColor: colors.lightGray },
                    didParseCell: (data) => {
                        if (data.section === 'body' && data.column.index === 3 && data.cell.raw === 'Escalated') {
                            data.cell.styles.textColor = colors.danger;
                            data.cell.styles.fontStyle = 'bold';
                        }
                    },
                    margin: { left: margin, right: margin },
                });
            }

            const fileName = `Accounts_Summary_${new Date().toLocaleDateString('en-GB').replace(/\//g, '-')}.pdf`;
            doc.save(fileName);
            showToast('✅ Accounts summary report generated!', 'success');
        } catch (err) {
            console.error('Accounts summary PDF error:', err);
            showToast('❌ Failed to generate Accounts report. Please try again.');
        }
    };

    // generateAttendancePDF بقت جوه useTeamController.

    // uploadDocument / filteredDocuments / getFileIcon / formatFileSize /
    // documentStats كلهم بقوا جوه useDocumentsController
    // (src/hooks/useDocumentsController.jsx).
    const filteredExpenses = budgetExpenses.filter(exp => {
        const date = new Date(exp.expense_date);

        const matchesYear = date.getFullYear() === Number(selectedYear);

        const matchesMonth =
            selectedMonth === "all" ||
            date.getMonth() === Number(selectedMonth);

        return matchesYear && matchesMonth;
    });
    const expenseChartData = budgetCategories.map(category => {
        const categoryExpenses = filteredExpenses.filter(
            exp => exp.category_id === category.category_id
        );

        const totalSpent = categoryExpenses.reduce(
            (sum, exp) => sum + Number(exp.amount),
            0
        );

        return {
            name: category.category_name,
            allocated: Number(category.allocated || 0), // ← مهم
            spent: totalSpent
        };
    }).filter(item => item.allocated > 0 || item.spent > 0);





    //==================RENDER: Budget View



    // ========== RENDER: DOCUMENTATION VIEW ==========






    // Forms
    // newComment/commentPriority/submittingComment/submitComment بقوا جوه
    // useCommentsController.
    // multiFiles/multiSys/multiDesc/multiProgress/isBulkUploading بقوا جوه
    // usePhotosController.

    const [invoiceForm, setInvoiceForm] = useState({
        invoiceNumber: "",
        vendor: "",
        amount: "",
        category: "",
        issueDate: "",
        dueDate: "",
        status: "Pending",
        description: ""
    });
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);
    const [submittingInvoice, setSubmittingInvoice] = useState(false);

    // Client Account Forms
    const [showClientTransactionModal, setShowClientTransactionModal] = useState(false);

    // ========== UTILS ==========
    /*   const parseCSV = (text) => {
           const lines = text.split("\n").filter(l => l.trim());
           if (!lines.length) return [];
           const headers = lines[0].split(",").map(h => h.trim());
           return lines.slice(1).map(line => {
               const values = line.split(",");
               return headers.reduce((obj, header, i) => ({ ...obj, [header]: values[i]?.trim() || "" }), {});
           });
       };*/

    // ========== PERMISSIONS SYSTEM ==========
    // PERMISSIONS/hasPermission/canViewSection now live in src/lib/auth/roles.ts
    // (shared with ProtectedDashboard's route guards and useCurrentAgent) so
    // this app and the route-level auth checks can never drift apart.
    // ✅ نقلت البلوك ده (userPerms/hasPermission/canViewSection/isHRPortal/
    // isAccountsPortal/isPortalMode + الـ redirect effect) لتحت بعد تعريف
    // authController مباشرة — لأنه بيعتمد على currentUser/isLoggedIn، واللي
    // بقوا دلوقتي بيتم تعريفهم (destructure) بعد fetchData مش فوق زي الأصل.
    // تسييبه في مكانه القديم كان هيعمل "used before it was defined" فعلي
    // (TDZ) بدل ما يكون بس تحذير linter — الكود كان هيكسر في كل render.

    const canManageAttendance = () => {
        const roles = ['CEO', 'Program Manager', 'Operations Manager'];
        return roles.includes(currentUser?.position);
    };

    const canViewProject = (projectId) => {
        if (!currentUser) return false;
        // ✅ Coordination Team اتشالت من هنا — بقت بورتال Materials-only مقفول،
        // مش full-access على كل المشاريع زي قبل.
        const fullAccess = ['CEO', 'Program Manager', 'Operations Manager'];
        if (fullAccess.includes(currentUser.position)) return true;
        const assigned = currentUser.assigned_projects || [];
        // لو فاضية — مش يشوف أي مشروع
        if (assigned.length === 0) return false;
        return assigned.includes(projectId);
    };



    const formatCurrency = (amount) => {
        return `SAR ${Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    // ========== SESSION ==========
    const SESSION_DURATION = 24 * 60 * 60 * 1000; // 24 ساعة

    const startSession = () => {
        clearTimeout(sessionTimer);
        localStorage.setItem('lastActivity', Date.now());
        sessionTimer = setTimeout(() => {
            handleLogout();
            showToast("Session expired due to inactivity");
        }, SESSION_DURATION);
    };

    // في useEffect تحقق لو الـ session منتهية أو لا
    useEffect(() => {
        const lastActivity = localStorage.getItem('lastActivity');
        if (lastActivity && Date.now() - parseInt(lastActivity) > SESSION_DURATION) {
            handleLogout(); // انتهت الـ session
        } else {
            startSession(); // لسه صالحة، جدد الـ timer
        }

        const reset = () => startSession();
        window.addEventListener("mousemove", reset);
        window.addEventListener("keydown", reset);
        return () => {
            window.removeEventListener("mousemove", reset);
            window.removeEventListener("keydown", reset);
            clearTimeout(sessionTimer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- startSession دالة بتتعاد إنشائها كل render (مش memoized)؛ إضافتها كـ dependency هتخلي الـ effect يشتغل كل render ويعيد تسجيل الـ event listeners من غير داعي. الـ effect ده مقصود يشتغل مرة واحدة بس عند mount
    }, []);



    // ✅ ثالث محاولة استعادة سيشن ميتة في الملف (بعد اللي اتشالت فوق جوه
    // useAuthController): كانت بتقرا localStorage.getItem("currentUser") —
    // بفحص شامل (grep) للمشروع كله، محدش بيكتب على المفتاح ده (setItem) في
    // أي مكان، يعني الـ effect ده ما كان بيعمل حاجة أبدًا. استعادة السيشن
    // الحقيقية بتتم من checkUser() (جوه useAuthController) باستخدام
    // supabase.auth.getSession().

    // ========== DATA FETCHING ==========



    // ✅ اتنقل منطق الجلب لـ src/services/projectDataService.js: كل الاستعلامات
    // بقت بالتوازي (Promise.allSettled) بدل ما تكون متتالية (await واحد ورا التاني) —
    // ده كان أكبر سبب لبطء التحميل، خصوصًا أول مرة على متصفح/جهاز جديد. كمان
    // فشل جدول واحد بقى بيتسجل ويظهر توست حقيقي بدل ما يوقف تحميل باقي الأقسام.

    // ✅ تحسين تاني (خاص برمشة المودلز): fetchData كانت بتنادي الـ 25 setter
    // دول *كل مرة* تتنفذ، سواء البيانات الجديدة فعليًا مختلفة عن القديمة
    // أو لا — وده بيحصل كل 30 ثانية (auto-refresh) وكمان مع أي Realtime
    // تعديل في أي جدول من الـ 24 جدول المرتبطة بالمشروع (حتى لو التعديل
    // من مستخدم تاني في قسم تاني خالص). كل setState بقيمة جديدة (reference
    // مختلف، حتى لو المحتوى زي ما هو لأنها جاية من fetch جديد) بيخلي React
    // يعيد رندر الـ App.jsx كله (~4900 سطر، 90+ hook) من جديد، وده اللي كان
    // بيتعارض مع حركة فتح UserMenu/PhotoModal (دول state بتاعهم منعزل خلاص
    // من إصلاح سابق، بس إعادة رندر App نفسها من حاجة تانية خالص كانت لسة
    // بتبيّن كرمشة بصرية لو حصلت في نفس اللحظة اللي المودال بيتحرك فيها).
    //
    // الحل: نقارن القيمة الجديدة بالقديمة قبل ما ننادي كل setter، باستخدام
    // الـ functional form من useState (prev => ...). لو البيانات زي ما هي
    // (JSON.stringify متطابق)، نرجّع نفس الـ prev reference بالظبط — وده
    // بالذات اللي بيخلي React يعمل "bail out" (يقارن بالـ Object.is، يلاقي
    // نفس الـ reference، فميعيدش رندر أي مكون بيعتمد على الـ state ده خالص).
    // لو البيانات فعلاً اتغيرت، بترجع القيمة الجديدة عادي وبيحصل رندر طبيعي
    // زي ما كان قبل التعديل ده. مفيش أي تأخير في ظهور بيانات جديدة حقيقية —
    // المقارنة نفسها بسيطة وأسرع بكتير من إعادة رسم الشجرة كلها.
    const sameData = (a, b) => {
        if (a === b) return true;
        try {
            return JSON.stringify(a) === JSON.stringify(b);
        } catch {
            return false;
        }
    };

    const fetchData = async (projectId = currentProjectId, forceRun = false) => {
        if ((!isLoggedIn && !forceRun) || !projectId) return;

        try {
            const { data, errors } = await fetchProjectData(projectId);

            setClientComments(prev => sameData(prev, data.clientComments) ? prev : data.clientComments);
            setTasksData(prev => sameData(prev, data.tasks) ? prev : data.tasks);
            setAttendanceData(prev => sameData(prev, data.attendance) ? prev : data.attendance);
            setDailyActivities(prev => sameData(prev, data.dailyActivities) ? prev : data.dailyActivities);
            setPhases(prev => sameData(prev, data.phases) ? prev : data.phases);
            setDependencies(prev => sameData(prev, data.dependencies) ? prev : data.dependencies);
            setMilestones(prev => sameData(prev, data.milestones) ? prev : data.milestones);
            setBaselineRevisions(prev => sameData(prev, data.baselineRevisions) ? prev : data.baselineRevisions);
            setLeaveRequests(prev => sameData(prev, data.leaveRequests) ? prev : data.leaveRequests);
            setTaskChallenges(prev => sameData(prev, data.taskChallenges) ? prev : data.taskChallenges);
            setTeamData(prev => sameData(prev, data.teamData) ? prev : data.teamData);
            setBudgetData(prev => sameData(prev, data.budgetData) ? prev : data.budgetData);
            setMaterialsData(prev => sameData(prev, data.materialsData) ? prev : data.materialsData);
            setPerformanceData(prev => sameData(prev, data.performanceData) ? prev : data.performanceData);
            setPhotosData(prev => sameData(prev, data.photosData) ? prev : data.photosData);
            setCommentsData(prev => sameData(prev, data.commentsData) ? prev : data.commentsData);
            setCommentReplies(prev => sameData(prev, data.commentReplies) ? prev : data.commentReplies);
            setInvoicesData(prev => sameData(prev, data.invoicesData) ? prev : data.invoicesData);
            setClientAccountData(prev => sameData(prev, data.clientAccountData) ? prev : data.clientAccountData);
            setDocumentsData(prev => sameData(prev, data.documentsData) ? prev : data.documentsData);
            setBudgetCategories(prev => sameData(prev, data.budgetCategories) ? prev : data.budgetCategories);
            setBudgetExpenses(prev => sameData(prev, data.budgetExpenses) ? prev : data.budgetExpenses);
            setBudgetCommitments(prev => sameData(prev, data.budgetCommitments) ? prev : data.budgetCommitments);
            setRfis(prev => sameData(prev, data.rfis) ? prev : data.rfis);
            setContacts(prev => sameData(prev, data.contacts) ? prev : data.contacts);
            setHseIncidents(prev => sameData(prev, data.hseIncidents) ? prev : data.hseIncidents);

            if (errors.length > 0) {
                console.error('⚠️ Some tables failed to load:', errors);
                showToast(`⚠️ Failed to load: ${errors.map(e => e.table).join(', ')}`);
            }
        } catch (error) {
            console.error('❌ Error fetching data for project', projectId, ':', error);
            showToast('Failed to load data. Please check console for details.');
        }
    };

    // ✅ كل state و handlers صفحة Login/Auth (otpCode، step، isLoggedIn،
    // currentUser، checkUser، handleLogin، handleVerifyOTP، handleLogout...
    // إلخ) بقوا جوه useAuthController. لازم يُستدعى بعد تعريف fetchData (فوق)
    // وقبل كل الـ controllers التانية (تحت) لأن كتير منهم بياخدوا currentUser
    // كـ parameter.
    const authController = useAuthController({
        currentProjectId, setCurrentProjectId, fetchData, loadProjectsFromDatabase,
        setClientComments, setTasksData, setAttendanceData, setDailyActivities,
        setPhases, setDependencies, setMilestones, setBaselineRevisions,
        setLeaveRequests, setTaskChallenges, setTeamData, setBudgetData,
        setMaterialsData, setPerformanceData, setPhotosData, setCommentsData,
        setCommentReplies, setInvoicesData, setClientAccountData, setDocumentsData,
        setBudgetCategories, setBudgetExpenses, setRfis, setContacts,
        setHseIncidents, setAllTeamMembers,
    });
    const {
        // setOtpTimer/setCanResend/setTempEmail/setLoggingIn/
        // setAuthLoading/resendCount/setResendCount مش محتاجين هنا — بيُستخدموا
        // داخليًا جوه useAuthController نفسها بس (مفيش حد في App.jsx بيناديهم
        // مباشرة). setLoginError دلوقتي مطلوبة هنا عشان نقدر نعرض رسالة لو
        // تحقق Turnstile فشل (قبل حتى ما نوصل لـ handleLogin نفسها).
        otpCode, setOtpCode, otpTimer, canResend,
        step, setStep, otpError, setOtpError, tempEmail,
        isLoggedIn, currentUser,
        loginForm, setLoginForm, loginError, setLoginError,
        loggingIn, authLoading,
        showWelcome, setShowWelcome,
        handleLogin, handleOtpChange, handleOtpKeyDown, handleOtpPaste,
        handleVerifyOTP, handleLogout,
    } = authController;

    // ══════════════════════════════════════════════
    // Cloudflare Turnstile — تحقّق إن اللي بيحاول يسجّل دخول إنسان حقيقي
    // مش بوت. الـ widget بيدي توكن مرة واحدة بس (single-use)، فبنتحقق منه
    // على السيرفر (verify-turnstile Edge Function) قبل ما ننادي handleLogin
    // الفعلية. لو فشل التحقق، نعرض رسالة ونصفّر الـ widget عشان يولّد توكن
    // جديد — من غير الخطوة دي، أي حد يقدر يتجاوز الـ widget بالكامل بنداء
    // Supabase Auth API مباشرة من غير ما يمر عليه خالص.
    // ══════════════════════════════════════════════
    const turnstileRef = useRef(null);
    const [turnstileToken, setTurnstileToken] = useState('');
    const [verifyingTurnstile, setVerifyingTurnstile] = useState(false);

    const handleLoginWithTurnstile = async () => {
        if (!turnstileToken) {
            setLoginError('Please complete the verification challenge.');
            return;
        }
        setVerifyingTurnstile(true);
        try {
            const { data, error } = await supabase.functions.invoke('verify-turnstile', {
                body: { token: turnstileToken },
            });
            turnstileRef.current?.reset();
            setTurnstileToken('');
            if (error || !data?.success) {
                setLoginError('Verification failed. Please try again.');
                return;
            }
        } catch (err) {
            console.error('Turnstile verify call error:', err);
            turnstileRef.current?.reset();
            setTurnstileToken('');
            setLoginError('Verification failed. Please try again.');
            return;
        } finally {
            setVerifyingTurnstile(false);
        }
        await handleLogin();
    };

    // ========== PERMISSIONS SYSTEM ==========
    // PERMISSIONS/hasPermission/canViewSection now live in src/lib/auth/roles.ts
    // (shared with ProtectedDashboard's route guards and useCurrentAgent) so
    // this app and the route-level auth checks can never drift apart.
    const userPerms = PERMISSIONS[currentUser?.position] || PERMISSIONS["Viewer"];

    const hasPermission = (action) => hasPermissionForPosition(currentUser?.position, action);

    const canViewSection = (section) => canViewSectionForPosition(currentUser?.position, section);

    // HR عندها صلاحية على قسم واحد بس ("HR") مش مربوط بمشروع معيّن —
    // فبدل ما تشوف نسخة مبتورة من داشبورد المشاريع (شريط تابات فيه تاب واحد،
    // بيانات عقد/قيمة مالية لمشروع مالوش دعوة بيه)، بتاخد بورتال مخصص ليها:
    // هيدر مبسّط + هي بتهبط على قسم HR (اللي فيه Overview خاص بيها) مباشرة.
    const isHRPortal = Array.isArray(userPerms.sections) && userPerms.sections.length === 1 && userPerms.sections[0] === 'HR';

    // Accounts عندها نفس فكرة بورتال HR بالظبط — صلاحية على قسم واحد بس
    // ("Accounts") مش مربوط بمشروع معيّن، فبتاخد بورتال مخصص ليها كمان.
    const isAccountsPortal = Array.isArray(userPerms.sections) && userPerms.sections.length === 1 && userPerms.sections[0] === 'Accounts';

    // Coordination Team نفس الفكرة — كانت full access (sections:'all')، دلوقتي
    // مقفولة على قسم "Coordination" بس (شوف roles.ts)، فبتاخد بورتال مخصص
    // زي HR/Accounts بالظبط.
    const isCoordinationPortal = Array.isArray(userPerms.sections) && userPerms.sections.length === 1 && userPerms.sections[0] === 'Coordination';

    // أي بورتال "قسم واحد" (HR أو Accounts أو Coordination) بيستخدم نفس
    // الهيدر المبسّط ونفس إخفاء شريط تابات المشروع — فبنجمعهم في فلاج واحد
    // بدل ما نكرر الشرط.
    const isPortalMode = isHRPortal || isAccountsPortal || isCoordinationPortal;

    useEffect(() => {
        if (isLoggedIn) {
            if (isHRPortal && view !== 'HR') {
                setSearchParams({ view: 'HR' }, { replace: true });
            } else if (isAccountsPortal && view !== 'Accounts') {
                setSearchParams({ view: 'Accounts' }, { replace: true });
            } else if (isCoordinationPortal && view !== 'Coordination') {
                setSearchParams({ view: 'Coordination' }, { replace: true });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- بس عايزين ده يحصل مرة لما يدخل، مش كل مرة view يتغيّر
    }, [isHRPortal, isAccountsPortal, isCoordinationPortal, isLoggedIn]);

    // ✅ كل state و handlers صفحة Tasks (الفلاتر/الترتيب/المودالات/التحديات)
    // بقوا هنا، مُجمّعين جوه hook واحد بدل ما يكونوا متفرّقين على أكتر من
    // 2000 سطر مخلوطين مع state صفحات تانية. لازم يُستدعى بعد تعريف fetchData
    // (فوق) لأنه بياخده كـ parameter.
    const tasksController = useTasksController({
        currentProjectId, setTasksData, setTaskChallenges, fetchData, showToast, showConfirm,
    });
    const {
        searchTerm, setSearchTerm, filterStatus, setFilterStatus, filterOwner, setFilterOwner, filterSystem, setFilterSystem,
        sortBy, setSortBy, sortOrder, setSortOrder,
        tasksView, setTasksView, tasksPage, setTasksPage,
        selectedTasks, setSelectedTasks, handleToggleTaskSelect, handleBulkDelete,
        showAddTask, setShowAddTask, taskForm, setTaskForm, editingTask, setEditingTask, savingTask, handleSaveTask,
        showTaskDetails, setShowTaskDetails, handleOpenTaskDetails, handleEditTask, handleDeleteTask,
        handleUpdateTaskValue,
        taskDetailTab, setTaskDetailTab,
        challengeForm, setChallengeForm, uploadingChallenge, setUploadingChallenge, loadChallenges,
        getSortedTasks,
    } = tasksController;

    // نفس فكرة useTasksController، لصفحة Materials (الفورم/المودال/استيراد Excel).
    const materialsController = useMaterialsController({
        currentProjectId, fetchData, showToast, showConfirm,
    });
    const {
        selectedSystem, setSelectedSystem, selectedStatus, setSelectedStatus,
        showMaterialModal, setShowMaterialModal, editingMaterial, setEditingMaterial,
        materialForm, setMaterialForm, savingMaterial,
        handleSaveMaterial, handleEditMaterial, handleDeleteMaterial,
        showImportModal, setShowImportModal, importData, setImportData,
        importLoading, importError, setImportError, importSuccess,
        handleImportExcel, handleSubmitImport, handleDownloadTemplate,
    } = materialsController;
    // searchTerm بتاع Materials كان اسمه materialsSearchTerm في App.jsx القديم
    // (عشان ميتلخبطش مع searchTerm بتاع Tasks)؛ دلوقتي كل واحد منهم جوه الـ
    // hook بتاعه، فالاسمين أصبحوا مستقلين فعليًا — بس نفس أسماء الـ props اللي
    // MaterialsView متوقعاها (searchTerm/setSearchTerm) ما تغيّرتش.
    const materialsSearchTerm = materialsController.searchTerm;
    const setMaterialsSearchTerm = materialsController.setSearchTerm;

    // نفس فكرة الاتنين فوق، لصفحة Documents.
    const documentsController = useDocumentsController({
        currentProjectId, currentUser, documentsData, fetchData, showToast,
    });
    const {
        showUploadModal, setShowUploadModal, documentFilter, setDocumentFilter,
        documentSearchTerm, setDocumentSearchTerm, uploadForm, setUploadForm, uploadingDocument,
        deleteDocument, uploadDocument, filteredDocuments, getFileIcon, formatFileSize, documentStats,
    } = documentsController;

    // نفس فكرة اللي فوق، لصفحة Comments (فورم الكومنت الجديد + فلتر الأولوية).
    // commentReplies/setCommentReplies اتسابوا في App.jsx (مركزيين من fetchData).
    const commentsController = useCommentsController({
        currentProjectId, currentUser, showToast, fetchData,
    });
    const {
        commentFilter, setCommentFilter, editingComment, setEditingComment,
        replyInputs, setReplyInputs,
        newComment, setNewComment, commentPriority, setCommentPriority,
        submittingComment, setSubmittingComment, submitComment,
    } = commentsController;

    // نفس فكرة اللي فوق، لصفحة Photos (الفلاتر/الترتيب/الصفحات + الرفع الجماعي).
    // photosData/setPhotosData و modalPhoto/setModalPhoto و compressImage
    // اتسابوا في App.jsx (الأول مركزي من fetchData، التاني مُستخدم في مودال
    // الـ lightbox بتاع App.jsx، التالت مُستخدم كمان من Daily Activities).
    const photosController = usePhotosController({
        currentProjectId, showToast, fetchData, setPhotosData,
    });
    const {
        photoSearch, setPhotoSearch, photoSort, setPhotoSort, photoView, setPhotoView,
        selectedSystemFilter, setSelectedSystemFilter, photosPage, setPhotosPage,
        multiFiles, setMultiFiles, multiSys, setMultiSys, multiDesc, setMultiDesc,
        multiProgress, setMultiProgress, isBulkUploading, setIsBulkUploading,
        refreshPhotos, deletePhoto,
    } = photosController;

    // نفس فكرة اللي فوق، لصفحة Project Schedule (أعقد صفحة لحد دلوقتي —
    // متشابكة مع phases/dependencies/baselineRevisions اللي اتسابوا هنا).
    const scheduleController = useScheduleController({
        currentProjectId, currentUser, showToast, showConfirm, fetchData,
        phases, tasksData, dependencies, setDependencies, baselineRevisions, setBaselineRevisions,
    });
    const {
        scheduleView, setScheduleView, ganttKey, ganttScheduleRef,
        selectedPhase, setSelectedPhase,
        showAddPhaseModal, setShowAddPhaseModal, editingPhase, setEditingPhase, phaseForm, setPhaseForm, addPhase, updatePhase, deletePhase,
        showAddMilestoneModal, setShowAddMilestoneModal, editingMilestone, setEditingMilestone, milestoneForm, setMilestoneForm, addMilestone, updateMilestone, deleteMilestone,
        showAddDependency, setShowAddDependency, depFromPhase, setDepFromPhase, depType, setDepType, addDependency, deleteDependency,
        showCreateRevisionModal, setShowCreateRevisionModal, revisionForm, setRevisionForm, createBaselineRevision,
        phasesFromTasks,
    } = scheduleController;

    // نفس فكرة اللي فوق، لصفحة Team (فيها كمان Attendance/Leave/Performance/
    // Achievements مدمجين في نفس TeamView.jsx). teamData/allTeamMembers/
    // attendanceData/leaveRequests/performanceData اتسابوا في App.jsx (مركزيين
    // من fetchData أو من effect تسجيل الدخول).
    const teamController = useTeamController({
        currentProjectId, currentUser, PROJECT, showToast, showConfirm, fetchData, fetchAllTeamMembers,
        teamData, attendanceData,
    });
    const {
        editingMember, setEditingMember, showEditMemberModal, setShowEditMemberModal, openEditMemberModal, updateTeamMember,
        showAddTeamMemberModal, setShowAddTeamMemberModal, newMemberForm, setNewMemberForm, addNewTeamMember, uploadingTeamMember,
        showAssignExistingMemberModal, setShowAssignExistingMemberModal, memberSearchTerm, setMemberSearchTerm,
        selectedMemberToAssign, setSelectedMemberToAssign, assignmentSystems, setAssignmentSystems, assigningMember, assignExistingMember,
        removeTeamMember,
        allAchievements, memberAchievements, newAchievement, setNewAchievement, addMemberAchievement, removeMemberAchievement,
        showQRModal, setShowQRModal, qrUrl, generateAttendanceQR,
        showAttendanceModal, setShowAttendanceModal, attendanceDate, attendanceRecords, setAttendanceRecords,
        openAttendanceModal, saveAttendance,
        showAttendanceSummary, setShowAttendanceSummary, attendanceStats, attendanceSummary, totalHours,
        showExportModal, setShowExportModal, exportRange, setExportRange, exportAttendanceRange, generateAttendancePDF,
        showLeaveRequests, setShowLeaveRequests, processingLeaveId, setProcessingLeaveId,
        showPerformanceModal, setShowPerformanceModal, editingPerformance, setEditingPerformance, savingPerformance, setSavingPerformance,
        teamSearchQuery, setTeamSearchQuery, teamPage, setTeamPage,
    } = teamController;

    // Keep the same useEffects


    // ✅ كان الاعتمادية ناقصة currentProjectId — يعني بعد تبديل مشروع والـ
    // auto-refresh شغّال، الـ interval كان بيفضل يستخدم الـ closure القديمة
    // (بتاعة المشروع اللي كان متفتوح وقت ما الـ effect اتشغل أول مرة) بدل
    // المشروع الحالي. إضافة currentProjectId بتخلي الـ interval يتصفّر ويتعمل
    // من جديد بأحدث قيمة كل مرة المشروع يتغيّر.
    useEffect(() => {
        if (autoRefresh && isLoggedIn) {
            const int = setInterval(() => fetchData(), 30000);
            return () => clearInterval(int);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchData مش memoized؛ الـ effect ده مقصود يعيد تشغيل الـ interval بس لما autoRefresh/isLoggedIn/currentProjectId يتغيّروا
    }, [autoRefresh, isLoggedIn, currentProjectId]);

    useEffect(() => {
        if (!isLoggedIn) return;
        if (isHRPortal) {
            fetchHRNotifications();
        } else if (isAccountsPortal) {
            fetchAccountsNotifications();
        } else if (isCoordinationPortal) {
            // لسه مفيش notifications مخصصة لبورتال Coordination (زي
            // leave requests لـ HR أو aging لـ Accounts) — مفيش حاجة تُجاب دلوقتي.
            setNotifications([]);
        } else if (currentProjectId) {
            fetchNotifications();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchNotifications/fetchHRNotifications/fetchAccountsNotifications مش memoized؛ الـ effect ده مقصود يتنفّذ بس لما isLoggedIn/isHRPortal/isAccountsPortal/isCoordinationPortal/currentProjectId يتغيّروا
    }, [isLoggedIn, isHRPortal, isAccountsPortal, isCoordinationPortal, currentProjectId]);



    // ========== MODIFIED USEEFFECTS ==========





    // الـ Enter-key listener (كان هنا) بقى جوه useAuthController.

    useEffect(() => {
        if (isLoggedIn && currentProjectId) {

            // ✅ Reset all data أولاً
            setTasksData([]);
            setTeamData([]);
            setBudgetData([]);
            setBudgetCategories([]);
            setBudgetExpenses([]);
            setMaterialsData([]);
            setPerformanceData([]);
            setPhotosData([]);
            setCommentsData([]);
            setInvoicesData([]);
            setClientAccountData([]);
            setDocumentsData([]);
            setAllTeamMembers([]);

            // ✅ ثم fetch البيانات الجديدة — وبعد ما تخلص، نطفي مؤشر "Switching..."
            // (كان switchingProject متعرّف بس متستخدمش أبدًا قبل كده، فالمؤشر
            // والـ overlay كانوا كود ميت فعليًا).
            Promise.all([fetchData(), fetchAllTeamMembers()])
                .finally(() => setSwitchingProject(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchData/fetchAllTeamMembers مش memoized؛ الـ effect ده مقصود يشتغل بس لما isLoggedIn/currentProjectId يتغيّروا (ده أصلًا الـ mechanism اللي بيخلي تبديل المشروع سريع)
    }, [isLoggedIn, currentProjectId]);
    // في App.jsx - أضف بعد fetchData
    useEffect(() => {
        if (!isLoggedIn || !currentProjectId) return;

        // ✅ تحسين حقيقي هنا (بعد الرجوع لـ schema قاعدة البيانات على Supabase):
        // فحصت الجداول كلها (85 جدول في public schema) ولقيت إن fetchData() فعلياً
        // بتجيب بيانات من 25 جدول بس (tasks, budget, materials, photos... إلخ).
        // الاشتراك القديم كان بيسمع لأي تغيير في الـ 85 جدول كلهم (تذاكر ITSM،
        // تحليلات الـ showcase، إلخ) ويعمل فحص على كل واحد فيهم — ده حمل زيادة
        // تماماً من غير أي فايدة لأن fetchData مش بتستخدم غير 25 منهم بس.
        //
        // الحل: بدل الاستماع لكل الـ schema، بنعمل .on() منفصل لكل جدول من الـ 25
        // دول بس، ومع filter بتاع project_id=eq.currentProjectId يتفلتر من فوق
        // على مستوى السيرفر نفسه (Supabase) قبل ما يوصل للمتصفح أصلاً — يعني
        // تغييرات مشاريع تانية ولا حتى بتوصل للعميل.
        const debounceTimer = { current: null };
        const debouncedFetch = () => {
            clearTimeout(debounceTimer.current);
            debounceTimer.current = setTimeout(() => fetchData(), 500);
        };

        // الجداول اللي عندها عمود project_id وفعلياً بتتقرأ في fetchData()
        const PROJECT_SCOPED_TABLES = [
            'tasks', 'budget', 'materials', 'photos', 'comments', 'comment_replies',
            'invoices', 'client_account', 'client_comments', 'documents',
            'budget_categories', 'budget_expenses', 'daily_activities', 'performance',
            'project_team_assignments', 'project_phases', 'project_milestones',
            'phase_dependencies', 'baseline_revisions', 'task_challenges',
            'hse_incidents', 'rfi_tracker', 'communication_matrix',
            'attendance', 'leave_requests',
        ];

        let channel = supabase.channel('db-changes');
        PROJECT_SCOPED_TABLES.forEach((table) => {
            channel = channel.on('postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table,
                    filter: `project_id=eq.${currentProjectId}`,
                },
                () => debouncedFetch()
            );
        });
        channel.subscribe();

        return () => {
            clearTimeout(debounceTimer.current);
            channel.unsubscribe();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchData مش memoized وبيتنادى جوه debouncedFetch؛ الاشتراك في الـ realtime channel مقصود يتعمل من جديد بس لما isLoggedIn/currentProjectId يتغيّروا
    }, [isLoggedIn, currentProjectId]);

    // OTP countdown effect، handleLogin، OTP input handlers (handleOtpChange/
    // handleOtpKeyDown/handleOtpPaste)، handleVerifyOTP، و handleLogout —
    // كلهم بقوا جوه useAuthController.

    // ========== ACTIONS ==========
    // submitComment بقت جوه useCommentsController.
    // refreshPhotos/deletePhoto بقوا جوه usePhotosController.

    const addInvoice = async () => {
        // Validate all required fields
        if (!invoiceForm.invoiceNumber || !invoiceForm.vendor || !invoiceForm.amount ||
            !invoiceForm.category || !invoiceForm.issueDate || !invoiceForm.dueDate) {
            showToast("Please fill all required fields");
            return;
        }

        setSubmittingInvoice(true);

        try {
            const { error } = await supabase
                .from('invoices')
                .insert([
                    {
                        project_id: currentProjectId,
                        invoice_number: invoiceForm.invoiceNumber,
                        vendor: invoiceForm.vendor,
                        amount: parseFloat(invoiceForm.amount),
                        category: invoiceForm.category,
                        status: invoiceForm.status || 'Pending',
                        due_date: invoiceForm.dueDate,
                        payment_date: null,
                        notes: invoiceForm.description || '',
                        created_at: new Date().toISOString()
                    }
                ])
                .select();

            if (error) throw error;

            showToast("Invoice added successfully!");
            setShowInvoiceModal(false);
            setInvoiceForm({
                invoiceNumber: "",
                vendor: "",
                amount: "",
                category: "",
                issueDate: "",
                dueDate: "",
                status: "Pending",
                description: ""
            });

            // Refresh invoices
            fetchData();

        } catch (err) {
            console.error("Invoice submission error:", err);
            showToast("❌ Failed to add invoice. Please try again.");
        } finally {
            setSubmittingInvoice(false);
        }
    };

    const handleSaveClient = async () => {
        const data = {
            project_id: currentProjectId,
            transaction_date: clientForm.transaction_date,
            description: clientForm.description,
            debit: parseFloat(clientForm.debit) || 0,
            credit: parseFloat(clientForm.credit) || 0,
            reference: clientForm.reference,
        };

        if (editingClient) {
            await supabase.from('client_account').update(data).eq('id', editingClient.id);
            showToast('✅ Transaction updated!');
        } else {
            await supabase.from('client_account').insert([data]);
            showToast('✅ Transaction added!');
        }

        setShowClientTransactionModal(false);
        setEditingClient(null);
        await fetchData(currentProjectId);
    };

    const handleDeleteClient = async (id) => {
        showConfirm('Are you sure you want to delete this transaction?', async () => {
            await supabase.from('client_account').delete().eq('id', id);
            showToast('✅ Deleted!');
            await fetchData(currentProjectId);
        })
    };
    // ========== STATS ==========
    const stats = {
        totalTasks: tasksData.length,
        completed: tasksData.filter(t => t.status === "Completed").length,
        inProgress: tasksData.filter(t => t.status === "In Progress").length,
        pending: tasksData.filter(t => t.status === "Pending" || t.status === "Not Started").length,
        avgProgress: tasksData.length ? Math.round((tasksData.filter(t => t.status === "Completed").length / tasksData.length) * 100) : 0,
        totalBudget: budgetCategories.reduce((sum, cat) => sum + cat.allocated, 0),
        totalSpent: budgetCategories.reduce((sum, cat) => sum + getCategorySpent(cat.category_id), 0),
        // الحل الآمن:
        daysLeft: PROJECT?.endDate ? Math.ceil((PROJECT.endDate - new Date()) / 86400000) : 0,
        invoicesTotal: invoicesData.reduce((s, i) => s + (Number(i.amount) || 0), 0),
        invoicesPaid: invoicesData.filter(i => i.status === "Paid").length,
        invoicesPending: invoicesData.filter(i => i.status === "Pending").length,
        changeOrdersTotal: changeOrdersData.reduce((s, c) => s + (Number(c.amount) || 0), 0),

        totalInvoiced: clientAccountData.reduce((sum, t) => sum + (Number(t.debit) || 0), 0),
        totalReceived: clientAccountData.reduce((sum, t) => sum + (Number(t.credit) || 0), 0),
        clientBalance: clientAccountData.reduce((sum, t) => {
            const debit = Number(t.debit) || 0;
            const credit = Number(t.credit) || 0;
            return sum + debit - credit;
        }, 0),


    };




    // ✅ theme اتنقل برّا الكمبوننت (module-level constant فوق) — انظر الشرح هناك.

    if (showSplash) {
        return <SplashScreen onFinish={() => {
            sessionStorage.setItem('splashShown', 'true')
            setShowSplash(false)
        }} />;
    }
    // ========== ENHANCED LOGIN PAGE ==========
    if (authLoading) {
        return (
            <div className="jr-app" data-theme="light" style={{
                minHeight: '100vh', background: 'var(--jr-bg)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: 'var(--jr-font-sans)',
            }}>
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, ease: 'easeOut' }}
                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', maxWidth: 320 }}
                >
                    {/* Logo */}
                    <div style={{
                        width: 64, height: 64, borderRadius: 'var(--jr-radius-lg)',
                        background: 'var(--jr-surface)', border: '1px solid var(--jr-border)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        padding: 8, marginBottom: 22, boxShadow: 'var(--jr-shadow-md)',
                    }}>
                        <img
                            src="/avatars/logo.jpeg"
                            alt="RITIS"
                            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            onError={e => { e.target.style.display = 'none'; e.target.parentNode.style.background = 'var(--jr-accent)'; e.target.parentNode.innerHTML = '<span style="color:var(--jr-accent-fg);font-weight:800;font-size:22px">R</span>'; }}
                        />
                    </div>

                    {/* Company name */}
                    <div style={{ textAlign: 'center', marginBottom: 28 }}>
                        <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--jr-text)', letterSpacing: '-0.01em', marginBottom: 4 }}>
                            Al Rammah International
                        </div>
                        <div className="jr-eyebrow">IT &amp; Telecom Services Co. — RITIS</div>
                    </div>

                    {/* Spinner */}
                    <div style={{ position: 'relative', width: 40, height: 40, marginBottom: 26 }}>
                        <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '3px solid var(--jr-border)' }} />
                        <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 0.9, repeat: Infinity, ease: 'linear' }}
                            style={{
                                position: 'absolute', inset: 0, borderRadius: '50%',
                                border: '3px solid transparent',
                                borderTopColor: 'var(--jr-accent)',
                            }}
                        />
                    </div>

                    {/* Loading text */}
                    <div style={{ textAlign: 'center', marginBottom: 22 }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--jr-text)', marginBottom: 4 }}>
                            Loading Dashboard
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--jr-text-2)', fontWeight: 500 }}>
                            Preparing your workspace...
                        </div>
                    </div>

                    {/* Progress bar */}
                    <div style={{ width: 220, marginBottom: 22 }}>
                        <div style={{ marginBottom: 7 }}>
                            <span className="jr-eyebrow">Fetching project data</span>
                        </div>
                        <div className="jr-prog">
                            <motion.div
                                initial={{ width: '15%' }}
                                animate={{ width: ['15%', '85%', '15%'] }}
                                transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
                                className="jr-prog-fill"
                                style={{ background: 'var(--jr-accent)' }}
                            />
                        </div>
                    </div>

                    {/* Step indicators */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {[
                            { label: 'Auth', done: true },
                            { label: 'Projects', done: true },
                            { label: 'Data', active: true },
                            { label: 'Dashboard', pending: true },
                        ].map((s, i) => (
                            <React.Fragment key={i}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                    <div style={{
                                        width: 6, height: 6, borderRadius: '50%',
                                        background: s.done ? 'var(--jr-success)' : s.active ? 'var(--jr-accent)' : 'var(--jr-border-strong)',
                                    }} />
                                    <span style={{
                                        fontSize: 9, fontWeight: 700,
                                        color: s.done ? 'var(--jr-success)' : s.active ? 'var(--jr-accent)' : 'var(--jr-text-3)',
                                    }}>{s.label}</span>
                                </div>
                                {i < 3 && <span style={{ color: 'var(--jr-border-strong)', fontSize: 10 }}>—</span>}
                            </React.Fragment>
                        ))}
                    </div>

                    {/* Bottom tagline */}
                    <div style={{ marginTop: 36, textAlign: 'center' }}>
                        <div className="jr-eyebrow">Excellence in ICT &amp; ELV Services • Est. 1999</div>
                    </div>
                </motion.div>
            </div>
        );
    }


    if (!isLoggedIn) {
        return (
            // ✅ كانت data-theme مثبّتة على "light" دايمًا — زرار الكاميرا تحت
            // بيبدّل darkMode فعليًا دلوقتي، فلازم الشاشة دي تتبعه زي باقي التطبيق.
            <div className="jr-app" data-theme={darkMode ? 'dark' : 'light'} style={{ minHeight: '100vh', background: 'var(--jr-bg)', fontFamily: 'var(--jr-font-sans)' }}>

                {/* ═══════════════════════════════════════
                    OTP MODAL
                ═══════════════════════════════════════ */}
                {step === 'otp' && (
                    <div style={{
                        position: 'fixed', inset: 0, zIndex: 9999,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
                        background: 'rgba(12,10,9,0.5)',
                    }}>
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 12 }}
                            transition={{ duration: 0.18 }}
                            style={{
                                width: '100%', maxWidth: 420,
                                background: 'var(--jr-surface)',
                                border: '1px solid var(--jr-border)',
                                borderRadius: 'var(--jr-radius-lg)',
                                boxShadow: 'var(--jr-shadow-lg)',
                                overflow: 'hidden',
                            }}>

                            {/* Header */}
                            <div style={{
                                padding: '18px 22px', borderBottom: '1px solid var(--jr-border)',
                                display: 'flex', alignItems: 'center', gap: 12,
                            }}>
                                <div style={{
                                    width: 36, height: 36, borderRadius: 'var(--jr-radius-sm)',
                                    background: 'var(--jr-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                }}>
                                    <Lock size={16} color="var(--jr-accent-fg)" />
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--jr-text)' }}>Check your email</div>
                                    <div className="jr-eyebrow">Step 2 of 2 — Verification</div>
                                </div>
                                <button
                                    onClick={() => { setStep('login'); setOtpCode(['', '', '', '', '', '', '', '']); setOtpError(''); }}
                                    style={{
                                        width: 28, height: 28, borderRadius: 'var(--jr-radius-sm)',
                                        border: '1px solid var(--jr-border)', background: 'var(--jr-surface)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        cursor: 'pointer', color: 'var(--jr-text-2)', flexShrink: 0,
                                    }}>
                                    <X size={14} />
                                </button>
                            </div>

                            <div style={{ padding: 22 }}>
                                <p style={{ fontSize: 12, color: 'var(--jr-text-2)', marginBottom: 10 }}>We sent a secure 8-digit code to</p>
                                <div className="jr-mono" style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                    padding: '5px 10px', borderRadius: 'var(--jr-radius-sm)',
                                    background: 'var(--jr-accent-soft)', border: '1px solid var(--jr-accent-soft-border)',
                                    fontSize: 12, fontWeight: 600, color: 'var(--jr-text)', marginBottom: 20,
                                }}>
                                    <Mail size={12} /> {tempEmail}
                                </div>

                                {otpError && (
                                    <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: 8,
                                            padding: '10px 12px', borderRadius: 'var(--jr-radius-sm)', marginBottom: 16,
                                            background: 'var(--jr-danger-bg)', border: '1px solid var(--jr-danger-border)',
                                        }}>
                                        <AlertCircle size={14} color="var(--jr-danger)" />
                                        <span style={{ fontSize: 12, color: 'var(--jr-danger)', fontWeight: 600 }}>{otpError}</span>
                                    </motion.div>
                                )}

                                {/* Digit boxes */}
                                <div style={{ marginBottom: 18 }}>
                                    <div className="jr-eyebrow" style={{ marginBottom: 10 }}>Enter 8-digit code</div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        {otpCode.slice(0, 4).map((digit, index) => (
                                            <input
                                                key={index}
                                                id={`otp-${index}`}
                                                type="text" inputMode="numeric" maxLength={1} value={digit}
                                                onChange={e => handleOtpChange(index, e.target.value)}
                                                onKeyDown={e => handleOtpKeyDown(index, e)}
                                                onPaste={index === 0 ? handleOtpPaste : undefined}
                                                className="jr-mono"
                                                style={{
                                                    width: 36, height: 44, textAlign: 'center',
                                                    fontSize: 17, fontWeight: 700, outline: 'none',
                                                    borderRadius: 'var(--jr-radius-sm)',
                                                    border: `1.5px solid ${digit ? 'var(--jr-accent)' : 'var(--jr-border-strong)'}`,
                                                    background: digit ? 'var(--jr-accent-soft)' : 'var(--jr-surface-2)',
                                                    color: 'var(--jr-text)', flexShrink: 0,
                                                }}
                                            />
                                        ))}
                                        <span style={{ color: 'var(--jr-text-3)', fontSize: 16, flexShrink: 0, padding: '0 2px' }}>—</span>
                                        {otpCode.slice(4, 8).map((digit, i) => {
                                            const index = i + 4;
                                            return (
                                                <input
                                                    key={index}
                                                    id={`otp-${index}`}
                                                    type="text" inputMode="numeric" maxLength={1} value={digit}
                                                    onChange={e => handleOtpChange(index, e.target.value)}
                                                    onKeyDown={e => handleOtpKeyDown(index, e)}
                                                    className="jr-mono"
                                                    style={{
                                                        width: 36, height: 44, textAlign: 'center',
                                                        fontSize: 17, fontWeight: 700, outline: 'none',
                                                        borderRadius: 'var(--jr-radius-sm)',
                                                        border: `1.5px solid ${digit ? 'var(--jr-accent)' : 'var(--jr-border-strong)'}`,
                                                        background: digit ? 'var(--jr-accent-soft)' : 'var(--jr-surface-2)',
                                                        color: 'var(--jr-text)', flexShrink: 0,
                                                    }}
                                                />
                                            );
                                        })}
                                    </div>
                                    <div style={{ fontSize: 11, color: 'var(--jr-text-3)', marginTop: 10 }}>
                                        {otpCode.filter(d => d).length} of 8 digits entered · auto-submits when complete
                                    </div>
                                </div>

                                {/* Timer row */}
                                <div style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    padding: '10px 12px', borderRadius: 'var(--jr-radius-sm)', marginBottom: 18,
                                    background: 'var(--jr-surface-2)', border: '1px solid var(--jr-border)',
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                        <svg width="30" height="30" viewBox="0 0 30 30" style={{ flexShrink: 0 }}>
                                            <circle cx="15" cy="15" r="12" fill="none" stroke="var(--jr-border-strong)" strokeWidth="2.5" />
                                            <circle cx="15" cy="15" r="12" fill="none" stroke="var(--jr-accent)" strokeWidth="2.5"
                                                strokeDasharray="75.4" strokeDashoffset={75.4 - (otpTimer / 60) * 75.4}
                                                strokeLinecap="round" transform="rotate(-90 15 15)"
                                                style={{ transition: 'stroke-dashoffset 1s linear' }} />
                                        </svg>
                                        <div>
                                            <div className="jr-mono" style={{ fontSize: 15, fontWeight: 700, color: 'var(--jr-text)', lineHeight: 1 }}>{otpTimer}s</div>
                                            <div style={{ fontSize: 10, color: 'var(--jr-text-3)', fontWeight: 500 }}>until resend</div>
                                        </div>
                                    </div>
                                    {canResend ? (
                                        <button onClick={handleLogin} className="jr-btn jr-btn-sm">
                                            <RefreshCw size={11} /> Resend
                                        </button>
                                    ) : (
                                        <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--jr-text-3)' }}>Resend code</span>
                                    )}
                                </div>

                                {/* Verify btn */}
                                <button onClick={handleVerifyOTP}
                                    disabled={otpCode.join('').length !== 8 || loggingIn}
                                    className="jr-btn jr-btn-primary"
                                    style={{
                                        width: '100%', height: 42, justifyContent: 'center', fontSize: 13,
                                        marginBottom: 8,
                                        cursor: otpCode.join('').length !== 8 || loggingIn ? 'not-allowed' : 'pointer',
                                        opacity: otpCode.join('').length !== 8 || loggingIn ? 0.5 : 1,
                                    }}>
                                    {loggingIn ? (
                                        <><div style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.35)', borderTopColor: 'currentColor', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /> Verifying...</>
                                    ) : (
                                        <><Check size={15} /> Verify & Access Dashboard</>
                                    )}
                                </button>

                                <button
                                    onClick={() => { setStep('login'); setOtpCode(['', '', '', '', '', '', '', '']); setOtpError(''); }}
                                    className="jr-btn jr-btn-ghost"
                                    style={{ width: '100%', justifyContent: 'center' }}>
                                    <ChevronLeft size={13} /> Back to login
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}

                {/* ═══════════════════════════════════════
                    LOGIN SCREEN — split-screen: فورم يسار / بانل RITIS يمين
                    (استبدلنا تصميم "كاميرا المراقبة" بيه بناءً على طلب باكري،
                    نفس الفكرة اللي شافها في صفحة دخول Cloudflare — فورم بسيط
                    يسار، وبانل بالألوان الأساسية يمين فيه نفس الكرة الأرضية
                    المتحركة والمتوهجة بتاعة مانجر برزنتيشن (PresentationGlobe))
                ═══════════════════════════════════════ */}
                <div className="jr-login-screen" style={{
                    position: 'relative', minHeight: '100vh', width: '100%',
                    display: 'flex', background: 'var(--jr-bg)', transition: 'background 0.5s ease',
                }}>

                    {/* زرار الوضع الداكن/الفاتح — بسيط دلوقتي بعد ما اتشالت الكاميرا */}
                    <button
                        type="button"
                        onClick={() => setDarkMode(dm => !dm)}
                        aria-label="Toggle dark mode"
                        style={{
                            position: 'absolute', top: 28, right: 28, zIndex: 5,
                            width: 36, height: 36, borderRadius: 10,
                            background: 'var(--jr-surface)', border: '1px solid var(--jr-border)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', color: 'var(--jr-text-2)',
                        }}>
                        {darkMode ? <Sun size={16} /> : <Moon size={16} />}
                    </button>

                    {/* ───── يسار: الفورم (نفس المحتوى والمنطق القديم بالظبط،
                        بس من غير الكاميرا/البطاقة المؤطرة بزوايا viewfinder) ───── */}
                    <div className="jr-login-left" style={{
                        flex: '1 1 50%', minWidth: 0, position: 'relative',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        padding: 24,
                    }}>

                        {/* Brand mark, top-left — اللوقو الأصلي الكامل (الشعار + RITIS +
                            Al Rammah Int'l Telecom & IT Services) بدل الأيقونة الصغيرة
                            + النص جنبها اللي كانت قبل كده، بناءً على طلب باكري. */}
                        <div style={{
                            position: 'absolute', top: 32, left: 36,
                            background: '#fff', borderRadius: 10, border: '1px solid var(--jr-border)',
                            padding: '7px 12px', display: 'flex', alignItems: 'center',
                        }}>
                            <img src="/brand/ritis-logo.jpg" alt="RITIS — Al Rammah International Telecom & IT Services"
                                style={{ height: 30, width: 'auto', display: 'block', objectFit: 'contain' }}
                                onError={e => { e.target.style.display = 'none'; }} />
                        </div>

                        <div style={{ width: '100%', maxWidth: 380 }}>
                            <div className="jr-pill jr-pill-info" style={{ marginBottom: 20 }}>
                                <span className="jr-dot" style={{ background: 'var(--jr-info)' }} />
                                Secure Access
                            </div>

                            <h2 style={{ fontSize: 25, fontWeight: 800, color: 'var(--jr-text)', letterSpacing: '-0.02em', margin: '0 0 4px' }}>Welcome back</h2>
                            <p style={{ fontSize: 12, color: 'var(--jr-text-2)', margin: '0 0 26px' }}>Sign in to your RITIS dashboard</p>

                            {loginError && (
                                <div style={{
                                    display: 'flex', alignItems: 'center', gap: 8, padding: '11px 14px',
                                    borderRadius: 'var(--jr-radius-sm)', background: 'var(--jr-danger-bg)',
                                    border: '1px solid var(--jr-danger-border)', marginBottom: 18,
                                }}>
                                    <AlertCircle size={15} color="var(--jr-danger)" />
                                    <span style={{ fontSize: 12, color: 'var(--jr-danger)', fontWeight: 600 }}>{loginError}</span>
                                </div>
                            )}

                            <form onSubmit={e => { e.preventDefault(); handleLoginWithTurnstile(); }}>
                                <div style={{ marginBottom: 14 }}>
                                    <label htmlFor="login-email" className="jr-eyebrow" style={{ display: 'block', marginBottom: 8 }}>Email</label>
                                    {/* ✅ الحقل لسه بيسيب loginForm.username داخليًا (عشان handleLogin ومنطق
                                        emailMap اللي بيقبل username قديم أو إيميل كامل يفضلوا شغالين من غير
                                        تعديل) — بس اتعرض للمستخدم كـ "Email" زي ما طلب باكري. */}
                                    <input id="login-email" type="email" value={loginForm.username} onChange={e => setLoginForm({ ...loginForm, username: e.target.value })}
                                        placeholder="you@alrammah.com.sa" autoComplete="email" required
                                        style={{
                                            width: '100%', boxSizing: 'border-box', padding: '11px 13px',
                                            background: 'var(--jr-surface-2)', border: '1px solid var(--jr-border-strong)',
                                            borderRadius: 8, color: 'var(--jr-text)', fontSize: 13, fontWeight: 500, outline: 'none',
                                        }} />
                                </div>

                                <div style={{ marginBottom: 22 }}>
                                    <label htmlFor="login-password" className="jr-eyebrow" style={{ display: 'block', marginBottom: 8 }}>Password</label>
                                    <input id="login-password" type="password" value={loginForm.password} onChange={e => setLoginForm({ ...loginForm, password: e.target.value })}
                                        placeholder="••••••••" autoComplete="current-password" required
                                        style={{
                                            width: '100%', boxSizing: 'border-box', padding: '11px 13px',
                                            background: 'var(--jr-surface-2)', border: '1px solid var(--jr-border-strong)',
                                            borderRadius: 8, color: 'var(--jr-text)', fontSize: 13, fontWeight: 500, outline: 'none',
                                        }} />
                                </div>

                                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'center' }}>
                                    <TurnstileWidget ref={turnstileRef} onVerify={setTurnstileToken} theme={darkMode ? 'dark' : 'light'} />
                                </div>

                                <button type="submit" disabled={loggingIn || verifyingTurnstile}
                                    className="jr-btn jr-btn-primary"
                                    style={{ width: '100%', height: 44, justifyContent: 'center', fontSize: 13 }}>
                                    {loggingIn || verifyingTurnstile ? (
                                        <><div style={{ width: 15, height: 15, border: '2px solid rgba(255,255,255,0.35)', borderTopColor: 'currentColor', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /> Signing in...</>
                                    ) : (
                                        <>Sign In <ArrowRight size={15} /></>
                                    )}
                                </button>
                            </form>

                            {/* ✅ قسم "Client Access" (زراير "Client Portal" → /client/itsm/tickets
                                و"IT Services" → /client/services) اتشال مؤقتًا من صفحة تسجيل الدخول
                                بناءً على طلب باكري — هنرجعه بعد ما نشتغل على بورتال الكلاينت. */}

                            {/* Slim stats row */}
                            <div style={{
                                marginTop: 28, display: 'flex', alignItems: 'center', gap: 14,
                                fontFamily: 'var(--jr-font-mono)', fontSize: 11, fontWeight: 600, color: 'var(--jr-text-3)',
                            }}>
                                <span>{COMPANY_INFO.projects} Projects</span>
                                <span style={{ opacity: 0.5 }}>·</span>
                                <span>{COMPANY_INFO.clients} Clients</span>
                                <span style={{ opacity: 0.5 }}>·</span>
                                <span>{COMPANY_INFO.employees} Employees</span>
                                <span style={{ opacity: 0.5 }}>·</span>
                                <span>Est. {COMPANY_INFO.founded}</span>
                            </div>
                        </div>
                    </div>

                    {/* ───── يمين: بانل RITIS بألوان البراند + نفس الكرة الأرضية
                        المتحركة والمتوهجة اللي في مانجر برزنتيشن (PresentationGlobe —
                        نقط تمثل اليابسة، تدور وتزوم على السعودية، وهالة توهج حوالين
                        الكرة، وبينج رادار نابض من الخبر مستمر لحد ما الصفحة تتقفل).
                        بيتخفي تلقائيًا على الشاشات الضيقة (شوف designSystem.css) ───── */}
                    <div className="jr-login-right" style={{
                        flex: '1 1 50%', minWidth: 0, position: 'relative', overflow: 'hidden',
                        display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: 64,
                        background: 'linear-gradient(145deg, #0F172A 0%, #1E293B 100%)',
                    }}>
                        {/* showHQCard=false — بطاقة "RITIS OPERATIONS / Al Khobar · Head
                            Office" اتشالت من على الكرة هنا بناءً على طلب باكري */}
                        <PresentationGlobe active sites={[]} showHQCard={false} />

                        <div style={{ position: 'relative', zIndex: 1, maxWidth: 460, pointerEvents: 'none' }}>
                            <h1 style={{ fontSize: 34, fontWeight: 800, color: '#fff', letterSpacing: '-0.02em', lineHeight: 1.15, margin: '0 0 16px' }}>
                                Where ICT &amp; ELV<br />infrastructure comes together.
                            </h1>
                            <p style={{ fontSize: 14, color: 'rgba(226,232,240,0.7)', lineHeight: 1.6, margin: 0 }}>
                                CCTV · Access Control · Fiber · Fire Alarm · AV · Structured Cabling — managed from one dashboard across {COMPANY_INFO.projects} live projects.
                            </p>
                        </div>
                    </div>
                </div>

            </div>
        );
    }

    return (
        <div className={`jr-app min-h-screen ${theme.bg} transition-colors duration-300`} data-theme={darkMode ? 'dark' : 'light'}>


            <AnimatePresence>
                {showWelcome && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        style={{ position: 'fixed', inset: 0, background: 'rgba(12,10,9,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
                    >
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 12 }}
                            transition={{ duration: 0.18 }}
                            style={{
                                width: '100%', maxWidth: 460,
                                background: 'var(--jr-surface)',
                                borderRadius: 'var(--jr-radius-lg)',
                                overflow: 'hidden',
                                border: '1px solid var(--jr-border)',
                                boxShadow: 'var(--jr-shadow-lg)',
                            }}
                        >
                            {/* ── HERO ── */}
                            <div style={{
                                padding: '24px 26px 20px',
                                background: 'var(--jr-surface-2)',
                                borderBottom: '1px solid var(--jr-border)',
                            }}>
                                {/* User card */}
                                <div style={{
                                    display: 'flex', alignItems: 'center', gap: 14,
                                    padding: '12px 14px', borderRadius: 'var(--jr-radius)',
                                    background: 'var(--jr-surface)',
                                    border: '1px solid var(--jr-border)',
                                    marginBottom: 18,
                                }}>
                                    <div style={{
                                        width: 46, height: 46, borderRadius: 'var(--jr-radius)', overflow: 'hidden', flexShrink: 0,
                                        border: '1px solid var(--jr-border-strong)',
                                    }}>
                                        <img src={currentUser?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(currentUser?.name || 'U')}&background=0F172A&color=fff&size=96`}
                                            alt={currentUser?.name}
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--jr-text)', marginBottom: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {currentUser?.name}
                                        </div>
                                        <div style={{ fontSize: 11, color: 'var(--jr-text-2)', fontWeight: 500 }}>
                                            {currentUser?.position} · Al-Rammah
                                        </div>
                                    </div>
                                    <div className="jr-pill jr-pill-success" style={{ flexShrink: 0 }}>
                                        <span className="jr-dot" style={{ background: 'var(--jr-success)' }} />
                                        Active
                                    </div>
                                </div>

                                {/* Title */}
                                <div className="jr-pill jr-pill-warning" style={{ marginBottom: 10 }}>
                                    <Shield size={10} />
                                    Confidentiality Required
                                </div>
                                <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--jr-text)', letterSpacing: '-0.01em', marginBottom: 4 }}>
                                    Data Confidentiality Agreement
                                </div>
                                <div style={{ fontSize: 11, color: 'var(--jr-text-2)', fontWeight: 500 }}>
                                    {new Date().getHours() < 12 ? 'Good Morning' : new Date().getHours() < 17 ? 'Good Afternoon' : 'Good Evening'}, {currentUser?.name?.split(' ')[0]} — please review before proceeding
                                </div>
                            </div>

                            {/* ── BODY ── */}
                            <div style={{ padding: '20px 26px' }}>

                                {/* Notice */}
                                <div style={{
                                    display: 'flex', gap: 12, alignItems: 'flex-start',
                                    padding: '13px 15px', borderRadius: 'var(--jr-radius)', marginBottom: 18,
                                    background: 'var(--jr-warning-bg)', border: '1px solid var(--jr-warning-border)',
                                }}>
                                    <div style={{
                                        width: 30, height: 30, borderRadius: 'var(--jr-radius-sm)', flexShrink: 0,
                                        background: 'var(--jr-surface)', border: '1px solid var(--jr-warning-border)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        <AlertCircle size={14} color="var(--jr-warning)" />
                                    </div>
                                    <div>
                                        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--jr-text)', marginBottom: 5, lineHeight: 1.6, direction: 'rtl', textAlign: 'right' }}>
                                            جميع البيانات والمعلومات تخص شركة <strong>الرماح</strong> حصرياً وهي سرية وخاصة.
                                        </div>
                                        <div style={{ fontSize: 10.5, color: 'var(--jr-text-2)', fontWeight: 500, lineHeight: 1.6 }}>
                                            All data belongs exclusively to <strong style={{ color: 'var(--jr-text)' }}>Al-Rammah</strong> and is strictly confidential.
                                        </div>
                                    </div>
                                </div>

                                {/* Terms label */}
                                <div className="jr-eyebrow" style={{ marginBottom: 10 }}>
                                    Terms of Access
                                </div>

                                {/* Term items */}
                                {[
                                    { n: '01', title: 'No External Sharing', sub: 'Sharing or copying any data with external parties is strictly prohibited.', tone: 'info' },
                                    { n: '02', title: 'Authorized Access Only', sub: 'System use is restricted to authorized Al-Rammah employees only.', tone: 'danger' },
                                    { n: '03', title: 'Legal Accountability', sub: 'Any breach or misuse will result in full legal accountability.', tone: 'warning' },
                                ].map((t, i) => (
                                    <div key={i} style={{
                                        display: 'flex', gap: 12, alignItems: 'flex-start',
                                        padding: '11px 14px', borderRadius: 'var(--jr-radius)', marginBottom: 7,
                                        background: 'var(--jr-surface)', border: '1px solid var(--jr-border)',
                                    }}>
                                        <div className="jr-mono" style={{
                                            width: 24, height: 24, borderRadius: 'var(--jr-radius-sm)', flexShrink: 0,
                                            fontSize: 10, fontWeight: 700,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            background: `var(--jr-${t.tone}-bg)`, border: `1px solid var(--jr-${t.tone}-border)`, color: `var(--jr-${t.tone})`,
                                        }}>{t.n}</div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--jr-text)', marginBottom: 2 }}>{t.title}</div>
                                            <div style={{ fontSize: 10.5, color: 'var(--jr-text-2)', fontWeight: 500, lineHeight: 1.5 }}>{t.sub}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* ── FOOTER ── */}
                            <div style={{ padding: '12px 26px 22px', borderTop: '1px solid var(--jr-border)', display: 'flex', flexDirection: 'column', gap: 10 }}>
                                <div style={{
                                    display: 'flex', alignItems: 'center', gap: 7,
                                    fontSize: 10.5, color: 'var(--jr-text-3)', fontWeight: 500,
                                    padding: '7px 10px', borderRadius: 'var(--jr-radius-sm)', background: 'var(--jr-surface-2)',
                                }}>
                                    <Lock size={11} />
                                    Your access is logged and monitored for security purposes
                                </div>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <button onClick={() => handleLogout()}
                                        className="jr-btn"
                                        style={{
                                            flex: 1, justifyContent: 'center', height: 40,
                                            background: 'var(--jr-danger-bg)', border: '1px solid var(--jr-danger-border)', color: 'var(--jr-danger)',
                                        }}>
                                        <LogOut size={13} />
                                        Decline
                                    </button>
                                    <button onClick={() => setShowWelcome(false)}
                                        className="jr-btn jr-btn-primary"
                                        style={{ flex: 2, justifyContent: 'center', height: 40 }}>
                                        <Check size={14} />
                                        I Agree &amp; Access Dashboard
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
            {/* Project Switching Overlay */}
            <ProjectSwitchingOverlay
                switchingProject={switchingProject}
                darkMode={darkMode}
                projects={PROJECTS}
                currentProjectId={currentProjectId}
            />

            {/* ============ EXECUTIVE HEADER — Pro Max Redesign ============ */}
            <div style={{
                background: '#0B0C0E',
                borderBottom: '1px solid rgba(255,255,255,0.06)',
                position: 'sticky', top: 0, zIndex: 40,
            }}>

                {/* ── TOP BAR ── */}
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '7px 20px', borderBottom: '1px solid rgba(255,255,255,0.04)',
                    gap: 12,
                }}>
                    {/* Left: Logo + Project Selector */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                        {/* Logo — ✅ بناءً على طلب باكري: اللوجو نفسه بس (هو أصلاً
                            فيه RITIS + Al Rammah Int'l Telecom & IT Services
                            مرسومين جواه)، من غير ما نكرر "Al-Rammah"/"RITIS"
                            كنص منفصل جمبه زي ما كان قبل كده. الصورة بقت بارتفاع
                            المساحة المتاحة في الـ top bar بدل ما تتزنق في مربع
                            30×30 صغير (كانت بتبان صغيرة جدًا/مقصوصة). */}
                        <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                            <img src="/avatars/logo.jpeg" alt="RITIS — Al Rammah International IT &amp; Telecom Services"
                                style={{ height: 28, width: 'auto', objectFit: 'contain', borderRadius: 4 }}
                                onError={e => { e.target.style.display = 'none'; }} />
                        </div>

                        <div style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.07)', flexShrink: 0 }} />

                        {isHRPortal ? (
                            /* بورتال HR مالوش دعوة بمشروع معيّن — بدل الـ Project Selector بادج ثابت */
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                <div style={{
                                    width: 26, height: 26, borderRadius: 7, flexShrink: 0,
                                    background: 'rgba(109,40,217,0.15)', border: '1px solid rgba(109,40,217,0.3)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <Users size={13} color="#A78BFA" />
                                </div>
                                <span style={{ fontSize: 13, fontWeight: 800, color: '#fff', letterSpacing: '-0.01em', whiteSpace: 'nowrap' }}>
                                    Human Resources Portal
                                </span>
                            </div>
                        ) : isAccountsPortal ? (
                            /* بورتال Accounts — بادج ثابت زي HR بالظبط، مالوش مشروع محدد */
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                <div style={{
                                    width: 26, height: 26, borderRadius: 7, flexShrink: 0,
                                    background: 'rgba(8,145,178,0.15)', border: '1px solid rgba(8,145,178,0.3)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <Wallet size={13} color="#22D3EE" />
                                </div>
                                <span style={{ fontSize: 13, fontWeight: 800, color: '#fff', letterSpacing: '-0.01em', whiteSpace: 'nowrap' }}>
                                    Accounts Portal
                                </span>
                            </div>
                        ) : isCoordinationPortal ? (
                            /* بورتال Coordination — بادج ثابت زي HR/Accounts بالظبط، مالوش مشروع محدد */
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                <div style={{
                                    width: 26, height: 26, borderRadius: 7, flexShrink: 0,
                                    background: 'rgba(180,83,9,0.15)', border: '1px solid rgba(180,83,9,0.3)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <Boxes size={13} color="#FBBF24" />
                                </div>
                                <span style={{ fontSize: 13, fontWeight: 800, color: '#fff', letterSpacing: '-0.01em', whiteSpace: 'nowrap' }}>
                                    Coordination Portal
                                </span>
                            </div>
                        ) : (
                            /* Project Selector */
                            <ProjectSelector
                                currentProjectId={currentProjectId}
                                projects={PROJECTS}
                                canViewProject={canViewProject}
                                showToast={showToast}
                                onSwitchProject={switchProject}
                                switchingProject={switchingProject}
                            />
                        )}
                    </div>

                    {/* Right: Actions */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>

                        {/* Dark mode toggle */}
                        <div style={{ display: 'flex', background: 'rgba(255,255,255,0.04)', border: '0.5px solid rgba(255,255,255,0.08)', borderRadius: 8, overflow: 'hidden' }}>
                            {[{ mode: false, Icon: Sun }, { mode: true, Icon: Moon }].map(({ mode, Icon }) => (
                                <button key={String(mode)} onClick={() => setDarkMode(mode)} style={{
                                    width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    background: darkMode === mode ? 'rgba(37,99,235,0.2)' : 'transparent',
                                    border: 'none', cursor: 'pointer',
                                    color: darkMode === mode ? '#2563EB' : 'rgba(148,163,184,0.4)',
                                }}><Icon size={12} /></button>
                            ))}
                        </div>

                        {/* Fullscreen */}
                        <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                            onClick={() => { if (!document.fullscreenElement) document.documentElement.requestFullscreen(); else document.exitFullscreen(); }}
                            style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(255,255,255,0.04)', border: '0.5px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'rgba(148,163,184,0.5)' }}>
                            <Maximize size={12} />
                        </motion.button>

                        {/* Notifications */}
                        <div style={{ position: 'relative' }}>
                            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                                onClick={() => setShowNotifications(!showNotifications)}
                                style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(255,255,255,0.04)', border: '0.5px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'rgba(148,163,184,0.5)', position: 'relative' }}>
                                <Bell size={12} />
                                {notifications.length > 0 && (
                                    <>
                                        <span style={{ position: 'absolute', top: -3, right: -3, width: 14, height: 14, background: '#ef4444', borderRadius: '50%', border: '1.5px solid #0B0C0E', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 7, fontWeight: 900, color: '#fff' }}>
                                            {notifications.length > 9 ? '9+' : notifications.length}
                                        </span>
                                        <span style={{ position: 'absolute', top: -3, right: -3, width: 14, height: 14, background: '#ef4444', borderRadius: '50%', animation: 'ping 1s infinite', opacity: 0.5 }} />
                                    </>
                                )}
                            </motion.button>

                            <AnimatePresence>
                                {showNotifications && (
                                    <>
                                        <div className="fixed inset-0 z-40" onClick={() => setShowNotifications(false)} />
                                        <motion.div initial={{ opacity: 0, y: 8, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 8, scale: 0.96 }}
                                            style={{ position: 'absolute', right: 0, top: '100%', marginTop: 8, width: 380, background: '#131417', borderRadius: 16, border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 25px 50px rgba(0,0,0,0.5)', overflow: 'hidden', zIndex: 9999 }}>
                                            <div style={{ padding: '14px 18px', background: '#191A1E', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <div>
                                                    <h3 style={{ fontWeight: 900, color: '#fff', fontSize: 14, display: 'flex', alignItems: 'center', gap: 7 }}>
                                                        <Bell size={14} style={{ color: '#2563EB' }} /> Notifications
                                                    </h3>
                                                    <p style={{ color: 'rgba(148,163,184,0.5)', fontSize: 10, marginTop: 2 }}>
                                                        {notifications.length > 0 ? `${notifications.filter(n => n.priority === 'critical').length} critical · ${notifications.length} total` : 'All caught up!'}
                                                    </p>
                                                </div>
                                                <div style={{ display: 'flex', gap: 5 }}>
                                                    {notifications.length > 0 && (
                                                        <button onClick={async () => {
                                                            if (isHRPortal || isAccountsPortal || isCoordinationPortal) { setNotifications([]); return; }
                                                            await supabase.from('notifications').update({ is_read: true }).eq('project_id', currentProjectId); fetchNotifications();
                                                        }}
                                                            style={{ padding: '4px 10px', background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: 7, color: '#fff', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>
                                                            Mark all read
                                                        </button>
                                                    )}
                                                    <button onClick={() => setShowNotifications(false)}
                                                        style={{ width: 26, height: 26, background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: 7, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                                                        <X size={12} />
                                                    </button>
                                                </div>
                                            </div>
                                            {notifications.length === 0 ? (
                                                <div style={{ padding: '36px 20px', textAlign: 'center' }}>
                                                    <CheckCircle size={28} style={{ color: '#34d399', margin: '0 auto 10px' }} />
                                                    <p style={{ fontWeight: 700, color: '#fff', fontSize: 13 }}>All caught up!</p>
                                                    <p style={{ color: 'rgba(148,163,184,0.4)', fontSize: 11, marginTop: 3 }}>No new notifications</p>
                                                </div>
                                            ) : (
                                                <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                                                    {['critical', 'high', 'medium', 'low'].map(priority => {
                                                        const group = notifications.filter(n => n.priority === priority);
                                                        if (!group.length) return null;
                                                        const cfg = { critical: { label: 'Critical', color: '#ef4444', bg: 'rgba(239,68,68,0.06)' }, high: { label: 'High', color: '#fb923c', bg: 'rgba(251,146,60,0.06)' }, medium: { label: 'Medium', color: '#2563EB', bg: 'rgba(129,140,248,0.06)' }, low: { label: 'Low', color: 'rgba(148,163,184,0.5)', bg: 'transparent' } }[priority];
                                                        return (
                                                            <div key={priority}>
                                                                <div style={{ padding: '5px 14px', borderBottom: '1px solid rgba(255,255,255,0.04)', display: 'flex', alignItems: 'center', gap: 5 }}>
                                                                    <div style={{ width: 5, height: 5, borderRadius: '50%', background: cfg.color }} />
                                                                    <span style={{ fontSize: 8, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', color: cfg.color }}>{cfg.label} · {group.length}</span>
                                                                </div>
                                                                {group.map((notif, idx) => (
                                                                    <motion.div key={notif.id} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: idx * 0.03 }}
                                                                        onClick={async () => {
                                                                            if (isHRPortal || isAccountsPortal || isCoordinationPortal) { setNotifications(prev => prev.filter(n => n.id !== notif.id)); return; }
                                                                            await supabase.from('notifications').update({ is_read: true }).eq('id', notif.id); fetchNotifications();
                                                                        }}
                                                                        style={{ padding: '9px 14px', borderBottom: '1px solid rgba(255,255,255,0.04)', background: cfg.bg, cursor: 'pointer', display: 'flex', gap: 9 }}>
                                                                        <div style={{ width: 4, height: 4, borderRadius: '50%', background: cfg.color, flexShrink: 0, marginTop: 5 }} />
                                                                        <div>
                                                                            <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)', lineHeight: 1.4 }}>{notif.message}</p>
                                                                            <p style={{ fontSize: 9, color: 'rgba(148,163,184,0.4)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 3 }}>
                                                                                <Clock size={8} />{new Date(notif.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                                                            </p>
                                                                        </div>
                                                                    </motion.div>
                                                                ))}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                            <div style={{ padding: '9px 14px', borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <span style={{ fontSize: 9, color: 'rgba(148,163,184,0.35)' }}>{isHRPortal ? 'Live HR status' : isAccountsPortal ? 'Live Accounts status' : isCoordinationPortal ? 'Live Coordination status' : 'Updated daily at 9 AM'}</span>
                                                <button onClick={() => isHRPortal ? fetchHRNotifications() : isAccountsPortal ? fetchAccountsNotifications() : isCoordinationPortal ? setNotifications([]) : fetchNotifications()} style={{ fontSize: 9, color: '#2563EB', fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3 }}>
                                                    <RefreshCw size={9} /> Refresh
                                                </button>
                                            </div>
                                        </motion.div>
                                    </>
                                )}
                            </AnimatePresence>
                        </div>

                        <div style={{ width: 1, height: 22, background: 'rgba(255,255,255,0.07)' }} />

                        {/* Export PDF — ✅ لسه مفيش generator مخصص لبورتال Coordination
                            (زي generateHRSummaryPDF/generateAccountsSummaryPDF)، وبورتال
                            Coordination مالوش currentProjectId ثابت فـ generatePDF()
                            العادية ممكن تحاول تشتغل على مشروع null — بنخفي الزرار خالص
                            ليهم دلوقتي بدل ما نخطر نادي على حاجة ممكن تكسر. */}
                        {!isCoordinationPortal && (
                            <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} onClick={() => isHRPortal ? generateHRSummaryPDF() : isAccountsPortal ? generateAccountsSummaryPDF() : generatePDF()}
                                style={{ padding: '5px 11px', borderRadius: 7, background: 'transparent', border: '1px solid rgba(255,255,255,0.14)', color: 'rgba(255,255,255,0.75)', fontSize: 10, fontWeight: 800, letterSpacing: '0.04em', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, textTransform: 'uppercase' }}>
                                <FileDown size={11} /> PDF
                            </motion.button>
                        )}

                        {/* Client Portal */}
                        {hasPermission('all') && (
                            <motion.button
                                whileHover={{ scale: 1.03 }}
                                whileTap={{ scale: 0.97 }}
                                onClick={() => setShowPortalModal(true)}  // ← بدل generateClientLink
                                style={{ padding: '5px 11px', borderRadius: 7, background: '#3B82F6', border: '1px solid #3B82F6', color: '#fff', fontSize: 10, fontWeight: 800, letterSpacing: '0.04em', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, textTransform: 'uppercase' }}>
                                <Globe size={11} /> Portal
                            </motion.button>
                        )}

                        {/* المودل */}
                        {showPortalModal && (
                            <Suspense fallback={null}>
                                <ClientPortalModal
                                    projectId={currentProjectId}
                                    projectName={PROJECT?.name}
                                    clientName={PROJECT?.client}
                                    supabase={supabase}
                                    onClose={() => setShowPortalModal(false)}
                                />
                            </Suspense>
                        )}

                        <div style={{ width: 1, height: 22, background: 'rgba(255,255,255,0.07)' }} />

                        {/* ✅ User Menu اتنقل لكمبوننت مستقل (src/components/common/UserMenu.jsx)
                            بحالته الخاصة (showUserMenu/showITSMMenu بقوا local state جواه)
                            — فتح/قفل الدروب داون دلوقتي بيعمل re-render للكمبوننت ده بس، مش
                            لـ App كلها (كان ده سبب الـ "رمشة" اللي باكري لاحظها). */}
                        <UserMenu
                            currentUser={currentUser}
                            hasPermission={hasPermission}
                            canViewSection={canViewSection}
                            setShowSettings={setShowSettings}
                            setShowProjectManager={setShowProjectManager}
                            setShowUserManagement={setShowUserManagement}
                            handleLogout={handleLogout}
                        />
                    </div>
                </div>

                {/* ── INFO BAND — بيانات مشروع معيّن، مالهاش معنى في بورتال HR/Accounts ── */}
                {!isPortalMode && (
                <div style={{
                    display: 'flex', alignItems: 'center',
                    padding: '7px 20px', gap: 0,
                    borderBottom: '1px solid rgba(255,255,255,0.04)',
                    overflowX: 'auto', scrollbarWidth: 'none',
                }}>
                    {[
                        { icon: Briefcase, label: 'Client', value: PROJECT?.client?.slice(0, 18) || '—', color: '#2563EB', bg: 'rgba(37,99,235,0.1)', show: true },
                        { icon: FileText, label: 'Contract', value: PROJECT?.contract?.slice(0, 16) || '—', color: 'rgba(148,163,184,0.7)', bg: 'rgba(255,255,255,0.05)', show: true },
                        { icon: DollarSign, label: 'Value', value: `SAR ${(PROJECT?.totalValue || 0).toLocaleString()}`, color: '#34d399', bg: 'rgba(52,211,153,0.1)', show: hasPermission('financial') },
                        { icon: Calendar, label: 'Deadline', value: PROJECT?.endDate instanceof Date ? PROJECT.endDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : (PROJECT?.endDate || '—'), color: '#fb923c', bg: 'rgba(251,146,60,0.1)', show: true },
                        { icon: Activity, label: 'Status', value: PROJECT?.status || 'Active', color: '#34d399', bg: 'rgba(52,211,153,0.08)', show: true, dot: true },
                    ].filter(m => m.show).map((item, i, arr) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 16px', borderRight: i < arr.length - 1 ? '0.5px solid rgba(255,255,255,0.06)' : 'none', flexShrink: 0 }}>
                            <div style={{ width: 26, height: 26, borderRadius: 7, background: item.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <item.icon size={12} style={{ color: item.color }} />
                            </div>
                            <div>
                                <div style={{ fontSize: 8, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(148,163,184,0.3)', marginBottom: 2 }}>{item.label}</div>
                                <div style={{ fontSize: 11, fontWeight: 700, color: item.color, lineHeight: 1, display: 'flex', alignItems: 'center', gap: 4 }}>
                                    {item.dot && <span style={{ width: 5, height: 5, borderRadius: '50%', background: item.color, display: 'inline-block' }} />}
                                    {item.value}
                                </div>
                            </div>
                        </div>
                    ))}

                    {/* Manage buttons — ✅ كانت مقيّدة بالاسم النصي، بدّلناها بنظام الصلاحيات.
                        زرار "Users" بالذات اتقفل على باكري بس (طلبه) — مش على hasPermission('all')
                        العام زي "Projects"، عشان إدارة حسابات الموظفين تفضل ليه لوحده. */}
                    {hasPermission('all') && (
                        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 5, paddingLeft: 16, flexShrink: 0 }}>
                            <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                                onClick={() => setShowProjectManager(true)}
                                style={{ padding: '4px 10px', borderRadius: 7, background: 'rgba(71,85,105,0.3)', border: '0.5px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.6)', fontSize: 9, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, textTransform: 'uppercase', letterSpacing: '0.04em', transition: 'all 0.15s' }}
                                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(37,99,235,0.15)'; e.currentTarget.style.color = '#2563EB'; e.currentTarget.style.borderColor = 'rgba(37,99,235,0.25)'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(71,85,105,0.3)'; e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}>
                                <Settings size={11} /> Projects
                            </motion.button>
                            {currentUser?.username === 'bakri' && (
                            <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                                onClick={() => setShowUserManagement(true)}
                                style={{ padding: '4px 10px', borderRadius: 7, background: 'rgba(71,85,105,0.3)', border: '0.5px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.6)', fontSize: 9, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, textTransform: 'uppercase', letterSpacing: '0.04em', transition: 'all 0.15s' }}
                                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(37,99,235,0.15)'; e.currentTarget.style.color = '#2563EB'; e.currentTarget.style.borderColor = 'rgba(37,99,235,0.25)'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(71,85,105,0.3)'; e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}>
                                <UserCog size={11} /> Users
                            </motion.button>
                            )}
                        </div>
                    )}
                </div>
                )}

                {/* ✅ اتشالت هنا: overlay "Loading Project..." كان كود ميت مكرر —
                    loadingProject متعرّف بس setLoadingProject ما بتتنادى في أي
                    مكان، ونفس الوظيفة دي بالظبط بيعملها ProjectSwitchingOverlay
                    (اللي بيستخدم switchingProject) وهو شغال فعليًا. */}

                {/* ── NAVIGATION TABS — تاب واحد بس هيفضل لبورتال HR/Accounts، فبنستغنى عن الشريط كله ── */}
                {!isPortalMode && (
                <div style={{
                    display: 'flex', alignItems: 'center',
                    padding: '6px',
                    gap: 0,
                    overflowX: 'auto',
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'rgba(255,255,255,.15) transparent',
                    background: 'rgba(255,255,255,.04)',
                    borderTop: '1px solid rgba(255,255,255,.06)',
                }}>
                    <style>{`
        .dash-navtab {
            display: flex; align-items: center; gap: 5px;
            padding: 7px 13px; border-radius: 9px;
            font-size: 12px; font-weight: 600;
            cursor: pointer; white-space: nowrap;
            border: 1px solid transparent;
            background: none; position: relative;
           color: rgba(255,255,255,.45);
            transition: color 0.15s, background 0.15s, border-color 0.15s;
            flex-shrink: 0; letter-spacing: 0.01em;
        }
        .dash-navtab svg { opacity: 0.55; transition: opacity 0.15s; }
        .dash-navtab:hover:not(.active) {
            color: rgba(255,255,255,.7);
        }
        .dash-navtab.active {
            background: #1e293b;
           color: #ffffff;
            border-color: rgba(255,255,255,.1);
        }
        .dash-navtab.active svg { opacity: 1; }
        .dash-navtab .tab-badge {
            min-width: 16px; height: 16px; padding: 0 4px;
            border-radius: 99px; background: rgba(239,68,68,0.2);
            color: #f87171; font-size: 8px; font-weight: 800;
            display: flex; align-items: center; justify-content: center;
            line-height: 1;
        }
    `}</style>

                    {[
                        { label: "Overview", Icon: TrendingUp },
                        { label: "Project Schedule", Icon: LayoutList },
                        { label: "Tasks", Icon: CheckCircle },
                        { label: "Daily Activities", Icon: ClipboardList },
                        { label: "Team", Icon: Users },
                        { label: "Materials", Icon: Package },
                        { label: "Photos", Icon: ImageIcon },
                        { label: "Invoices", Icon: Receipt },
                        { label: "Client Account", Icon: Wallet },
                        { label: "EVM", Icon: Gauge },
                        { label: "Comments", Icon: MessageSquare },
                        { label: "Timeline", Icon: Calendar },
                        { label: "Documents", Icon: FileText },
                        { label: "Expenses Management", Icon: DollarSign },
                        { label: "Location", Icon: MapPin },
                        { label: "Systems Library", Icon: BookOpen },
                        { label: "Submittal Assembler", Icon: FileDown },
                        { label: "HR", Icon: Users },
                        { label: "Coordination", Icon: Boxes },
                        { label: "Meetings", Icon: Mic },
                        { label: "Prequal Profile", Icon: Shield },
                        { label: "Showcase Tokens", Icon: Link },
                        { label: "Showcase Leads", Icon: Inbox },
                        { label: "Showcase Analytics", Icon: Activity },
                        { label: "Approved Brands", Icon: Package },
                    ]
                        .filter(item => canViewSection(item.label))
                        .map(({ label, Icon }, idx) => (
                            <motion.button
                                key={idx}
                                className={`dash-navtab${view === label ? ' active' : ''}`}
                                whileTap={{ scale: 0.97 }}
                                onClick={() => setView(label)}
                            >
                                <Icon size={13} />
                                {label}
                                {label === 'Showcase Leads' && counts?.newLeads > 0 && (
                                    <span className="tab-badge">{counts.newLeads}</span>
                                )}
                            </motion.button>
                        ))}
                </div>
                )}
            </div>

            {/* CONTENT */}
            <div style={{ width: '100%', padding: '0' }}>
                {/* ✅ GLOBAL PHOTO MODAL انتقل لـ PhotoModalProvider (src/contexts/PhotoModalContext.jsx)
                    — بيترندر فوق <App/> في index.tsx دلوقتي، مش جوه App نفسها. */}


                {view === "Location" && (
                    <LocationView
                        darkMode={darkMode}
                        PROJECT={PROJECT}
                        teamData={teamData}
                        tasksData={tasksData}
                        currentProjectId={currentProjectId}
                        mapClickPos={mapClickPos}
                        setMapClickPos={setMapClickPos}
                        selectedTaskForPin={selectedTaskForPin}
                        setSelectedTaskForPin={setSelectedTaskForPin}
                        showToast={showToast}
                        fetchData={fetchData}
                    />
                )}
                {view === "Overview" && (
                    <OverviewView
                        PROJECT={PROJECT}
                        darkMode={darkMode}
                        currentUser={currentUser}
                        hasPermission={hasPermission}
                        setView={setView}
                        stats={stats}
                        tasksData={tasksData}
                        teamData={teamData}
                        materialsData={materialsData}
                        photosData={photosData}
                        invoicesData={invoicesData}
                        attendanceData={attendanceData}
                        dailyActivities={dailyActivities}
                        phases={phases}
                        clientComments={clientComments}
                        expenseChartData={expenseChartData}
                        formatCurrency={formatCurrency}
                    />
                )}
                {/* BUDGET */}
                {view === "Budget" && (
                    <BudgetView theme={theme} stats={stats} budgetData={budgetData} />
                )}

                {/* EVM — Earned Value Management */}
                {view === "EVM" && (
                    <EVMView
                        darkMode={darkMode}
                        evmData={evmData}
                        tasksData={tasksData}
                        budgetExpenses={budgetExpenses}
                        budgetCategories={budgetCategories}
                        budgetCommitments={budgetCommitments}
                        getCategorySpent={getCategorySpent}
                        getCategoryCommitted={getCategoryCommitted}
                        phases={phases}
                        teamData={teamData}
                        milestones={milestones}
                        formatCurrency={formatCurrency}
                        PROJECT={PROJECT}
                    />
                )}




                {/* ✅ كانت متركبة (mounted) طول الوقت حتى لو التاب ده مش مفتوح —
                    بس التاب الحالي (view) هو اللي يقرر لو يظهر محتوى أو لا جوه
                    الكومبوننت نفسه. يعني أي تغيير state في أي حتة في App (فتح أي
                    مودال في أي سكشن تاني، كتابة في أي حقل بحث..) كان بيخلي React
                    يعيد render لكل الـ 11 View الثقيلة دي مرة واحدة، حتى اللي
                    مش ظاهرة أصلاً — وده شغل زيادة على المتصفح بيسبب التهنيج/الرمش
                    اللي بيظهر وقت فتح/قفل أي مودال في أي سكشن. دلوقتي بقت متركبة
                    بس وقت ما التاب ده فعلاً مفتوح، زي باقي الفيوز (Location/
                    Overview/Budget/EVM/Documents) اللي كانت عاملة كذا من الأساس. */}
                {view === "Invoices" && (
                <InvoicesView
                    view={view} darkMode={darkMode} currentUser={currentUser} hasPermission={hasPermission} formatCurrency={formatCurrency}
                    invoicesData={invoicesData}
                    selectedInvoice={selectedInvoice} setSelectedInvoice={setSelectedInvoice}
                    showInvoiceDetailsModal={showInvoiceDetailsModal} setShowInvoiceDetailsModal={setShowInvoiceDetailsModal}
                    rejectionReason={rejectionReason} setRejectionReason={setRejectionReason}
                    approvingInvoice={approvingInvoice} handleInvoiceApproval={handleInvoiceApproval}
                    invoiceForm={invoiceForm} setInvoiceForm={setInvoiceForm}
                    showInvoiceModal={showInvoiceModal} setShowInvoiceModal={setShowInvoiceModal}
                    submittingInvoice={submittingInvoice} addInvoice={addInvoice}
                />
                )}




                {/* ✅ بقت متركبة بس وقت ما التاب ده مفتوح — نفس السبب المذكور فوق InvoicesView */}
                {view === "Tasks" && (
                <TasksView
                    view={view} darkMode={darkMode} currentUser={currentUser} currentProjectId={currentProjectId} showToast={showToast} showConfirm={showConfirm}
                    PROJECT={PROJECT} confirmTaskCompletion={confirmTaskCompletion} confirmProjectCompletion={confirmProjectCompletion}
                    tasksData={tasksData} dailyActivities={dailyActivities}
                    searchTerm={searchTerm} setSearchTerm={setSearchTerm} filterStatus={filterStatus} setFilterStatus={setFilterStatus} filterOwner={filterOwner} setFilterOwner={setFilterOwner} filterSystem={filterSystem} setFilterSystem={setFilterSystem}
                    sortBy={sortBy} setSortBy={setSortBy} sortOrder={sortOrder} setSortOrder={setSortOrder}
                    tasksView={tasksView} setTasksView={setTasksView} tasksPage={tasksPage} setTasksPage={setTasksPage}
                    selectedTasks={selectedTasks} setSelectedTasks={setSelectedTasks} handleToggleTaskSelect={handleToggleTaskSelect} handleBulkDelete={handleBulkDelete}
                    showAddTask={showAddTask} setShowAddTask={setShowAddTask} taskForm={taskForm} setTaskForm={setTaskForm} editingTask={editingTask} setEditingTask={setEditingTask} savingTask={savingTask} handleSaveTask={handleSaveTask}
                    showTaskDetails={showTaskDetails} setShowTaskDetails={setShowTaskDetails} handleOpenTaskDetails={handleOpenTaskDetails} handleEditTask={handleEditTask} handleDeleteTask={handleDeleteTask}
                    handleUpdateTaskValue={handleUpdateTaskValue}
                    taskDetailTab={taskDetailTab} setTaskDetailTab={setTaskDetailTab}
                    taskChallenges={taskChallenges} setTaskChallenges={setTaskChallenges} challengeForm={challengeForm} setChallengeForm={setChallengeForm} uploadingChallenge={uploadingChallenge} setUploadingChallenge={setUploadingChallenge} loadChallenges={loadChallenges}
                    setModalPhoto={setModalPhoto} getSortedTasks={getSortedTasks}
                />
                )}
                {/* ✅ بقت متركبة بس وقت ما التاب ده مفتوح — نفس السبب المذكور فوق InvoicesView */}
                {view === "Client Account" && (
                <ClientAccountView
                    view={view} darkMode={darkMode} PROJECT={PROJECT}
                    clientAccountData={clientAccountData}
                    clientForm={clientForm} setClientForm={setClientForm} editingClient={editingClient} setEditingClient={setEditingClient}
                    handleSaveClient={handleSaveClient} handleDeleteClient={handleDeleteClient}
                    showClientTransactionModal={showClientTransactionModal} setShowClientTransactionModal={setShowClientTransactionModal}
                />
                )}
                {/* ✅ بقت متركبة بس وقت ما التاب ده مفتوح — نفس السبب المذكور فوق InvoicesView */}
                {view === "Team" && (
                <TeamView
                    view={view} darkMode={darkMode} PROJECT={PROJECT} currentUser={currentUser} currentProjectId={currentProjectId} hasPermission={hasPermission}
                    showToast={showToast} showConfirm={showConfirm} fetchData={fetchData} setModalPhoto={setModalPhoto}
                    teamData={teamData} allTeamMembers={allTeamMembers}
                    teamSearchQuery={teamSearchQuery} setTeamSearchQuery={setTeamSearchQuery} teamPage={teamPage} setTeamPage={setTeamPage}
                    editingMember={editingMember} setEditingMember={setEditingMember} showEditMemberModal={showEditMemberModal} setShowEditMemberModal={setShowEditMemberModal} openEditMemberModal={openEditMemberModal} updateTeamMember={updateTeamMember}
                    showAddTeamMemberModal={showAddTeamMemberModal} setShowAddTeamMemberModal={setShowAddTeamMemberModal} newMemberForm={newMemberForm} setNewMemberForm={setNewMemberForm} addNewTeamMember={addNewTeamMember} uploadingTeamMember={uploadingTeamMember}
                    showAssignExistingMemberModal={showAssignExistingMemberModal} setShowAssignExistingMemberModal={setShowAssignExistingMemberModal} memberSearchTerm={memberSearchTerm} setMemberSearchTerm={setMemberSearchTerm}
                    selectedMemberToAssign={selectedMemberToAssign} setSelectedMemberToAssign={setSelectedMemberToAssign} assignmentSystems={assignmentSystems} setAssignmentSystems={setAssignmentSystems} assigningMember={assigningMember} assignExistingMember={assignExistingMember}
                    removeTeamMember={removeTeamMember}
                    allAchievements={allAchievements} memberAchievements={memberAchievements} newAchievement={newAchievement} setNewAchievement={setNewAchievement} addMemberAchievement={addMemberAchievement} removeMemberAchievement={removeMemberAchievement}
                    showQRModal={showQRModal} setShowQRModal={setShowQRModal} qrUrl={qrUrl} generateAttendanceQR={generateAttendanceQR}
                    showAttendanceModal={showAttendanceModal} setShowAttendanceModal={setShowAttendanceModal} attendanceDate={attendanceDate} attendanceRecords={attendanceRecords} setAttendanceRecords={setAttendanceRecords}
                    openAttendanceModal={openAttendanceModal} saveAttendance={saveAttendance} canManageAttendance={canManageAttendance}
                    showAttendanceSummary={showAttendanceSummary} setShowAttendanceSummary={setShowAttendanceSummary} attendanceStats={attendanceStats} attendanceSummary={attendanceSummary} totalHours={totalHours}
                    showExportModal={showExportModal} setShowExportModal={setShowExportModal} exportRange={exportRange} setExportRange={setExportRange} exportAttendanceRange={exportAttendanceRange} generateAttendancePDF={generateAttendancePDF}
                    showLeaveRequests={showLeaveRequests} setShowLeaveRequests={setShowLeaveRequests} leaveRequests={leaveRequests} processingLeaveId={processingLeaveId} setProcessingLeaveId={setProcessingLeaveId}
                    performanceData={performanceData} showPerformanceModal={showPerformanceModal} setShowPerformanceModal={setShowPerformanceModal} editingPerformance={editingPerformance} setEditingPerformance={setEditingPerformance} savingPerformance={savingPerformance} setSavingPerformance={setSavingPerformance}
                />
                )}
                {/* ✅ بقت متركبة بس وقت ما التاب ده مفتوح — نفس السبب المذكور فوق InvoicesView */}
                {view === "Project Schedule" && (
                <ScheduleView
                    view={view} darkMode={darkMode} PROJECT={PROJECT} hasPermission={hasPermission}
                    tasksData={tasksData} teamData={teamData} phases={phases} phasesFromTasks={phasesFromTasks} dependencies={dependencies} milestones={milestones} baselineRevisions={baselineRevisions}
                    scheduleView={scheduleView} setScheduleView={setScheduleView} ganttView={ganttView} ganttKey={ganttKey} ganttScheduleRef={ganttScheduleRef}
                    selectedPhase={selectedPhase} setSelectedPhase={setSelectedPhase}
                    showAddPhaseModal={showAddPhaseModal} setShowAddPhaseModal={setShowAddPhaseModal} editingPhase={editingPhase} setEditingPhase={setEditingPhase} phaseForm={phaseForm} setPhaseForm={setPhaseForm} addPhase={addPhase} updatePhase={updatePhase} deletePhase={deletePhase}
                    showAddMilestoneModal={showAddMilestoneModal} setShowAddMilestoneModal={setShowAddMilestoneModal} editingMilestone={editingMilestone} setEditingMilestone={setEditingMilestone} milestoneForm={milestoneForm} setMilestoneForm={setMilestoneForm} addMilestone={addMilestone} updateMilestone={updateMilestone} deleteMilestone={deleteMilestone}
                    showAddDependency={showAddDependency} setShowAddDependency={setShowAddDependency} depFromPhase={depFromPhase} setDepFromPhase={setDepFromPhase} depType={depType} setDepType={setDepType} addDependency={addDependency} deleteDependency={deleteDependency}
                    showCreateRevisionModal={showCreateRevisionModal} setShowCreateRevisionModal={setShowCreateRevisionModal} revisionForm={revisionForm} setRevisionForm={setRevisionForm} createBaselineRevision={createBaselineRevision} setSelectedRevision={setSelectedRevision}
                    setTaskDetailTab={setTaskDetailTab}
                />
                )}

                {/* ✅ بقت متركبة بس وقت ما التاب ده مفتوح — نفس السبب المذكور فوق InvoicesView */}
                {view === "Photos" && (
                <PhotosView
                    view={view} darkMode={darkMode} PROJECT={PROJECT} currentUser={currentUser} currentProjectId={currentProjectId} hasPermission={hasPermission}
                    showToast={showToast} showConfirm={showConfirm} setModalPhoto={setModalPhoto}
                    photosData={photosData} tasksData={tasksData}
                    photoSearch={photoSearch} setPhotoSearch={setPhotoSearch} photoSort={photoSort} setPhotoSort={setPhotoSort} photoView={photoView} setPhotoView={setPhotoView}
                    selectedSystemFilter={selectedSystemFilter} setSelectedSystemFilter={setSelectedSystemFilter} photosPage={photosPage} setPhotosPage={setPhotosPage}
                    deletePhoto={deletePhoto} refreshPhotos={refreshPhotos} compressImage={compressImage}
                    multiFiles={multiFiles} setMultiFiles={setMultiFiles} multiSys={multiSys} setMultiSys={setMultiSys} multiDesc={multiDesc} setMultiDesc={setMultiDesc}
                    multiProgress={multiProgress} setMultiProgress={setMultiProgress} isBulkUploading={isBulkUploading} setIsBulkUploading={setIsBulkUploading}
                />
                )}
            </div>


            {/* ✅ بقت متركبة بس وقت ما التاب ده مفتوح — نفس السبب المذكور فوق InvoicesView */}
            {view === "Comments" && (
            <CommentsView
                view={view} darkMode={darkMode} PROJECT={PROJECT} currentUser={currentUser} currentProjectId={currentProjectId} hasPermission={hasPermission}
                showToast={showToast} showConfirm={showConfirm} fetchData={fetchData}
                commentsData={commentsData}
                commentFilter={commentFilter} setCommentFilter={setCommentFilter} editingComment={editingComment} setEditingComment={setEditingComment}
                commentReplies={commentReplies} setCommentReplies={setCommentReplies} replyInputs={replyInputs} setReplyInputs={setReplyInputs}
                newComment={newComment} setNewComment={setNewComment} commentPriority={commentPriority} setCommentPriority={setCommentPriority}
                submittingComment={submittingComment} setSubmittingComment={setSubmittingComment} submitComment={submitComment}
            />
            )}

            {view === "Documents" && (
                <DocumentsView
                    theme={theme}
                    documentsData={documentsData}
                    hasPermission={hasPermission}
                    showConfirm={showConfirm}
                    documentFilter={documentFilter}
                    setDocumentFilter={setDocumentFilter}
                    documentSearchTerm={documentSearchTerm}
                    setDocumentSearchTerm={setDocumentSearchTerm}
                    showUploadModal={showUploadModal}
                    setShowUploadModal={setShowUploadModal}
                    uploadForm={uploadForm}
                    setUploadForm={setUploadForm}
                    uploadingDocument={uploadingDocument}
                    deleteDocument={deleteDocument}
                    uploadDocument={uploadDocument}
                    filteredDocuments={filteredDocuments}
                    getFileIcon={getFileIcon}
                    formatFileSize={formatFileSize}
                    documentStats={documentStats}
                />
            )}

            {/* ✅ بقت متركبة بس وقت ما التاب ده مفتوح — نفس السبب المذكور فوق InvoicesView */}
            {view === "Timeline" && (
            <TimelineView
                view={view} darkMode={darkMode} tasksData={tasksData} groupedTasks={groupedTasks}
                ganttView={ganttView} setGanttView={setGanttView} setGanttstart_date={setGanttstart_date}
                getTimelineColumns={getTimelineColumns} getTaskBarStyle={getTaskBarStyle} navigateTimeline={navigateTimeline}
                timelineTask={timelineTask} setTimelineTask={setTimelineTask}
            />
            )}

            {/* ✅ بقت متركبة بس وقت ما التاب ده مفتوح — نفس السبب المذكور فوق InvoicesView */}
            {view === "Expenses Management" && (
            <ExpensesView
                view={view} darkMode={darkMode} hasPermission={hasPermission} formatCurrency={formatCurrency}
                budgetCategories={budgetCategories} budgetExpenses={budgetExpenses} budgetCommitments={budgetCommitments}
                expandedCategory={expandedCategory} setExpandedCategory={setExpandedCategory} selectedCategory={selectedCategory} setSelectedCategory={setSelectedCategory}
                getCategorySpent={getCategorySpent} getCategoryExpenses={getCategoryExpenses}
                getCategoryCommitted={getCategoryCommitted} getCategoryCommitments={getCategoryCommitments}
                showAddCategoryModal={showAddCategoryModal} setShowAddCategoryModal={setShowAddCategoryModal} categoryForm={categoryForm} setCategoryForm={setCategoryForm} addBudgetCategory={addBudgetCategory} uploadingCategory={uploadingCategory}
                showAddExpenseModal={showAddExpenseModal} setShowAddExpenseModal={setShowAddExpenseModal} expenseForm={expenseForm} setExpenseForm={setExpenseForm} addBudgetExpense={addBudgetExpense} uploadingExpense={uploadingExpense}
                showAddCommitmentModal={showAddCommitmentModal} setShowAddCommitmentModal={setShowAddCommitmentModal} commitmentForm={commitmentForm} setCommitmentForm={setCommitmentForm} addBudgetCommitment={addBudgetCommitment} uploadingCommitment={uploadingCommitment}
                editingCategory={editingCategory} setEditingCategory={setEditingCategory} updateCategory={updateCategory} deleteCategory={deleteCategory}
                editingExpense={editingExpense} setEditingExpense={setEditingExpense} updateExpense={updateExpense} deleteExpense={deleteExpense}
                editingCommitment={editingCommitment} setEditingCommitment={setEditingCommitment} updateCommitment={updateCommitment} deleteCommitment={deleteCommitment} closeCommitment={closeCommitment}
            />
            )}
            {/* ✅ اتشالت هنا: مودال "Task Details" قديم ميت — كان بيعتمد على selectedTask اللي setSelectedTask بتاعها ما بتتنادى في أي مكان تاني في الابليكيشن (يعني selectedTask دايمًا null وده كان يمنع المودال من الظهور خالص)، وكمان كان شكله مش متسق مع باقي الصفحة (Tailwind classes بدل الـ inline styles). */}
            {/* ✅ بقت متركبة بس وقت ما التاب ده مفتوح — نفس السبب المذكور فوق InvoicesView.
                ده بالذات أكتر فيو وصلنا شكوى عنها (فيو صور الديلي اكتفتي)، فده
                جزء مباشر من حل مشكلة الرمش المتكررة فيها. */}
            {view === "Daily Activities" && (
            <DailyActivitiesView
                view={view} theme={theme} PROJECT={PROJECT} currentUser={currentUser} hasPermission={hasPermission} setModalPhoto={setModalPhoto}
                dailyActivities={dailyActivities} tasksData={tasksData} hseIncidents={hseIncidents} rfis={rfis} setRfis={setRfis} contacts={contacts} setContacts={setContacts}
                activityViewMode={activityViewMode} setActivityViewMode={setActivityViewMode}
                activitySearch={activitySearch} setActivitySearch={setActivitySearch} activitySystemFilter={activitySystemFilter} setActivitySystemFilter={setActivitySystemFilter}
                activitySupervisorFilter={activitySupervisorFilter} setActivitySupervisorFilter={setActivitySupervisorFilter} activityFilter={activityFilter} setActivityFilter={setActivityFilter}
                calendarMonth={calendarMonth} setCalendarMonth={setCalendarMonth} selectedCalendarDay={selectedCalendarDay} setSelectedCalendarDay={setSelectedCalendarDay}
                activitiesDatePage={activitiesDatePage} setActivitiesDatePage={setActivitiesDatePage}
                showAddActivityModal={showAddActivityModal} setShowAddActivityModal={setShowAddActivityModal} activityForm={activityForm} setActivityForm={setActivityForm}
                addDailyActivity={addDailyActivity} uploadingActivity={uploadingActivity} uploadProgressCount={uploadProgressCount}
                editingActivity={editingActivity} setEditingActivity={setEditingActivity} updateDailyActivity={updateDailyActivity} deleteDailyActivity={deleteDailyActivity}
            />
            )}

            {/* ✅ بقت متركبة بس وقت ما التاب ده مفتوح — نفس السبب المذكور فوق InvoicesView */}
            {view === "Materials" && (
            <MaterialsView
                view={view} darkMode={darkMode}
                materialsData={materialsData}
                searchTerm={materialsSearchTerm} setSearchTerm={setMaterialsSearchTerm} selectedSystem={selectedSystem} setSelectedSystem={setSelectedSystem} selectedStatus={selectedStatus} setSelectedStatus={setSelectedStatus}
                showMaterialModal={showMaterialModal} setShowMaterialModal={setShowMaterialModal} editingMaterial={editingMaterial} setEditingMaterial={setEditingMaterial}
                materialForm={materialForm} setMaterialForm={setMaterialForm} savingMaterial={savingMaterial}
                handleSaveMaterial={handleSaveMaterial} handleEditMaterial={handleEditMaterial} handleDeleteMaterial={handleDeleteMaterial}
                showImportModal={showImportModal} setShowImportModal={setShowImportModal} importData={importData} setImportData={setImportData}
                importLoading={importLoading} importError={importError} setImportError={setImportError} importSuccess={importSuccess}
                handleImportExcel={handleImportExcel} handleSubmitImport={handleSubmitImport} handleDownloadTemplate={handleDownloadTemplate}
            />
            )}
            {/* ✅ اتشالت هنا: مودال "Add New Invoice" قديم ومكرر — كان بيتفعّل بنفس showInvoiceModal بتاع المودال الحقيقي الموجود جوّه InvoicesView (اللي بيدعم الوضع الداكن ومتسق مع باقي الصفحة)، يعني كانوا هيظهروا فوق بعض في نفس الوقت. */}


            {showProjectManager && (
                <Suspense fallback={null}>
                    <ProjectManager
                        onClose={() => setShowProjectManager(false)}
                        onProjectAdded={() => {
                            loadProjectsFromDatabase();
                            setShowProjectManager(false);
                        }}
                        currentUser={currentUser}
                        showToast={showToast}
                        hasPermission={hasPermission}
                        canViewProject={canViewProject}
                        showConfirm={showConfirm}
                    />
                </Suspense>
            )}
            {/* Toast Notification
                ✅ ده الـ toast العام المستخدم في كل حركة فعليًا (إضافة/تعديل/حذف/
                استيراد/دخول...) في الداشبورد كله — يعني بيظهر عشرات المرات في
                الجلسة الواحدة. كان فيه نفس تعارض backdrop-filter + transform
                (scale) animation اللي سبق إصلاحه في مودال الصور (PhotoModalContext)
                والمودالز التانية: backdrop-blur-sm هنا + scale (0.3→1 عند
                الظهور، →0.5 عند الاختفاء) مع بعض — كل ما يظهر توست (كتير جدًا
                عبر الداشبورد)، ده اللي كان بيرجّع الرمشة في أماكن متفرقة. شلنا
                backdrop-blur-sm والـ scale خالص، وسيبنا بس opacity + y (أرخص
                حركة ممكنة للمتصفح، بدون أي تعارض). */}
            {toast.show && (
                <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 20, transition: { duration: 0.2 } }}
                    className={`fixed bottom-6 right-6 px-6 py-4 rounded-2xl shadow-2xl z-[9999] flex items-center gap-3 ${toast.type === 'success'
                        ? 'bg-gradient-to-r from-green-500 to-emerald-600'
                        : toast.type === 'error'
                            ? 'bg-gradient-to-r from-red-500 to-rose-600'
                            : 'bg-gradient-to-r from-blue-500 to-indigo-600'
                        } text-white border border-white/20`}
                >
                    <div className="flex items-center gap-3">
                        {toast.type === 'success' && (
                            <div className="p-2 bg-white/20 rounded-lg">
                                <CheckCircle size={20} />
                            </div>
                        )}
                        {toast.type === 'error' && (
                            <div className="p-2 bg-white/20 rounded-lg">
                                <XCircle size={20} />
                            </div>
                        )}
                        {toast.type === 'info' && (
                            <div className="p-2 bg-white/20 rounded-lg">
                                <AlertCircle size={20} />
                            </div>
                        )}
                        <div>
                            <p className="font-semibold text-sm">{toast.message}</p>
                        </div>
                    </div>

                    {/* Progress Bar */}
                    <motion.div
                        initial={{ width: '100%' }}
                        animate={{ width: '0%' }}
                        transition={{ duration: 3, ease: 'linear' }}
                        className="absolute bottom-0 left-0 h-1 bg-white/30 rounded-full"
                    />

                    {/* Close Button */}
                    <button
                        onClick={() => setToast({ show: false, message: '', type: '' })}
                        className="ml-2 p-1 hover:bg-white/20 rounded-lg transition"
                    >
                        <X size={16} />
                    </button>
                </motion.div>
            )}
            {showUserManagement && (
                <UserManagement
                    onClose={() => setShowUserManagement(false)}
                    theme={theme}
                    projects={PROJECTS}
                    showToast={showToast}
                    showConfirm={showConfirm}
                />
            )}
            {showSettings && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
                    onClick={() => setShowSettings(false)}>
                    <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }}
                        onClick={e => e.stopPropagation()}
                        className={`${theme.card} rounded-2xl p-6 w-full max-w-md shadow-2xl`}>

                        <div className="flex items-center justify-between mb-6">
                            <h3 className={`text-xl font-black ${theme.text} flex items-center gap-2`}>
                                <Settings size={20} className="text-blue-500" />Settings
                            </h3>
                            <button onClick={() => setShowSettings(false)}
                                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"><X size={18} /></button>
                        </div>

                        <div className="space-y-6">

                            {/* Appearance */}
                            <div>
                                <p className={`text-xs font-black uppercase tracking-wider ${theme.subtext} mb-3`}>Appearance</p>
                                <div className="flex gap-3">
                                    {['light', 'dark'].map(t => (
                                        <button key={t} onClick={() => setDarkMode(t === 'dark')}
                                            className={`flex-1 py-3 rounded-xl border-2 font-bold text-sm flex items-center justify-center gap-2 transition-all ${(t === 'dark') === darkMode
                                                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-600'
                                                : `${theme.border} ${theme.text}`
                                                }`}>
                                            {t === 'dark' ? <Moon size={16} /> : <Sun size={16} />}
                                            {t === 'dark' ? 'Dark' : 'Light'}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Account */}
                            <div>
                                <p className={`text-xs font-black uppercase tracking-wider ${theme.subtext} mb-3`}>Account</p>
                                <div className={`p-4 rounded-xl border ${theme.border} space-y-3`}>

                                    {/* Avatar */}
                                    <div className="flex items-center gap-4">
                                        {currentUser?.avatar
                                            ? <img src={currentUser.avatar} className="w-16 h-16 rounded-xl object-cover" alt="" />
                                            : <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center text-white font-black text-xl">
                                                {currentUser?.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                                            </div>
                                        }
                                        <div>
                                            <p className={`font-black ${theme.text}`}>{currentUser?.name}</p>
                                            <p className={`text-sm ${theme.subtext}`}>{currentUser?.email}</p>
                                            <p className="text-xs text-blue-500 font-bold mt-0.5">{currentUser?.position}</p>
                                        </div>
                                    </div>

                                    {/* Change Password */}
                                    <button onClick={async () => {
                                        await supabase.auth.resetPasswordForEmail(currentUser?.email);
                                        showToast('📧 Password reset email sent!');
                                    }}
                                        className="w-full py-2.5 border-2 border-amber-400 text-amber-600 rounded-xl font-bold text-sm hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors flex items-center justify-center gap-2">
                                        <Key size={15} />Send Password Reset Email
                                    </button>
                                </div>
                            </div>

                            {/* Danger Zone */}
                            <div>
                                <p className={`text-xs font-black uppercase tracking-wider text-red-400 mb-3`}>Session</p>
                                <button onClick={async () => {
                                    // ✅ كان بيعمل signOut + setIsLoggedIn/setCurrentUser بس، من غير ما
                                    // يصفّر currentProjectId/localStorage.selectedProject وكل بيانات
                                    // المشروع (الـ ~26 array الموجودين في fetchData) — يعني لو مستخدم
                                    // تاني دخل بعده في نفس التاب، كان ممكن يشوف لحظة بيانات المشروع
                                    // القديم قبل ما fetchData تجيب بياناته هو (نفس المشكلة اللي handleLogout
                                    // الأساسية معمولة بالذات عشان تمنعها). استخدمنا handleLogout نفسها.
                                    await handleLogout();
                                    showToast('👋 Logged out');
                                }}
                                    className="w-full py-2.5 border-2 border-red-400 text-red-500 rounded-xl font-bold text-sm hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex items-center justify-center gap-2">
                                    <LogOut size={15} />Sign Out
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
            {confirmModal.show && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-4">
                    <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }}
                        className={`${theme.card} rounded-2xl p-6 w-full max-w-sm shadow-2xl`}>
                        <div className="w-14 h-14 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                            <AlertTriangle size={28} className="text-red-500" />
                        </div>
                        <h3 className={`text-lg font-black text-center ${theme.text} mb-2`}>Are you sure?</h3>
                        <p className={`text-sm text-center ${theme.subtext} mb-6`}>{confirmModal.message}</p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setConfirmModal({ show: false, message: '', onConfirm: null })}
                                className={`flex-1 py-2.5 border-2 ${theme.border} ${theme.text} rounded-xl font-bold`}>
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    confirmModal.onConfirm?.();
                                    setConfirmModal({ show: false, message: '', onConfirm: null });
                                }}
                                className="flex-1 py-2.5 bg-gradient-to-r from-red-500 to-rose-600 text-white rounded-xl font-bold">
                                Delete
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
            {selectedRevision && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
                    onClick={() => setSelectedRevision(null)}>
                    <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }}
                        onClick={e => e.stopPropagation()}
                        className={`${theme.card} rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden`}>

                        {/* Header */}
                        <div className={`p-6 ${selectedRevision.status === 'Approved' ? 'bg-gradient-to-r from-emerald-500 to-teal-500' :
                            selectedRevision.status === 'Rejected' ? 'bg-gradient-to-r from-red-500 to-rose-500' :
                                'bg-gradient-to-r from-amber-500 to-orange-500'
                            }`}>
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-white/80 text-sm font-bold">{selectedRevision.revision_label}</p>
                                    <h3 className="text-white font-black text-xl mt-1">{selectedRevision.reason}</h3>
                                </div>
                                <button onClick={() => setSelectedRevision(null)}
                                    className="p-2 bg-white/20 rounded-xl text-white font-bold">✕</button>
                            </div>
                            <div className="flex items-center gap-3 mt-4">
                                <span className="px-3 py-1 bg-white/20 rounded-lg text-white text-xs font-black">
                                    {selectedRevision.status === 'Approved' ? '🔒 Approved' :
                                        selectedRevision.status === 'Rejected' ? '❌ Rejected' : '⏳ Pending'}
                                </span>
                                <span className="text-white/70 text-xs">
                                    Created by {selectedRevision.created_by} • {new Date(selectedRevision.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                </span>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="p-6 max-h-96 overflow-y-auto">

                            {/* Phases Snapshot */}
                            {selectedRevision.phases_snapshot?.length > 0 && (
                                <div className="mb-6">
                                    <p className={`text-xs font-black ${theme.subtext} uppercase mb-3`}>Baseline Schedule</p>
                                    <div className={`border ${theme.border} rounded-xl overflow-hidden`}>
                                        {selectedRevision.phases_snapshot.map((p, i) => (
                                            <div key={p.id} className={`flex items-center justify-between p-3 ${i % 2 === 0 ? '' : 'bg-gray-50 dark:bg-gray-800/30'} border-b ${theme.border} last:border-0`}>
                                                <div className="flex items-center gap-2">
                                                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: p.color || '#3b82f6' }} />
                                                    <span className={`text-sm font-bold ${theme.text}`}>{p.name}</span>
                                                </div>
                                                <span className={`text-xs ${theme.subtext}`}>
                                                    {p.baseline_start ? new Date(p.baseline_start).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '—'}
                                                    {' → '}
                                                    {p.baseline_end ? new Date(p.baseline_end).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Approval Info */}
                            {selectedRevision.approved_by_client && (
                                <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl mb-4">
                                    <p className="text-xs font-black text-emerald-600 uppercase mb-2">✅ Approved By</p>
                                    <p className="font-black text-emerald-700">{selectedRevision.approved_by_client}</p>
                                    <p className="text-sm text-emerald-600">{selectedRevision.approved_title}</p>
                                    <p className="text-xs text-emerald-500 mt-1">
                                        {new Date(selectedRevision.approved_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                </div>
                            )}

                            {/* Rejection Info */}
                            {selectedRevision.rejected_by_client && (
                                <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-xl mb-4">
                                    <p className="text-xs font-black text-red-600 uppercase mb-2">❌ Rejected By</p>
                                    <p className="font-black text-red-700">{selectedRevision.rejected_by_client}</p>
                                    <p className="text-sm text-red-600">{selectedRevision.rejected_title}</p>
                                    <p className="text-xs text-red-500 mt-1">
                                        {new Date(selectedRevision.rejected_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                    {selectedRevision.rejected_reason && (
                                        <p className="text-sm text-red-600 font-bold mt-2 p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                                            "{selectedRevision.rejected_reason}"
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}
            <Suspense fallback={<SectionLoader />}>
                {view === "Systems Library" && (
                    <SystemsLibrary theme={theme} />
                )}
                {view === "Submittal Assembler" && (
                    <SubmittalAssembler theme={theme} currentProjectId={currentProjectId} currentUser={currentUser} />
                )}
                {view === "HR" && (
                    <HRSection theme={theme} currentUser={currentUser} />
                )}
                {view === "Accounts" && (
                    <AccountsSection theme={theme} currentUser={currentUser} />
                )}
                {view === "Coordination" && (
                    <CoordinationSection theme={theme} currentUser={currentUser} />
                )}
                {view === "Meetings" && (
                    <Meetings />
                )}
                {view === "Prequal Profile" && (
                    <PrequalProfile PROJECT={PROJECT} tasksData={tasksData}
                        teamData={teamData} darkMode={darkMode} theme={theme} />
                )}
                {view === "Showcase Tokens" && <ShowcaseTokenManager />}
                {view === "Showcase Leads" && <ShowcaseLeadsDashboard />}
                {view === "Showcase Analytics" && <ShowcaseAnalytics />}
                {view === "Approved Brands" && <ApprovedBrands projectId={currentProjectId} />}
            </Suspense>

            {/* Phase 1 of the in-dashboard alerts assistant Bakri asked for
                (2026-11-04): a floating widget on every view, surfacing the
                same overdue-task / missing-Daily-Activity / absence warnings
                the email edge functions already send, scoped to what this
                user is allowed to see. */}
            <DashboardAlertsWidget
                currentUser={currentUser}
                theme={theme}
                darkMode={darkMode}
                onNavigate={(projectId, viewName) => {
                    if (projectId && projectId !== currentProjectId) switchProject(projectId);
                    setView(viewName);
                }}
            />

        </div>
    );
}